import React, { useState, useEffect, useRef, useLayoutEffect, useCallback } from 'react';
import ReactDOM from 'react-dom';
import styled, { keyframes, css } from 'styled-components';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { scrollElementIntoViewAdaptive } from '../utils/scrollUtils';
import SeoHead from './SeoHead';
import { useAuth } from '../context/AuthContext';
import { useUsageVisibility } from '../hooks/useUsageVisibility';
import ProfileImage from './ProfileImage';
import LoginButton from './LoginButton';
import UserLoginCard from './UserLoginCard';
import {
  TokenSection,
  TokenContainer,
  TokenTitle,
  TokenText,
  TokenButton,
  CapabilitiesSection,
  PromptBarContainer,
  PromptInputWrapper,
  PromptInput,
  ActionButton,
  SendButton,
  MissionSection,
  MissionContainer,
  MissionTitle,
  MissionText,
  MissionButton
} from './LandingPageComponents';
import db from '../utils/firestore';
import ShinyGoldButton from './pricing/ShinyGoldButton';
import { PlatformMessageService } from '../services/PlatformMessageService';
import {
  createWorkflowSession,
  listWorkflowSessions,
  completeWorkflowSession,
  renameWorkflowSession,
  getStoredWorkflowSessionId,
  setStoredWorkflowSessionId,
  type WorkflowSession,
} from '../services/workflowSessions';
import { clearConversationHistory } from '../repositories/conversationHistoryRepository';
import { getAuth } from 'firebase/auth';
import { openConstructionNotice, CONSTRUCTION_THREAD_OPEN_EVENT } from '../utils/constructionNotice';
import { onPlatformMessagesSnapshot, onSessionPlatformMessagesSnapshot } from '../repositories/realtime';
import { useConstructionWaitlist } from '../hooks/useConstructionWaitlist';
import ConstructionWaitlistBubble from './ConstructionWaitlistBubble';
import GlobalFooter from './GlobalFooter';
import { certificationBadges } from '../data/securityBadges';
import {
  // Storage keys
  GLOBAL_CHAT_HISTORY_KEY,
  GLOBAL_CHAT_DRAFT_KEY,
  GLOBAL_CHAT_OPEN_KEY,
  GLOBAL_CHAT_DOCK_KEY,
  GLOBAL_CHAT_FULLSCREEN_KEY,
  buildPageStorageKey,
  // Message action components
  CopyIconButton,
  TokenInfoButton,
  ProcessingTime,
  SPECIAL_MESSAGE_COLORS,
  TokenInfoPopupContent,
  // Chat container components
  ChatContainer,
  ChatHeader,
  ChatHeaderLogoImage,
  ChatTitle,
  ChatMessages,
  ChatFooter,
  // Message components
  MessageContainer,
  Message,
  MessageTime,
  // Buttons
  ScrollToBottomButton,
  HideButton,
  HeaderIconButton as ChatHeaderIconButton,
  // Floating Navigation Buttons
  FloatingButtonBar,
  FloatingPillButton,
  // Reveal Animation
  AnimatedRevealContainer,
  RevealDivider,
  // Attachment styled components
  AttachmentsContainer,
  AttachmentItem,
  AttachmentName,
  AttachmentSize,
  AttachmentPreviewPanel,
  PreviewHeader,
  PreviewTitle,
  PreviewBody,
  FilePreviewItem,
  FilePreviewInfo,
  FileName,
  FileSize,
  RemoveButton,
  ClearAllButton,
  // Attachment components
  FilePreview,
  ClickableAttachment,
  AttachmentIcon,
  AttachmentsSection,
  // SVG Icons
  ChevronUpSvgIcon,
  ChevronDownSvgIcon,
  TrashSvgIcon,
  // Modals
  ConfirmationModal,
  // Animations
  messageFadeIn,
  // Types
  type ChatMessage,
  type SerializedAttachment,
  type TokenUsageSnapshot,
} from './GlobalChatSystem';
import { publishChatUiStateSync, subscribeToChatUiStateSync } from './GlobalChatSystem/threadPersist';
import { normalizeChatHtml, ensureLinksOpenInNewTab, autoLinkText, formatProcessingTime, formatMessageTime, formatFileSize } from './GlobalChatSystem/utils';
// Shared paste sanitizer (owner directive 2026-07-13: uniform paste behavior
// on EVERY composer) — contrast-fixes too-light pasted colors, strips pasted
// backgrounds, trims the un-deletable edge blank blocks.
import { sanitizePastedHtml, sanitizePastedText, plainTextToComposerHtml, trimEdgeEmptyBlocks } from './GlobalChatSystem/pasteSanitizer';
// BUG-DR-COMPOSER-REPOP-LONG-TURN (2026-05-20) — shared draft-suppression
// singleton. The LandingPage hero composer has its own draft persist/restore
// loop (lines ~5757-6259) that previously had NO awareness of the post-send
// or in-flight suppression windows. Without these guards a Deep Research
// turn (5-8 min) would expire the legacy 1500 ms post-send window in
// useGlobalChat, and the LandingPage 500 ms persist effect would re-write
// the just-sent text to localStorage; the next 500 ms poll here would then
// repopulate the composer. shouldSuppressDraftRehydrate() composes the
// 1500 ms window with the full in-flight turn + 1500 ms post-flight
// cooldown. See draftSuppressionState.ts.
import {
  markSentNow as draftMarkSentNow,
  markSentText as draftMarkSentText,
  isSentTextMatch as draftIsSentTextMatch,
  markProcessingStarted as draftMarkProcessingStarted,
  markProcessingEnded as draftMarkProcessingEnded,
  shouldSuppressDraftRehydrate,
} from './GlobalChatSystem/draftSuppressionState';
import { MAX_UPLOAD_BYTES_PER_FILE, MAX_UPLOAD_LABEL_PER_FILE } from '../constants/uploads';
import Modal from './common/Modal';
import KeepCase from './common/KeepCase';
// The 0, as a day (plan C1 face 1). Static imports: the ring is part of the
// first paint, and the hero must never depend on a Suspense boundary
// (the 2026-05-09 stall class — see App.tsx:61).
import Zero from './zero/Zero';
// The permission room (plan C2) — rendered from the shipped, CI-gated
// permission catalog and the platform's own refusal wording.
import Room from './room/Room';
// The five mechanism blocks + the code-backed trust block (plan C4.4 / C6).
import Mechanisms, { TrustBlock } from './landing/Mechanisms';
import type { Vertical as ZeroVertical } from './zero/zeroDay';
import { MONTHLY_WU_PER_PRESTON, WU_FULL_NAME } from '../config/workUnits';

// BUG-DR-COMPOSER-MID-RUN-LANDINGPAGE (2026-05-20, Malaika 14:23 UTC retest):
// PR #188 (eb308ff9) wired the in-flight gate into useGlobalChat.ts via a
// useEffect that mirrors state.isProcessing -> markProcessingStarted/Ended.
// However the LandingPage hero composer's handleSend path imported only
// markSentNow + shouldSuppressDraftRehydrate; the in-flight gate was never
// opened from this entry point. Result: for sends originating from the hero
// composer, the in-flight refcount stayed at 0 (useGlobalChat may be in a
// different React tree or its isProcessing-bridging useEffect may not fire
// before the legacy 1500 ms post-send window closes), the 1500 ms window
// expired mid-DR-turn, and the LandingPage's own 500 ms storage poll +
// debounced persist rehydrated the just-sent text into the composer.
//
// Tightening: bracket every LandingPage handleSend with markProcessingStarted
// at the synchronous start AND a 15-minute safety-net markProcessingEnded.
// Cost of slightly-over-suppressing is zero; cost of mid-run rehydration is
// the demo blocker. The useGlobalChat bridge still runs in parallel and
// composes via the refcount, so the cooldown anchors to the LATER of the
// two ends -- which is exactly what we want for sends initiated here.
//
// BUG-DR-CLARIFYING-CARD-JSX-MISSING (2026-05-20): the prior placement of
// this const between two imports caused eslint `import/first` to fail the
// CI build, which silently prevented PR #192/#193's frontend changes
// (clarifying-card React component + Skip button DOM) from ever being
// re-bundled and shipped to gs://project-olivia-frontend-415/. The live
// bundle main.7aaa6733.js predates both PRs. Hoisting imports back above
// any module-level statements unblocks the build chain.
const LANDING_HANDLE_SEND_SAFETY_END_MS = 15 * 60 * 1000;
// Remembered industry pick for the 0 (see the state hook below).
const VERTICAL_KEY = 'prest0_landing_vertical';
// Fixed keyframes animations with explicit semicolons
const fadeIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const fadeInLeft = keyframes`
  from {
    opacity: 0;
    transform: translateX(-30px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
`;

const fadeInRight = keyframes`
  from {
    opacity: 0;
    transform: translateX(40px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
`;
const gradientShift = keyframes`
  0% {
    background-position: 0% 50%;
  }
  50% {
    background-position: 100% 50%;
  }
  100% {
    background-position: 0% 50%;
  }
`;
const pulse = keyframes`
  0%, 100% {
    opacity: 0.6;
    transform: scale(0.85);
  }
  50% {
    opacity: 1;
    transform: scale(1);
  }
`;
const reveal = keyframes`
  0% {
    opacity: 0;
    transform: translateY(20px) scale(0.95);
  }
  100% {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
`;

// More realistic cursor animations
const cursorBlink = keyframes`
  0%, 100% { opacity: 1; }
  50% { opacity: 0; }
`;

const cursorSolid = keyframes`
  0%, 100% { opacity: 1; }
`;

// Shine animation
const shine = keyframes`
  0% {
    background-position: -100px;
  }
  40%, 100% {
    background-position: 300px;
  }
`;
// Styled Components
const LandingContainer = styled.div`
  min-height: 100dvh;
  background: linear-gradient(135deg, #FAFAFB 0%, #F5F5F5 100%);
  display: flex;
  flex-direction: column;
  align-items: center;
  text-transform: lowercase;
  overflow-x: hidden;
  position: relative;
`;

const HomeHeader = styled.header`
  position: fixed;
  top: 0.5rem; /* Reduced from 1rem to 0.5rem */
  right: 2.75rem;
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 1.2rem;
  z-index: 2000000; /* higher than prompt bar to ensure dropdown overlays it */

  @media (min-width: 1024px) {
    top: clamp(1.35rem, 3vh, 2.25rem);
    right: clamp(2.5rem, 5vw, 4rem);
    gap: clamp(1.25rem, 2.5vw, 1.75rem);
  }

  @media (max-width: 768px) {
    top: 0.25rem; /* Reduced from 0.5rem to 0.25rem */
    right: 1rem;
    gap: 0.8rem;
  }
`;

const NavButtonGroup = styled.nav`
  display: grid;
  grid-auto-flow: column;
  grid-auto-columns: minmax(max-content, 1fr);
  gap: clamp(0.65rem, 1.5vw, 0.85rem);
  align-items: stretch;

  @media (max-width: 768px) {
    display: none;
  }
`;

const HeaderIconLink = styled(Link)`
  color: #2c3e50;
  text-decoration: none;
  transition: all 0.28s ease;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: clamp(0.4rem, 0.9vw, 0.5rem);
  padding: clamp(0.45rem, 1vw, 0.55rem) clamp(0.75rem, 1.75vw, 0.95rem);
  border-radius: 14px;
  font-weight: 500;
  font-size: clamp(0.825rem, 0.9vw, 0.875rem);
  letter-spacing: 0.015em;
  position: relative;
  background: rgba(255, 255, 255, 0.88);
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04), 0 1px 3px rgba(0, 0, 0, 0.02);
  border: 1px solid rgba(255, 255, 255, 0.35);
  min-height: clamp(36px, 3.2vw, 40px);
  width: 100%;
  max-width: 100%;
  flex-shrink: 0;
  
  &::after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 50%;
    width: 0;
    height: 1.5px;
    background: linear-gradient(135deg, var(--brand-gold-alt), var(--brand-black));
    transition: all 0.35s cubic-bezier(0.175, 0.885, 0.32, 1.275);
    transform: translateX(-50%);
    opacity: 0;
  }
  
  &:hover {
    color: #1a2533;
    background-color: rgba(255, 255, 255, 0.98);
    transform: translateY(-2px);
    box-shadow: 0 5px 14px rgba(0, 0, 0, 0.08), 0 2px 5px rgba(0, 0, 0, 0.04);
    border-color: rgba(255, 255, 255, 0.5);
    
    &::after {
      width: 70%;
      opacity: 1;
    }
  }

  svg {
    width: clamp(15px, 1.4vw, 16px);
    height: clamp(15px, 1.4vw, 16px);
    opacity: 0.75;
    transition: all 0.28s ease;
    stroke-width: 2.1;
  }

  &:hover svg {
    opacity: 1;
    transform: scale(1.1) rotate(-4deg);
  }
  
  @media (max-width: 768px) {
    display: none;
  }
`;

const HeaderIconButton = styled.button`
  color: #fff;
  text-decoration: none;
  transition: all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0.65rem 1.5rem;
  border-radius: 40px;
  background: linear-gradient(135deg, var(--brand-gold-alt), var(--brand-black));
  background-size: 200% 100%;
  border: none;
  font-family: inherit;
  font-size: 0.95rem;
  font-weight: 600;
  letter-spacing: 0.02em;
  cursor: pointer;
  position: relative;
  overflow: hidden;
  box-shadow: 0 6px 20px rgba(200, 167, 94, 0.2);
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(90deg, 
      transparent 0%, 
      rgba(255, 255, 255, 0.2) 50%, 
      transparent 100%);
    transition: all 0.6s ease;
  }
  
  &:hover {
    background-position: 100% 0;
    transform: translateY(-3px);
    box-shadow: 0 8px 25px rgba(200, 167, 94, 0.35);
    
    &::before {
      left: 100%;
    }
  }

  svg {
    width: 18px;
    height: 18px;
    opacity: 1;
    transition: all 0.3s ease;
    margin-right: 0.6rem;
    stroke: currentColor;
  }

  &:hover svg {
    transform: translateX(5px);
  }
  
  @media (max-width: 768px) {
    display: none;
  }
`;

// Notification icon button (desktop only)
const NotificationIconButton = styled.button`
  width: 36px;
  height: 36px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, 0.9);
  border: 1px solid rgba(0, 0, 0, 0.08);
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.06);
  color: #333;
  cursor: pointer;
  transition: all 0.2s ease;
  padding: 0;
  margin: 0;
  position: relative;
  
  &:hover {
    background: #fff;
    transform: translateY(-2px);
    box-shadow: 0 6px 16px rgba(0, 0, 0, 0.1);
  }
  
  svg {
    width: 18px;
    height: 18px;
  }
  
  @media (max-width: 768px) {
    display: none;
  }
`;

// Wrapper to anchor the notification dropdown under the icon
const NotificationWrapper = styled.div`
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
`;

// Dropdown panel for recent notifications (desktop)
const NotificationDropdown = styled.div<{ $isOpen: boolean }>`
  position: absolute;
  top: 44px;
  right: 0;
  background: white;
  border-radius: 12px;
  box-shadow: 0 10px 25px rgba(0,0,0,0.1);
  width: 320px;
  padding: 8px;
  z-index: 15000;
  transform-origin: top right;
  transform: ${props => props.$isOpen ? 'scale(1)' : 'scale(0.98)'};
  opacity: ${props => props.$isOpen ? 1 : 0};
  visibility: ${props => props.$isOpen ? 'visible' : 'hidden'};
  transition: all 0.18s cubic-bezier(0.175, 0.885, 0.32, 1.275);
  border: 1px solid rgba(0,0,0,0.06);
`;

const NotificationHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 6px 8px 10px;
  border-bottom: 1px solid rgba(0,0,0,0.05);
  margin: 0 2px 6px;
  
  h4 {
    margin: 0;
    font-size: 0.95rem;
    font-weight: 600;
    color: #333;
    text-transform: lowercase;
  }
`;

const NotificationList = styled.div`
  max-height: 360px;
  overflow-y: auto;
  padding: 4px 2px;
`;

const NotificationItem = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 10px;
  border-radius: 10px;
  transition: background 0.2s ease;
  
  &:hover {
    background: rgba(0,0,0,0.04);
  }
  
  .icon {
    width: 20px;
    height: 20px;
    color: var(--brand-gold-alt);
    flex: 0 0 auto;
    margin-top: 2px;
  }
  
  .content {
    display: flex;
    flex-direction: column;
    gap: 4px;
    min-width: 0;
  }
  
  .title {
    font-weight: 600;
    color: #222;
    font-size: 0.92rem;
    line-height: 1.2;
  }
  
  .message {
    color: #555;
    font-size: 0.88rem;
    line-height: 1.25;
    word-break: break-word;
  }
  
  .meta {
    color: var(--brand-text-muted);
    font-size: 0.78rem;
  }
`;

const NotificationEmpty = styled.div`
  padding: 16px 12px;
  text-align: center;
  color: #666;
  font-size: 0.92rem;
`;

const ViewAllButton = styled.button`
  width: 100%;
  padding: 10px 12px;
  margin-top: 6px;
  border: none;
  background: rgba(0,0,0,0.04);
  color: #333;
  border-radius: 10px;
  cursor: pointer;
  font-weight: 600;
  transition: all 0.2s ease;
  text-transform: lowercase;
  
  &:hover {
    background: rgba(0,0,0,0.06);
  }
`;

const HeroSection = styled.section`
  /* Speed paint for hero: avoid layout work beyond viewport */
  content-visibility: auto;
  contain-intrinsic-size: 1px 500px;
  width: 100%;
  min-height: calc(100dvh - 96px);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 2rem;
  padding-top: clamp(1.5rem, 4vh, 3rem);
  padding-bottom: clamp(2rem, 5vh, 4rem);
  position: relative;
  overflow: hidden;

  /* Previous ::before / ::after gradient overlays removed —
     the orbital canvas background handles its own warm gradient */

  @media (max-width: 768px) {
    padding-top: clamp(0.75rem, 4vh, 1.25rem);
    padding-left: 1.5rem;
    padding-right: 1.5rem;
    padding-bottom: 0; /* Remove extra space below hero on mobile */
    justify-content: flex-start; /* Align content to top instead of center on mobile */
    min-height: auto; /* Let content define height to eliminate large gap */
    margin-bottom: 0; /* Remove bottom margin entirely */
  }
  
  @media (max-width: 480px) {
    padding-top: clamp(0.5rem, 3vh, 1rem); /* Small top padding on small screens */
    padding-bottom: 0; /* Remove extra space below hero on small screens */
    min-height: auto; /* Let content define height to eliminate large gap */
    margin-bottom: 0; /* Remove bottom margin entirely */
  }

  
  
  /* Desktop: hero fills viewport, content centered */
  @media (min-width: 1024px) {
    justify-content: center;
    padding-top: clamp(2.5rem, 6vh, 4rem);
    padding-bottom: clamp(1.5rem, 4vh, 3rem);
  }

  @media (min-width: 768px) and (max-width: 1023px) {
    justify-content: center;
    padding-top: clamp(0.5rem, 4.5vh, 1.75rem);
    /* Ensure hero content clears the fixed prompt bar on tablet widths */
    padding-bottom: max(clamp(6px, 1vh, 16px), var(--prompt-bar-offset));
  }
`;
const HeroCanvasWrapper = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 0;
  pointer-events: none;
  overflow: hidden;

  canvas {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    transition: opacity 0.3s ease;

    @media (max-width: 768px) {
      opacity: 0.15; /* Make hovering background items deeply lighter/fainter on mobile to un-crowd the hero text */
    }
  }

  .hero-vignette {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: radial-gradient(ellipse at center, transparent 45%, rgba(250, 250, 248, 0.6) 100%);
    pointer-events: none;
  }

  .hero-grain {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    pointer-events: none;
    opacity: 0.03;
    background-image: url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E");
    background-repeat: repeat;
    background-size: 180px 180px;
  }
`;
const HeroContent = styled.div`
  /* Ensure text paints immediately */
  contain: layout style;
  max-width: 1200px;
  width: 100%;
  text-align: center;
  z-index: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 0 1rem;
  animation: ${reveal} 1s cubic-bezier(0.175, 0.885, 0.32, 1.275);
  position: relative;
  margin-top: clamp(0.5rem, 2vh, 1.25rem);
  margin-bottom: clamp(0.5rem, 2vh, 1.25rem);
  
  &::before {
    content: '';
    position: absolute;
    top: -40px; /* Reduced from -60px to -40px */
    left: 50%;
    transform: translateX(-50%);
    width: 200px;
    height: 180px;
    background: linear-gradient(135deg, rgba(200, 167, 94, 0.04), rgba(26, 26, 26, 0.04));
    filter: blur(40px);
    border-radius: 50%;
    z-index: -1;
  }
  
  @media (max-width: 768px) {
    padding-top: 0.25rem; /* Small top padding on mobile */
    margin-top: clamp(0.5rem, 4vh, 1.5rem);
    margin-bottom: clamp(0.5rem, 3vh, 1rem);
    
    &::before {
      top: -16px; /* Reduced by 60% from -40px to -16px */
      width: 140px;
      height: 100px;
    }
  }
  
  /* Desktop: compact margins */
  @media (min-width: 1024px) {
    margin-top: clamp(1rem, 3vh, 2rem);
    margin-bottom: clamp(0.75rem, 2vh, 1.5rem);
  }

  /* Smooth transition band between 768px and 960px where bars overlap in some aspect ratios */
  @media (min-width: 768px) and (max-width: 960px) {
    margin-bottom: max(clamp(0.25rem, 2vh, 1rem), calc(var(--prompt-bar-offset) * 0.6));
  }
`;

const Logo = styled.div`
  font-family: 'Space Grotesk', sans-serif;
  font-size: 3rem;
  font-weight: 700;
  margin-top: clamp(0.25rem, 1vh, 0.5rem);
  margin-bottom: clamp(0.75rem, 2vh, 1.25rem);
  color: var(--brand-text);
  display: inline-flex;
  align-items: center;
  gap: 0;

  @media (min-width: 1024px) {
    margin-top: clamp(0.25rem, 1vh, 0.5rem);
    margin-bottom: clamp(0.5rem, 1.5vh, 0.75rem);
  }
  letter-spacing: -0.03em;
  animation: ${fadeIn} 1s ease-out;
  position: relative;
  transition: transform 220ms cubic-bezier(0.2, 0.8, 0.2, 1), filter 220ms ease, color 220ms ease;
  padding: 0;
  
  &::before {
    display: none;
  }
  
  &::after {
    content: '';
    position: absolute;
    bottom: -10px;
    left: 50%;
    transform: translateX(-50%);
    width: 40px;
    height: 3px;
    background: linear-gradient(135deg, var(--brand-gold-alt), var(--brand-black));
    border-radius: 3px;
    transition: all 0.3s ease;
  }
  
  &:hover {
    transform: translateY(-2px) scale(1.02);
    filter: drop-shadow(0 6px 16px rgba(200, 167, 94, 0.12));
    color: #0e0e0e;
    
    &::after {
      width: 60px;
      background: linear-gradient(135deg, #B8924C, var(--brand-black));
    }
  }

  &:active {
    transform: translateY(0) scale(0.985);
    filter: drop-shadow(0 2px 8px rgba(200, 167, 94, 0.08));
  }

  @media (max-width: 768px) {
    margin-top: clamp(0.25rem, 2.5vh, 0.75rem);
    margin-bottom: clamp(1rem, 3vh, 1.75rem);
    font-size: 2.2rem;
  }
  
  @media (max-width: 480px) {
    margin-top: clamp(0.25rem, 2vh, 0.5rem);
    margin-bottom: clamp(0.85rem, 2.5vh, 1.5rem);
    font-size: 2rem;
  }
  
  /* Desktop tighten */
  @media (min-width: 1024px) {
    margin-top: clamp(0.25rem, 1.5vh, 0.75rem);
    margin-bottom: clamp(0.5rem, 1.8vh, 1rem);
  }
`;
const InlineLogo = styled.img`
  display: inline-block;
  vertical-align: baseline;
  /* width:auto so the HTML width/height attributes (added for CLS-free
     intrinsic aspect-ratio) never win over the em-based CSS height. */
  width: auto;
  object-fit: contain;
  filter: drop-shadow(0 2px 6px rgba(200, 167, 94, 0.15));
`;

const HeroInlineLogo = styled(InlineLogo)`
  height: 1.1em;
  margin-left: -0.12em;
  margin-bottom: 0.18em;
`;

const SmallInlineLogo = styled(InlineLogo)`
  height: 1.15em;
  margin-left: -0.12em;
  margin-bottom: -0.15em;
`;
const Title = styled.h1`
  font-family: 'Space Grotesk', sans-serif;
  color: #000;
  margin-bottom: clamp(0.75rem, 2vh, 1.25rem);
  line-height: 1.1;
  position: relative;
  width: 100%;
  max-width: 1100px;
  text-align: center;
  font-weight: 700;
  animation: ${fadeIn} 1.2s ease-out;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding-top: 0.25rem;
  text-wrap: balance;

  /* The acronym is written "AI" in the source string; this only stops the
     global lowercase rule from flattening it. Never uppercase -- no CSS on
     this platform forces copy into caps (owner rule 2026-08-16, pinned by
     tests/policy/noUppercase.test.ts). Same idea as KeepCase for Prest0n.
     NOTE: no backticks in here. This comment lives inside a styled-components
     template literal, where a backtick ends the literal and breaks the build. */
  .ai-uppercase {
    text-transform: none;
  }
  
  @media (max-width: 768px) {
    margin-bottom: clamp(0.5rem, 2vh, 1rem);
    padding-top: 0.35rem;
  }
  
  /* ensure the title block provides additional clearance on tablet widths */
  @media (min-width: 768px) and (max-width: 1023px) {
    margin-bottom: max(clamp(0.8rem, 2.2vh, 1.5rem), calc(var(--prompt-bar-offset) * 0.35));
  }
  
  .first-heading {
    @media (max-width: 768px) {
      margin-bottom: 0.4rem !important;
      font-size: clamp(1.8rem, 7vw, 2.8rem) !important;
      padding-top: 0.5rem !important;
      max-width: 100% !important;
      letter-spacing: 0.01em !important;
    }
  }
  
  .main-heading {
    @media (max-width: 768px) {
      margin-bottom: 1.5rem !important;
      font-size: clamp(1.8rem, 7vw, 3rem) !important;
      padding-bottom: 0.6rem !important;
      margin-top: 0.1rem !important;
    }
  }
  
  .top-line {
    font-size: clamp(2.25rem, 5.5vw, 4.25rem);
    margin-bottom: -0.2rem;
    letter-spacing: -0.03em;
    background: linear-gradient(to right, var(--brand-black), var(--brand-text));
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    position: relative;
    display: inline-block;
    transition: transform 200ms cubic-bezier(0.2, 0.8, 0.2, 1), text-shadow 200ms ease;
    
    &::after {
      content: '';
      position: absolute;
      bottom: 0;
      left: 0;
      width: 100%;
      height: 4px;
      background: linear-gradient(to right, rgba(200, 167, 94, 0.07), transparent);
      border-radius: 4px;
    }

    &:hover {
      transform: translateY(-1px);
      text-shadow: 0 6px 18px rgba(0,0,0,0.08);
    }

    &:active {
      transform: translateY(0);
      text-shadow: 0 3px 9px rgba(0,0,0,0.06);
    }
  }
  
  .bottom-line {
    font-size: clamp(2.25rem, 5.5vw, 4.25rem);
    margin-top: -0.15rem;
    letter-spacing: -0.03em;
    transition: transform 200ms cubic-bezier(0.2, 0.8, 0.2, 1), text-shadow 200ms ease;

    &:hover {
      transform: translateY(-1px);
      text-shadow: 0 8px 22px rgba(200, 167, 94, 0.12);
    }

    &:active {
      transform: translateY(0);
      text-shadow: 0 4px 12px rgba(200, 167, 94, 0.08);
    }
  }
  
  span {
    background: linear-gradient(135deg, var(--brand-gold-alt), var(--brand-black));
    background-size: 200% auto;
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    animation: ${gradientShift} 8s ease infinite;
    position: relative;
    transition: transform 220ms cubic-bezier(0.2, 0.8, 0.2, 1), text-shadow 220ms ease;
    
    &::after {
      content: '';
      position: absolute;
      bottom: -2px;
      left: 0;
      width: 100%;
      height: 12px;
      background: linear-gradient(135deg, rgba(200, 167, 94, 0.15), rgba(26, 26, 26, 0.15));
      border-radius: 5px;
      z-index: -1;
    }
    
    /* Removed white background and adjusted shine effect */
    &::before {
      content: '';
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      background: linear-gradient(to right, transparent, rgba(255, 255, 255, 0.15), transparent);
      transform: skewX(-15deg);
      animation: ${shine} 5s infinite;
      background-position: -100px;
      z-index: -1;
    }
    
    &:hover {
      transform: translateY(-2px) scale(1.01);
      text-shadow: 0 8px 24px rgba(200, 167, 94, 0.15);
    }
    
    &:active {
      transform: translateY(0) scale(0.995);
      text-shadow: 0 4px 12px rgba(200, 167, 94, 0.1);
    }
  }
  
  /* Desktop: compact title spacing */
  @media (min-width: 1024px) {
    margin-bottom: clamp(0.2rem, 0.6vh, 0.4rem);
  }
`;
const TypewriterContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  margin: clamp(0.5rem, 1.5vh, 1rem) 0 clamp(0.75rem, 3vh, 1.5rem);
  position: relative;
  width: 100%;
  max-width: 600px;
  z-index: 1;
  transition: transform 220ms cubic-bezier(0.2, 0.8, 0.2, 1), filter 220ms ease;
  
  &::before {
    content: '';
    position: absolute;
    top: -40px; /* Reduced from -60px to -40px */
    left: 50%;
    transform: translateX(-50%);
    width: 200px;
    height: 180px;
    background: linear-gradient(135deg, rgba(200, 167, 94, 0.04), rgba(26, 26, 26, 0.04));
    filter: blur(40px);
    border-radius: 50%;
    z-index: -1;
  }
  
  @media (max-width: 768px) {
    margin: 0.75rem 0 1rem;
    &::before { display: none; }
  }
  
  /* Desktop: compact typewriter spacing */
  @media (min-width: 1024px) {
    margin: clamp(0.3rem, 1vh, 0.6rem) 0 clamp(0.4rem, 1.2vh, 0.7rem);
  }
  
  &:hover {
    transform: translateY(-2px);
    filter: drop-shadow(0 8px 20px rgba(0,0,0,0.08));
  }
  
  &:active {
    transform: translateY(0) scale(0.995);
    filter: drop-shadow(0 4px 12px rgba(0,0,0,0.06));
  }
`;

const TypewriterLabel = styled.div`
  font-family: 'Space Grotesk', sans-serif;
  font-size: clamp(0.9rem, 3.5vw, 1.1rem);
  color: #565656;
  margin-bottom: clamp(0.35rem, 1.2vh, 0.6rem);
  letter-spacing: 0.02em;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 500;
  transition: transform 200ms cubic-bezier(0.2, 0.8, 0.2, 1), color 200ms ease;
  
  &::after {
    content: '';
    position: absolute;
    bottom: -3px;
    left: 50%;
    transform: translateX(-50%);
    width: 40px;
    height: 1px;
    background: linear-gradient(90deg, rgba(200, 167, 94, 0.5), rgba(26, 26, 26, 0.5));
  }
  
  &:hover {
    transform: translateY(-1px);
    color: #3b3b3b;
  }
  
  &:active {
    transform: translateY(0);
    color: #565656;
  }

  @media (max-width: 768px) {
    font-size: clamp(0.85rem, 3.5vw, 1rem);
    margin-bottom: 0.35rem;
    &::after { display: none; }
  }
`;

const TypewriterText = styled.div`
  font-family: 'Space Grotesk', sans-serif;
  font-size: clamp(1rem, 4vw, 1.4rem);
  color: #2a2a2a;
  margin-top: clamp(0.35rem, 1.25vh, 0.75rem);
  display: flex;
  justify-content: center;
  align-items: center;
  text-align: center;
  position: relative;
  background: white;
  padding: 0.85rem 2.5rem;
  border-radius: 12px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.06);
  width: 100%;
  max-width: 95%;
  overflow: visible;
  min-height: 3.4rem;
  border: 1px solid rgba(0, 0, 0, 0.08);
  transition: transform 220ms cubic-bezier(0.2, 0.8, 0.2, 1), box-shadow 220ms ease, background 220ms ease;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: linear-gradient(135deg, rgba(200, 167, 94, 0.02), rgba(26, 26, 26, 0.02));
    pointer-events: none;
  }
  
  &:hover {
    background: #fafafa;
    cursor: default;
    transform: translateY(-2px) scale(1.01);
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);
  }
  
  &:active {
    transform: translateY(0) scale(0.995);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
  }

  /* Mobile: keep white box but make it text-driven height, no clipping */
  @media (max-width: 768px) {
    display: block;
    text-align: center;
    padding: 0.85rem 1rem;
    min-height: unset;
    height: auto;
    width: 90%;
    max-width: 90%;
    font-size: clamp(0.95rem, 4.5vw, 1.15rem);
    font-weight: 500;
    line-height: 1.6;
    overflow: visible;
    white-space: normal;
    word-break: break-word;
    
    &:hover { transform: none; box-shadow: 0 2px 12px rgba(0,0,0,0.06); }
    &:active { transform: none; }
  }
`;

const PromptDecoration = styled.button`
  position: absolute;
  left: 12px;
  color: var(--brand-gold-alt);
  font-size: 1em;
  opacity: 0.6;
  background: none;
  border: none;
  cursor: pointer;
  user-select: none;
  padding: 0;
  margin: 0;
  outline: none;
  transition: all 0.2s ease;
  
  &:hover {
    opacity: 1;
    transform: scale(1.1);
  }
  
  &:active {
    transform: scale(0.95);
  }
  
  &:focus {
    opacity: 1;
    outline: 2px solid rgba(200, 167, 94, 0.3);
    outline-offset: 2px;
  }
  
  @media (max-width: 768px) {
    display: none;
  }
`;

const TypingText = styled.span`
  display: inline;
  color: var(--brand-gold-alt);
  font-weight: 600;
  white-space: normal;
  word-break: break-word;
  overflow-wrap: break-word;
  line-height: 1.6;
  font-family: 'Space Grotesk', sans-serif;
  text-shadow: 0 0 1px rgba(200, 167, 94, 0.1);
`;

// Cursor component with no gap
const Cursor = styled.span<{ $isTyping: boolean }>`
  display: inline-block;
  width: 2px;
  height: 0.85em;
  background-color: var(--brand-gold-alt);
  vertical-align: middle;
  margin-left: 1px;
  animation: ${props => props.$isTyping ? cursorSolid : cursorBlink} ${props => props.$isTyping ? '0.75s' : '1.1s'} step-end infinite;
`;
// Create a Typewriter component that handles text rotation
const Typewriter: React.FC<{ texts: string[]; onNext?: () => void }> = ({ texts, onNext }) => {
  const [displayText, setDisplayText] = useState("");
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isTyping, setIsTyping] = useState(true);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isActivelyTyping, setIsActivelyTyping] = useState(true);
  const [isManuallyControlled, setIsManuallyControlled] = useState(false);
  
  // Function to move to next text manually
  const moveToNext = () => {
    setIsManuallyControlled(true);
    const nextIndex = (currentIndex + 1) % texts.length;
    setCurrentIndex(nextIndex);
    setDisplayText("");
    setIsTyping(true);
    setIsDeleting(false);
    setIsActivelyTyping(true);
    
    // Resume automatic cycling after manual interaction
    setTimeout(() => {
      setIsManuallyControlled(false);
    }, 3000);
    
    if (onNext) {
      onNext();
    }
  };
  
  useEffect(() => {
    if (!texts.length) return;
    
    let timeout: NodeJS.Timeout;
    
    if (isTyping) {
      setIsActivelyTyping(true);
      if (displayText.length < texts[currentIndex].length) {
        // Still typing
        timeout = setTimeout(() => {
          setDisplayText(texts[currentIndex].slice(0, displayText.length + 1));
        }, 20); // Fast typing speed - reduced from 40ms to 20ms to go twice as fast
      } else {
        // Finished typing, pause before deleting
        setIsActivelyTyping(false); // Now cursor should blink
        timeout = setTimeout(() => {
          setIsTyping(false);
        }, isManuallyControlled ? 1000 : 2500); // Shorter pause if manually controlled - reduced from 5000ms to 2500ms to go twice as fast
      }
    } else if (isDeleting) {
      setIsActivelyTyping(true);
      if (displayText.length > 0) {
        // Still deleting
        timeout = setTimeout(() => {
          setDisplayText(displayText.slice(0, displayText.length - 1));
        }, 15); // Fast delete speed - reduced from 30ms to 15ms to go twice as fast
      } else {
        // Finished deleting, move to next text (only if not manually controlled)
        if (!isManuallyControlled) {
          const nextIndex = (currentIndex + 1) % texts.length;
          setCurrentIndex(nextIndex);
          setIsTyping(true);
        }
      }
    } else {
      // Start deleting after pause (only if not manually controlled)
      if (!isManuallyControlled) {
        timeout = setTimeout(() => {
          setIsDeleting(true);
          setIsActivelyTyping(true);
        }, 100); // Delay before starting to delete - reduced from 200ms to 100ms to go twice as fast
      }
    }
    
    return () => clearTimeout(timeout);
  }, [texts, currentIndex, displayText, isTyping, isDeleting, isManuallyControlled]);
  
  return (
    <div style={{ 
      textAlign: 'center',
      width: '100%',
    }}>
      <PromptDecoration onClick={moveToNext} title="Click to advance to next text">
        {'>'}
      </PromptDecoration>
      <TypingText>{displayText}</TypingText>
      <Cursor $isTyping={isActivelyTyping} />
    </div>
  );
};
// Hero Demo Video — click-to-play modal approach
const DemoPlayButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 0.7rem;
  margin-top: clamp(1.25rem, 3vh, 2rem);
  padding: 0.85rem 2rem;
  background: var(--brand-text);
  border: none;
  border-radius: 999px;
  color: #fff;
  font-family: 'Space Grotesk', 'Inter', sans-serif;
  font-size: 1.05rem;
  font-weight: 600;
  letter-spacing: 0.02em;
  cursor: pointer;
  transition: all 0.25s ease;
  z-index: 1;
  animation: ${fadeIn} 1.6s ease-out;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.18);

  &:hover {
    background: #000;
    transform: translateY(-2px);
    box-shadow: 0 8px 28px rgba(0, 0, 0, 0.25);
  }

  &:active {
    transform: translateY(0);
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
  }

  svg {
    width: 20px;
    height: 20px;
    fill: var(--brand-gold-alt);
  }

  @media (max-width: 768px) {
    font-size: 1rem;
    padding: 0.75rem 1.75rem;
    gap: 0.6rem;

    svg {
      width: 18px;
      height: 18px;
    }
  }
`;

const VideoModalOverlay = styled.div<{ $isOpen: boolean; $mobileFullscreen?: boolean }>`
  position: fixed;
  inset: 0;
  z-index: 10000;
  display: ${({ $isOpen }) => ($isOpen ? 'flex' : 'none')};
  align-items: center;
  justify-content: center;
  background: rgba(0, 0, 0, 0.88);
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
  padding: 2rem;
  cursor: pointer;

  @media (max-width: 768px) {
    padding: ${({ $mobileFullscreen }) => $mobileFullscreen ? '0' : '1rem'};
    background: ${({ $mobileFullscreen }) => $mobileFullscreen ? '#000' : 'rgba(0, 0, 0, 0.88)'};
  }
`;

const VideoModalContent = styled.div<{ $mobileFullscreen?: boolean }>`
  position: relative;
  width: 100%;
  max-width: 1100px;
  cursor: default;

  @media (max-width: 768px) {
    ${({ $mobileFullscreen }) => $mobileFullscreen ? `
      max-width: 100%;
      width: 100vw;
      height: 100vh;
      display: flex;
      align-items: center;
      justify-content: center;
    ` : ''}
  }
`;

const VideoModalContainer = styled.div<{ $mobileFullscreen?: boolean }>`
  position: relative;
  width: 100%;
  padding-top: 56.25%; /* 16:9 */
  border-radius: 12px;
  overflow: hidden;
  background: #0b0c0f;
  box-shadow: 0 20px 80px rgba(0, 0, 0, 0.5);

  video {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    object-fit: contain;
    border-radius: 12px;
  }

  @media (max-width: 768px) {
    border-radius: ${({ $mobileFullscreen }) => $mobileFullscreen ? '0' : '8px'};
    box-shadow: ${({ $mobileFullscreen }) => $mobileFullscreen ? 'none' : '0 20px 80px rgba(0, 0, 0, 0.5)'};
    
    ${({ $mobileFullscreen }) => $mobileFullscreen ? `
      /* CSS fallback for mobile landscape: rotate container 90° so widescreen video fills portrait screen */
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%) rotate(90deg);
      width: 100vh;
      height: 100vw;
      padding-top: 0;
      
      video {
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        object-fit: contain;
        border-radius: 0;
      }
    ` : `
      video { border-radius: 8px; }
    `}
  }
`;

const VideoModalClose = styled.button`
  position: absolute;
  top: -44px;
  right: 0;
  background: none;
  border: none;
  color: rgba(255, 255, 255, 0.6);
  font-size: 1.5rem;
  cursor: pointer;
  padding: 4px 8px;
  transition: color 0.2s;
  font-family: 'Inter', sans-serif;

  &:hover {
    color: #fff;
  }

  @media (max-width: 768px) {
    top: -36px;
    font-size: 1.3rem;
  }
`;

// Benefits Section - Emphasizing user benefits with larger, more prominent design
const BenefitsSection = styled.section`
  width: 100%;
  padding: 6rem 2rem 8rem;
  display: flex;
  flex-direction: column;
  align-items: center;
  background: linear-gradient(135deg, #f8fbff 0%, #ffffff 50%, #f5f9ff 100%);
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: linear-gradient(135deg, rgba(200, 167, 94, 0.05) 0%, rgba(26, 26, 26, 0.05) 100%);
    z-index: 0;
  }
  
  @media (max-width: 768px) {
    padding: 0.3rem 1.5rem 6rem;
  }
`;
const BenefitsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(280px, 1fr));
  gap: clamp(1.75rem, 2.5vw, 3rem);
  max-width: 1200px;
  width: 100%;
  position: relative;
  z-index: 1;
  margin: 0 auto;
  align-items: stretch;
  justify-items: stretch;

  @media (max-width: 1200px) {
    grid-template-columns: repeat(3, minmax(240px, 1fr));
    max-width: 1000px;
  }

  @media (max-width: 960px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
    max-width: 100%;
  }

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 2rem;
    padding: 0 0.75rem;
  }

  @media (max-width: 480px) {
    gap: 1.5rem;
    padding: 0;
  }
`;

const BenefitCard = styled.div<{ isExpanded?: boolean }>`
  background: rgba(255, 255, 255, 0.95);
  padding: 3.5rem 2.5rem;
  border-radius: 24px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.08);
  display: flex;
  flex-direction: column;
  align-items: center;
  transition: all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275);
  border: 1px solid rgba(255, 255, 255, 0.6);
  position: relative;
  overflow: hidden;
  backdrop-filter: blur(10px);
  cursor: pointer;
  min-height: ${props => props.isExpanded ? '450px' : '380px'};
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 8px;
    background: linear-gradient(to right, var(--brand-gold-alt), #62b9ff);
    opacity: ${props => props.isExpanded ? 1 : 0.3};
    transition: opacity 0.3s ease;
  }
  
  &:hover {
    transform: translateY(-10px);
    box-shadow: 0 25px 70px rgba(0, 0, 0, 0.12);
    
    &::before {
      opacity: 1;
    }
  }
  
  &:nth-child(1) {
    animation: ${fadeInLeft} 0.8s ease-out;
  }
  
  &:nth-child(2) {
    animation: ${fadeIn} 1s ease-out;
  }
  
  &:nth-child(3) {
    animation: ${fadeInRight} 1.2s ease-out;
  }
  
  @media (max-width: 768px) {
    min-height: ${props => props.isExpanded ? '400px' : '350px'};
    padding: 3rem 2rem;
    
    &:hover {
      transform: translateY(-5px);
    }
  }
`;

const BenefitIcon = styled.div`
  width: 140px;
  height: 140px;
  border-radius: 28px;
  background: linear-gradient(135deg, #f0f8ff, #e6f3ff);
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 2.5rem;
  position: relative;
  
  svg {
    width: 80px;
    height: 80px;
    color: var(--brand-gold-alt);
    filter: drop-shadow(0 4px 8px rgba(200, 167, 94, 0.3));
  }
  
  &::after {
    content: '';
    position: absolute;
    bottom: -15px;
    left: 50%;
    transform: translateX(-50%);
    width: 50%;
    height: 6px;
    background: linear-gradient(to right, var(--brand-gold-alt), #62b9ff);
    border-radius: 6px;
  }
`;
const BenefitTitle = styled.h3<{ isExpanded?: boolean }>`
  font-family: 'Space Grotesk', sans-serif;
  font-size: clamp(1.6rem, 3vw, 2rem);
  color: var(--brand-black);
  margin-bottom: 1.5rem;
  font-weight: 600;
  text-align: center;
  letter-spacing: -0.01em;
  position: relative;
  transition: color 0.3s ease;
  
  &::after {
    content: '${props => props.isExpanded ? '−' : '+'}';
    position: absolute;
    right: -35px;
    top: 50%;
    transform: translateY(-50%);
    width: 24px;
    height: 24px;
    border-radius: 50%;
    background: linear-gradient(135deg, var(--brand-gold-alt), #64B5F6);
    color: white;
    font-size: 1rem;
    display: flex;
    align-items: center;
    justify-content: center;
    opacity: 0;
    transition: all 0.3s ease;
    font-weight: 400;
    line-height: 1;
  }
  
  /* Show indicator on hover or when expanded */
  ${BenefitCard}:hover &::after,
  ${props => props.isExpanded && `&::after`} {
    opacity: 1;
  }
  
  @media (max-width: 768px) {
    &::after {
      right: -30px;
      width: 20px;
      height: 20px;
      font-size: 0.875rem;
    }
  }
  
  @media (max-width: 480px) {
    &::after {
      right: -25px;
      width: 18px;
      height: 18px;
      font-size: 0.8rem;
    }
  }
`;
const BenefitExpandedContent = styled.div<{ isExpanded: boolean }>`
  opacity: ${props => props.isExpanded ? 1 : 0};
  max-height: ${props => props.isExpanded ? '250px' : '0'};
  overflow: hidden;
  transition: all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275);
  margin-top: ${props => props.isExpanded ? '1.5rem' : '0'};
  padding: ${props => props.isExpanded ? '1.5rem' : '0'} 1rem 0;
  border-top: ${props => props.isExpanded ? '1px solid rgba(200, 167, 94, 0.1)' : 'none'};
  
  @media (max-width: 768px) {
    max-height: ${props => props.isExpanded ? '280px' : '0'};
    padding: ${props => props.isExpanded ? '1.25rem' : '0'} 0.75rem 0;
    transition: all 0.35s ease;
    margin-top: ${props => props.isExpanded ? '1.25rem' : '0'};
  }
  
  @media (max-width: 480px) {
    max-height: ${props => props.isExpanded ? '320px' : '0'};
    padding: ${props => props.isExpanded ? '1rem' : '0'} 0.5rem 0;
  }
`;

const BenefitExpandedDescription = styled.p<{ isAnimated?: boolean }>`
  color: #4a5568;
  font-size: clamp(1rem, 2vw, 1.1rem);
  line-height: 1.7;
  text-align: center;
  margin: 0;
  opacity: 0;
  animation: ${props => props.isAnimated ? css`${fadeInContent} 0.5s ease 0.2s forwards` : 'none'};
  
  @media (max-width: 768px) {
    font-size: 0.95rem;
    line-height: 1.6;
  }
`;
const fadeInContent = keyframes`
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;
// SVG Icons for the feature cards
const IntuitiveTeachingIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"></path>
    <polyline points="7.5 4.21 12 6.81 16.5 4.21"></polyline>
    <polyline points="7.5 19.79 7.5 14.6 3 12"></polyline>
    <polyline points="21 12 16.5 14.6 16.5 19.79"></polyline>
    <polyline points="3.27 6.96 12 12.01 20.73 6.96"></polyline>
    <line x1="12" y1="22.08" x2="12" y2="12"></line>
  </svg>
);

const CustomizationIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="3"></circle>
    <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z"></path>
  </svg>
);
const SystemCompatibilityIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <rect x="4" y="4" width="16" height="16" rx="2" ry="2"></rect>
    <rect x="9" y="9" width="6" height="6"></rect>
    <line x1="9" y1="2" x2="9" y2="4"></line>
    <line x1="15" y1="2" x2="15" y2="4"></line>
    <line x1="9" y1="20" x2="9" y2="22"></line>
    <line x1="15" y1="20" x2="15" y2="22"></line>
    <line x1="20" y1="9" x2="22" y2="9"></line>
    <line x1="20" y1="15" x2="22" y2="15"></line>
    <line x1="2" y1="9" x2="4" y2="9"></line>
    <line x1="2" y1="15" x2="4" y2="15"></line>
  </svg>
);

const AmbientFunctionalityIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 2a10 10 0 1 0 0 20 10 10 0 0 0 0-20z"></path>
    <path d="M12 8v8"></path>
    <path d="M8 12h8"></path>
    <path d="M4 12h2"></path>
    <path d="M18 12h2"></path>
    <path d="M12 4v2"></path>
    <path d="M12 18v2"></path>
  </svg>
);

const SelfOptimizingIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"></path>
    <circle cx="12" cy="12" r="3"></circle>
    <path d="M12 9a3 3 0 0 1 0 6"></path>
    <path d="M12 9a3 3 0 0 0 0 6"></path>
  </svg>
);
// New feature icons for the 7 additional features
const InfiniteCapabilityIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2z"></path>
    <path d="M8 12l2 2 4-4"></path>
    <path d="M16 8l-2 2-2-2"></path>
    <path d="M8 16l2-2 2 2"></path>
    <circle cx="12" cy="12" r="1"></circle>
  </svg>
);

const SmartIntentRecognitionIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"></path>
    <circle cx="12" cy="12" r="2"></circle>
    <path d="M9 9l6 6"></path>
    <path d="M15 9l-6 6"></path>
  </svg>
);

const SmartBreakdownIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
    <line x1="9" y1="9" x2="15" y2="9"></line>
    <line x1="9" y1="15" x2="15" y2="15"></line>
    <line x1="9" y1="12" x2="15" y2="12"></line>
    <circle cx="6" cy="9" r="1"></circle>
    <circle cx="6" cy="12" r="1"></circle>
    <circle cx="6" cy="15" r="1"></circle>
  </svg>
);

const AdaptivePlanningIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 2l2 4h4l-3.5 3.5L16 14l-4-2-4 2 1.5-4.5L6 6h4l2-4z"></path>
    <path d="M8 16l2-1 2 1v3l-2-1-2 1v-3z"></path>
    <circle cx="12" cy="12" r="1"></circle>
  </svg>
);

const IntelligentToolSelectionIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z"></path>
    <circle cx="12" cy="12" r="2"></circle>
  </svg>
);

const EasyKnowledgeUseIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20"></path>
    <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z"></path>
    <path d="M8 7h8"></path>
    <path d="M8 11h8"></path>
    <path d="M8 15h5"></path>
  </svg>
);

const RealtimeOnlineSearchIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="11" cy="11" r="8"></circle>
    <path d="M21 21l-4.35-4.35"></path>
    <path d="M11 6a5 5 0 0 1 5 5"></path>
    <circle cx="11" cy="11" r="3"></circle>
  </svg>
);

// Icons for Benefits Section
const EasyCreateIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"></polygon>
  </svg>
);

const ExactlyWhatYouNeedIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10"></circle>
    <path d="M8 14s1.5 2 4 2 4-2 4-2"></path>
    <line x1="9" y1="9" x2="9.01" y2="9"></line>
    <line x1="15" y1="9" x2="15.01" y2="9"></line>
    <path d="M12 6l-2 2h4l-2-2z"></path>
    <circle cx="12" cy="12" r="3"></circle>
  </svg>
);

const TrustShieldIcon = () => (
  <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 2l7 3v6c0 5-3.5 10-7 11-3.5-1-7-6-7-11V5l7-3z"></path>
    <path d="M9 11l2 2 4-4"></path>
    <circle cx="12" cy="12" r="9"></circle>
  </svg>
);
const ActionTitle = styled.h2`
  font-family: 'Space Grotesk', sans-serif;
  font-size: clamp(1.2rem, 5.5vw, 3.75rem);
  font-weight: 700;
  color: #fff;
  margin-bottom: 3rem;
  letter-spacing: -0.02em;
  text-transform: none;
  text-align: center;
  line-height: 1.3;
  width: 100%;
  
  @media (min-width: 500px) {
    white-space: nowrap;
  }
`;
const FooterWrapper = styled.div`
  width: 100%;
  margin-top: auto;
`;
// Add these interfaces and utility functions after other imports
interface EmailSubscription {
  email: string;
  timestamp: string;
  source: 'chat' | 'signup';
  metadata?: Record<string, any>;
}
// Update to use user_accounts storage
const saveEmailSubscription = (email: string, source: 'chat' | 'signup', metadata?: Record<string, any>) => {
  try {
    // Get existing emails from all storage keys
    const existingEmailSubsStr = localStorage.getItem('email_subscriptions');
    const existingUserAccountsStr = localStorage.getItem('user_accounts');
    const existingProfilesStr = localStorage.getItem('all_profiles_data');
    const userProfileStr = localStorage.getItem('user_profile');
    
    const existingEmailSubs: EmailSubscription[] = existingEmailSubsStr ? JSON.parse(existingEmailSubsStr) : [];
    const existingUserAccounts: EmailSubscription[] = existingUserAccountsStr ? JSON.parse(existingUserAccountsStr) : [];
    const existingProfiles: any[] = existingProfilesStr ? JSON.parse(existingProfilesStr) : [];
    const currentUserProfile = userProfileStr ? JSON.parse(userProfileStr) : null;
    
    // Combine both account sources into a map to remove duplicates
    const allAccountsMap = new Map<string, EmailSubscription>();
    
    // Add from both sources
    [...existingEmailSubs, ...existingUserAccounts].forEach(item => {
      allAccountsMap.set(item.email.toLowerCase(), item);
    });
    
    // Convert back to array
    const existingData = Array.from(allAccountsMap.values());
    
    // Check if email already exists
    const emailExists = existingData.some(item => item.email.toLowerCase() === email.toLowerCase());
    
    if (!emailExists) {
      // Collect all existing IDs from all sources to ensure true uniqueness
      const existingAccountIds = new Set<string>();
      const existingMemberIds = new Set<string>();
      
      // From accounts metadata
      existingData.forEach(item => {
        if (item.metadata?.accountId && /^[1-9]\d{8}$/.test(item.metadata.accountId.toString())) {
          existingAccountIds.add(item.metadata.accountId.toString());
        }
        if (item.metadata?.profileId && /^[1-9]\d{8}$/.test(item.metadata.profileId.toString())) {
          existingMemberIds.add(item.metadata.profileId.toString());
        }
      });
      
      // From profiles
      [currentUserProfile, ...existingProfiles].filter(Boolean).forEach(profile => {
        if (profile.accountId && /^[1-9]\d{8}$/.test(profile.accountId.toString())) {
          existingAccountIds.add(profile.accountId.toString());
        }
        if (profile.memberId && /^[1-9]\d{8}$/.test(profile.memberId.toString())) {
          existingMemberIds.add(profile.memberId.toString());
        }
      });
      
      // Generate unique account ID (9-digit number starting with non-zero digit)
      let accountId = '';
      do {
        const firstDigit = Math.floor(Math.random() * 9) + 1; // 1-9
        const remainingDigits = Array.from({ length: 8 }, () => Math.floor(Math.random() * 10)).join(''); // 0-9
        accountId = `${firstDigit}${remainingDigits}`;
      } while (existingAccountIds.has(accountId));
      
      // Track the new ID to prevent later collisions
      existingAccountIds.add(accountId);
      
      // Generate unique member ID (9-digit number starting with non-zero digit)
      let memberId = '';
      do {
        const firstDigit = Math.floor(Math.random() * 9) + 1; // 1-9
        const remainingDigits = Array.from({ length: 8 }, () => Math.floor(Math.random() * 10)).join(''); // 0-9
        memberId = `${firstDigit}${remainingDigits}`;
      } while (existingMemberIds.has(memberId) || memberId === accountId); // Also ensure memberID != accountID
      
      // Add new email with unique IDs
      const newSubscription: EmailSubscription = {
        email,
        timestamp: new Date().toISOString(),
        source,
        metadata: {
          ...(metadata || {}),
          accountId: accountId,
          profileId: memberId // Using profileId for member ID to maintain compatibility
        }
      };
      
      const updatedData = [...existingData, newSubscription];
      
      // Save to both storage keys for compatibility
      localStorage.setItem('email_subscriptions', JSON.stringify(updatedData));
      localStorage.setItem('user_accounts', JSON.stringify(updatedData));
      
      // Trigger a storage event to notify other components
      window.dispatchEvent(new StorageEvent('storage', {
        key: 'user_accounts',
        newValue: JSON.stringify(updatedData)
      }));
      
      console.log(`Email ${email} saved successfully from ${source} with accountId: ${accountId}, memberId: ${memberId}`);
      
      // Return object with the IDs for confirmation
      return true;
    } else {
      console.log(`Email ${email} already exists in accounts.`);
      return false;
    }
  } catch (error) {
    console.error('Error saving email:', error);
    return false;
  }
};
// Now add the styled components for the mobile menu - add this after the HeaderIconButton styled component
const MobileMenuButton = styled.button`
  display: none;
  background: transparent;
  border: none;
  color: #333;
  font-size: 1.5rem;
  cursor: pointer;
  width: 40px;
  height: 40px;
  padding: 8px;
  border-radius: 8px;
  transition: all 0.2s ease;
  align-items: center;
  justify-content: center;
  z-index: 1010;
  
  &:hover {
    background: rgba(255, 255, 255, 0.8);
  }
  
  @media (max-width: 768px) {
    display: flex;
    position: absolute;
    top: 0.25rem;
    right: 1rem;
  }
`;

const MobileMenuDropdown = styled.div<{ $isOpen: boolean }>`
  display: none;
  position: fixed; /* ensure overlays prompt bars */
  top: 60px;
  right: 20px;
  background: white;
  border-radius: 12px;
  padding: 8px;
  box-shadow: 0 10px 25px rgba(0,0,0,0.1);
  flex-direction: column;
  min-width: 180px;
  z-index: 1000000; /* ensure above prompt bars and overlays */
  pointer-events: ${props => props.$isOpen ? 'auto' : 'none'};
  transform-origin: top right;
  transform: ${props => props.$isOpen ? 'scale(1)' : 'scale(0.95)'};
  opacity: ${props => props.$isOpen ? 1 : 0};
  visibility: ${props => props.$isOpen ? 'visible' : 'hidden'};
  transition: all 0.2s cubic-bezier(0.175, 0.885, 0.32, 1.275);
  
  @media (max-width: 768px) {
    display: flex;
  }
  
  @media (max-width: 480px) {
    right: 10px;
    top: 45px; /* Reduced from 65px to 45px */
  }
`;

const MobileMenuItem = styled(Link)`
  color: #333;
  text-decoration: none;
  padding: 10px 16px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  gap: 10px;
  transition: all 0.2s ease;
  font-weight: 500;
  
  &:hover {
    background: rgba(0,0,0,0.05);
  }
  
  svg {
    width: 18px;
    height: 18px;
  }
`;

// Action variant for non-navigation items in the mobile menu
const MobileMenuAction = styled.button`
  color: #333;
  text-decoration: none;
  padding: 10px 16px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  gap: 10px;
  transition: all 0.2s ease;
  font-weight: 500;
  background: transparent;
  border: none;
  width: 100%;
  text-align: left;
  cursor: pointer;
  
  &:hover {
    background: rgba(0,0,0,0.05);
  }
  
  svg {
    width: 18px;
    height: 18px;
  }
`;

const MobileMenuLoginButton = styled.button`
  background: linear-gradient(135deg, var(--brand-gold-alt), var(--brand-black));
  color: white;
  border: none;
  border-radius: 8px;
  padding: 12px 16px;
  margin-top: 8px;
  font-weight: 600;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(200, 167, 94, 0.25);
  }
  
  svg {
    width: 18px;
    height: 18px;
  }
`;

// Now add the hamburger icon SVG after the other icon components
const HamburgerIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="3" y1="12" x2="21" y2="12"></line>
    <line x1="3" y1="6" x2="21" y2="6"></line>
    <line x1="3" y1="18" x2="21" y2="18"></line>
  </svg>
);

// Add this interface after the ChatMessage interface
interface ExpandedCardState {
  [key: string]: boolean;
}

/* ─── Orbital canvas background for hero section ─── */
const GOLD = { r: 178, g: 150, b: 70 };
const DARK = { r: 26, g: 26, b: 26 };

class OrbitalNode {
  ring: number;
  baseAngle: number;
  speed: number;
  size: number;
  pulse: number;
  pulseSpeed: number;
  isGold: boolean;

  constructor(ring: number, index: number, total: number) {
    this.ring = ring;
    this.baseAngle = (Math.PI * 2 / total) * index;
    this.speed = (0.08 + Math.random() * 0.06) * (ring % 2 === 0 ? 1 : -1);
    this.size = 1.5 + Math.random() * 2.5;
    this.pulse = Math.random() * Math.PI * 2;
    this.pulseSpeed = 0.5 + Math.random() * 1.5;
    this.isGold = Math.random() < 0.35;
  }

  getPos(t: number, W: number, H: number, cx: number, cy: number) {
    const radii = [
      Math.min(W, H) * 0.18,
      Math.min(W, H) * 0.26,
      Math.min(W, H) * 0.35,
      Math.min(W, H) * 0.44,
    ];
    const r = radii[this.ring] || radii[0];
    const angle = this.baseAngle + t * this.speed * 0.01;
    const ex = 1 + this.ring * 0.08;
    const ey = 0.7 + this.ring * 0.04;
    return {
      x: cx + Math.cos(angle) * r * ex,
      y: cy + Math.sin(angle) * r * ey,
    };
  }

  draw(ctx: CanvasRenderingContext2D, t: number, W: number, H: number, cx: number, cy: number) {
    const pos = this.getPos(t, W, H, cx, cy);
    const pulseFactor = 0.6 + 0.4 * Math.sin(t * this.pulseSpeed * 0.03 + this.pulse);
    const col = this.isGold ? GOLD : DARK;
    const alpha = (this.isGold ? 0.75 : 0.28) * pulseFactor;

    ctx.beginPath();
    ctx.arc(pos.x, pos.y, this.size * 3.5, 0, Math.PI * 2);
    ctx.fillStyle = `rgba(${col.r},${col.g},${col.b},${alpha * 0.12})`;
    ctx.fill();

    ctx.beginPath();
    ctx.arc(pos.x, pos.y, this.size * pulseFactor, 0, Math.PI * 2);
    ctx.fillStyle = `rgba(${col.r},${col.g},${col.b},${alpha})`;
    ctx.fill();

    return pos;
  }
}

function initOrbitalNodes(): OrbitalNode[] {
  const nodes: OrbitalNode[] = [];
  const ringCounts = [8, 14, 20, 28];
  ringCounts.forEach((count, ring) => {
    for (let i = 0; i < count; i++) {
      nodes.push(new OrbitalNode(ring, i, count));
    }
  });
  return nodes;
}

function drawConnections(
  ctx: CanvasRenderingContext2D,
  positions: { x: number; y: number }[],
  nodes: OrbitalNode[],
  W: number,
  H: number
) {
  const maxDist = Math.min(W, H) * 0.12;
  ctx.lineWidth = 0.5;
  for (let i = 0; i < positions.length; i++) {
    for (let j = i + 1; j < positions.length; j++) {
      const dx = positions[i].x - positions[j].x;
      const dy = positions[i].y - positions[j].y;
      const dist = Math.sqrt(dx * dx + dy * dy);
      if (dist < maxDist) {
        const alpha = (1 - dist / maxDist) * 0.14;
        const isGoldLine = nodes[i].isGold && nodes[j].isGold;
        const col = isGoldLine ? GOLD : DARK;
        ctx.beginPath();
        ctx.moveTo(positions[i].x, positions[i].y);
        ctx.lineTo(positions[j].x, positions[j].y);
        ctx.strokeStyle = `rgba(${col.r},${col.g},${col.b},${alpha})`;
        ctx.stroke();
      }
    }
  }
}

function drawZeroRing(ctx: CanvasRenderingContext2D, t: number, W: number, H: number, cx: number, cy: number) {
  const r = Math.min(W, H) * 0.135;
  const ex = 1.15;
  const ey = 0.72;

  ctx.beginPath();
  ctx.ellipse(cx, cy, r * ex + 12, r * ey + 12, 0, 0, Math.PI * 2);
  ctx.strokeStyle = `rgba(${GOLD.r},${GOLD.g},${GOLD.b},0.1)`;
  ctx.lineWidth = 25;
  ctx.stroke();

  ctx.beginPath();
  ctx.ellipse(cx, cy, r * ex, r * ey, 0, 0, Math.PI * 2);
  ctx.strokeStyle = `rgba(${GOLD.r},${GOLD.g},${GOLD.b},0.4)`;
  ctx.lineWidth = 1.5;
  ctx.setLineDash([4, 8]);
  ctx.lineDashOffset = -t * 0.3;
  ctx.stroke();
  ctx.setLineDash([]);

  ctx.beginPath();
  ctx.ellipse(cx, cy, r * ex - 2, r * ey - 2, 0, 0, Math.PI * 2);
  ctx.strokeStyle = `rgba(${GOLD.r},${GOLD.g},${GOLD.b},0.2)`;
  ctx.lineWidth = 0.5;
  ctx.stroke();

  for (let k = 0; k < 3; k++) {
    const angle = t * 0.02 + (Math.PI * 2 / 3) * k;
    const px = cx + Math.cos(angle) * r * ex;
    const py = cy + Math.sin(angle) * r * ey;
    ctx.beginPath();
    ctx.arc(px, py, 3, 0, Math.PI * 2);
    ctx.fillStyle = `rgba(${GOLD.r},${GOLD.g},${GOLD.b},0.75)`;
    ctx.fill();
    for (let tr = 1; tr <= 6; tr++) {
      const ta = angle - tr * 0.04;
      const tx = cx + Math.cos(ta) * r * ex;
      const ty = cy + Math.sin(ta) * r * ey;
      ctx.beginPath();
      ctx.arc(tx, ty, 2.5 - tr * 0.3, 0, Math.PI * 2);
      ctx.fillStyle = `rgba(${GOLD.r},${GOLD.g},${GOLD.b},${0.45 - tr * 0.07})`;
      ctx.fill();
    }
  }
}

function drawGrid(ctx: CanvasRenderingContext2D, t: number, W: number, H: number, cx: number, cy: number) {
  ctx.globalAlpha = 0.06;
  const radii = [0.18, 0.26, 0.35, 0.44];
  radii.forEach((ratio, i) => {
    const r = Math.min(W, H) * ratio;
    const ex = 1 + i * 0.08;
    const ey = 0.7 + i * 0.04;
    ctx.beginPath();
    ctx.ellipse(cx, cy, r * ex, r * ey, 0, 0, Math.PI * 2);
    ctx.strokeStyle = `rgba(${DARK.r},${DARK.g},${DARK.b},1)`;
    ctx.lineWidth = 0.5;
    ctx.stroke();
  });

  for (let i = 0; i < 12; i++) {
    const angle = (Math.PI * 2 / 12) * i + t * 0.001;
    const outerR = Math.min(W, H) * 0.48;
    ctx.beginPath();
    ctx.moveTo(cx, cy);
    ctx.lineTo(cx + Math.cos(angle) * outerR, cy + Math.sin(angle) * outerR * 0.68);
    ctx.strokeStyle = `rgba(${DARK.r},${DARK.g},${DARK.b},1)`;
    ctx.lineWidth = 0.3;
    ctx.stroke();
  }
  ctx.globalAlpha = 1;
}

const TASK_POOL = [
  // Tier 1 — High Impact & Core AI Roles (weight 100)
  { text: 'voice receptionist', weight: 100 },
  { text: 'executive assistant', weight: 100 },
  { text: 'deep researcher', weight: 100 },
  { text: 'litigation agent', weight: 100 },
  { text: 'calendar manager', weight: 100 },
  // Tier 2 — Senior Operational & Strategic Roles (weight 30)
  { text: 'discovery agent', weight: 30 },
  { text: 'immigration manager', weight: 30 },
  { text: 'multilingual paralegal', weight: 30 },
  { text: 'medical analyst', weight: 30 },
  { text: 'contract manager', weight: 30 },
  { text: 'compliance monitor', weight: 30 },
  { text: 'settlement analyst', weight: 30 },
  // Tier 3 — Associate & Administrative Roles (weight 10)
  { text: 'regulatory monitor', weight: 10 },
  { text: 'intake coordinator', weight: 10 },
  { text: 'evidence curator', weight: 10 },
  { text: 'deadline manager', weight: 10 },
  { text: 'inbox manager', weight: 10 },
  { text: 'billing administrator', weight: 10 },
  { text: 'motion drafter', weight: 10 },
  // Tier 4 — Foundational Support Roles (weight 3)
  { text: 'witness prep clerk', weight: 3 },
  { text: 'expert sourcer', weight: 3 },
  { text: 'class action clerk', weight: 3 },
  { text: 'litigation hold admin', weight: 3 },
  { text: 'verdict researcher', weight: 3 },
  { text: 'filing assistant', weight: 3 },
  { text: 'demand letter clerk', weight: 3 },
];

/** Pick a weighted random keyword that isn't already visible (no duplicates). */
function getWeightedRandomKeyword(exclude: Set<string>): string {
  const available = TASK_POOL.filter(item => !exclude.has(item.text));
  // If pool is exhausted fall back to full pool (shouldn't happen with 36 items & 8 slots)
  const pool = available.length > 0 ? available : TASK_POOL;
  const totalWeight = pool.reduce((sum, item) => sum + item.weight, 0);
  let rand = Math.random() * totalWeight;
  for (let j = 0; j < pool.length; j++) {
    rand -= pool[j].weight;
    if (rand <= 0) return pool[j].text;
  }
  return pool[pool.length - 1]?.text || 'automation';
}

const ORBIT_GEOMETRY = [
  { angle: 0.3, ring: 4.6, size: 18 },
  { angle: 1.1, ring: 5.0, size: 17 },
  { angle: 2.0, ring: 4.4, size: 18 },
  { angle: 2.9, ring: 5.2, size: 17 },
  { angle: 3.8, ring: 4.8, size: 17 },
  { angle: 4.5, ring: 5.1, size: 17 },
  { angle: 5.3, ring: 4.5, size: 18 },
  { angle: 5.9, ring: 5.3, size: 17 }
];

const KEYWORD_DEFS = (() => {
  const used = new Set<string>();
  return ORBIT_GEOMETRY.map((geom, i) => {
    const text = getWeightedRandomKeyword(used);
    used.add(text);
    return { text, lastLap: 0, ...geom };
  });
})();

/** Build the set of currently visible keyword texts (for dedup). */
function getVisibleKeywords(): Set<string> {
  return new Set(KEYWORD_DEFS.map(kw => kw.text));
}

interface KeywordHitBox {
  x: number; y: number; hw: number; hh: number;
}

function drawKeywords(
  ctx: CanvasRenderingContext2D,
  t: number, W: number, H: number, cx: number, cy: number,
  frozenAngles: number[],
  hoveredIdx: number,
): KeywordHitBox[] {
  const FONT_FAMILY = "'Inter', -apple-system, BlinkMacSystemFont, sans-serif";
  const hitBoxes: KeywordHitBox[] = [];
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';

  KEYWORD_DEFS.forEach((kw, i) => {
    const r = Math.min(W, H) * (kw.ring / 10);
    const baseProgress = t * 0.003 * (i % 2 === 0 ? 1 : -1);

    // Staggered transitions: each keyword rotates in and out of focus via a sine wave.
    // The sine wave completes a full cycle every ~418 frames (~7 seconds).
    // Math.sin() bottoms out at -1 (lightest/faded text) when phase is 1.5π.
    // By offsetting phase by +0.5π, the floor() boundary happens exactly at the dip.
    const phase = t * 0.015 + i * 1.8;
    const currentPulseLap = Math.floor((phase + Math.PI / 2) / (Math.PI * 2));
    
    if (currentPulseLap > kw.lastLap) {
      kw.lastLap = currentPulseLap;
      const used = getVisibleKeywords();
      used.delete(kw.text);
      kw.text = getWeightedRandomKeyword(used);
    }

    // Use frozen angle when this keyword is hovered, otherwise advance normally
    const angle = i === hoveredIdx ? frozenAngles[i] : kw.angle + baseProgress;
    const ex = 1 + (kw.ring / 10) * 0.6;
    const ey = 0.65;
    const x = cx + Math.cos(angle) * r * ex;
    const y = cy + Math.sin(angle) * r * ey;

    // Base sinusoidal visibility pulse
    const raw = Math.sin(phase);
    const clamped = raw > 0.3 ? 1 : (raw + 0.7) / 1.0;

    // Hovered keyword gets full opacity; others use animated alpha
    const alpha = i === hoveredIdx ? 0.95 : 0.4 + 0.5 * clamped;

    const fontSize = i === hoveredIdx ? kw.size + 2 : kw.size;
    ctx.fillStyle = `rgba(${DARK.r},${DARK.g},${DARK.b},${alpha})`;
    ctx.font = `500 ${fontSize}px ${FONT_FAMILY}`;
    ctx.fillText(kw.text, x, y);
    // Store hit-box for mouse detection (half-width, half-height)
    const metrics = ctx.measureText(kw.text);
    hitBoxes.push({ x, y, hw: metrics.width / 2 + 8, hh: fontSize / 2 + 6 });
  });
  return hitBoxes;
}

const HeroCanvasBackground: React.FC = () => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const nodesRef = useRef<OrbitalNode[]>(initOrbitalNodes());
  const mouseRef = useRef({ x: -1000, y: -1000 });
  const timeRef = useRef(0);
  const rafRef = useRef<number>(0);
  // Keyword hover state
  const hoveredKeywordRef = useRef<number>(-1);
  const frozenAnglesRef = useRef<number[]>(KEYWORD_DEFS.map(() => 0));
  const keywordHitBoxesRef = useRef<KeywordHitBox[]>([]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    let W = 0, H = 0, cx = 0, cy = 0;

    const resize = () => {
      const dpr = window.devicePixelRatio || 1;
      const parent = canvas.parentElement;
      W = parent?.clientWidth || window.innerWidth;
      H = parent?.clientHeight || window.innerHeight;
      canvas.width = W * dpr;
      canvas.height = H * dpr;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      cx = W / 2;
      cy = H / 2;
    };

    resize();
    window.addEventListener('resize', resize);

    const handleMouse = (e: MouseEvent) => {
      const rect = canvas.getBoundingClientRect();
      const mx = e.clientX - rect.left;
      const my = e.clientY - rect.top;
      mouseRef.current.x = mx;
      mouseRef.current.y = my;

      // Hit-test keywords for hover-pause
      let found = -1;
      const boxes = keywordHitBoxesRef.current;
      for (let i = 0; i < boxes.length; i++) {
        const b = boxes[i];
        if (Math.abs(mx - b.x) < b.hw && Math.abs(my - b.y) < b.hh) {
          found = i;
          break;
        }
      }
      if (found !== -1 && hoveredKeywordRef.current === -1) {
        // Entering a keyword — freeze its current angle
        const kw = KEYWORD_DEFS[found];
        const t = timeRef.current;
        frozenAnglesRef.current[found] = kw.angle + t * 0.003 * (found % 2 === 0 ? 1 : -1);
      }
      hoveredKeywordRef.current = found;
    };
    document.addEventListener('mousemove', handleMouse);

    const nodes = nodesRef.current;

    const mouseInfluence = (pos: { x: number; y: number }) => {
      const dx = pos.x - mouseRef.current.x;
      const dy = pos.y - mouseRef.current.y;
      const dist = Math.sqrt(dx * dx + dy * dy);
      if (dist < 120) {
        const force = (120 - dist) / 120;
        pos.x += dx * force * 0.05;
        pos.y += dy * force * 0.05;
      }
      return pos;
    };

    const frame = () => {
      timeRef.current++;
      const t = timeRef.current;
      ctx.clearRect(0, 0, W, H);

      // Subtle warm background gradient (matches hero #FAFAF8)
      const grad = ctx.createRadialGradient(cx, cy, 0, cx, cy, Math.max(W, H) * 0.7);
      grad.addColorStop(0, '#F5F3EE');
      grad.addColorStop(0.5, '#FAFAF8');
      grad.addColorStop(1, '#F0EEE8');
      ctx.fillStyle = grad;
      ctx.fillRect(0, 0, W, H);

      drawGrid(ctx, t, W, H, cx, cy);
      drawZeroRing(ctx, t, W, H, cx, cy);

      const positions = nodes.map(n => {
        let pos = n.draw(ctx, t, W, H, cx, cy);
        pos = mouseInfluence(pos);
        return pos;
      });

      drawConnections(ctx, positions, nodes, W, H);
      // Update frozen angles for any newly-hovered keyword
      const hIdx = hoveredKeywordRef.current;
      keywordHitBoxesRef.current = drawKeywords(
        ctx, t, W, H, cx, cy,
        frozenAnglesRef.current, hIdx,
      );

      // Strong light center wash — painted LAST so it covers all animation
      // in the center, creating a clear reading zone for hero text
      const centerWash = ctx.createRadialGradient(cx, cy, 0, cx, cy, Math.min(W, H) * 0.45);
      centerWash.addColorStop(0, 'rgba(250, 250, 248, 1)');
      centerWash.addColorStop(0.25, 'rgba(250, 250, 248, 0.98)');
      centerWash.addColorStop(0.45, 'rgba(250, 250, 248, 0.92)');
      centerWash.addColorStop(0.65, 'rgba(250, 250, 248, 0.7)');
      centerWash.addColorStop(0.82, 'rgba(250, 250, 248, 0.35)');
      centerWash.addColorStop(1, 'rgba(250, 250, 248, 0)');
      ctx.fillStyle = centerWash;
      ctx.fillRect(0, 0, W, H);

      rafRef.current = requestAnimationFrame(frame);
    };

    rafRef.current = requestAnimationFrame(frame);

    return () => {
      cancelAnimationFrame(rafRef.current);
      window.removeEventListener('resize', resize);
      document.removeEventListener('mousemove', handleMouse);
    };
  }, []);

  return (
    <HeroCanvasWrapper>
      <canvas ref={canvasRef} />
      <div className="hero-vignette" />
      <div className="hero-grain" />
    </HeroCanvasWrapper>
  );
};

/* ── Value-prop band (2026-08-19 owner directive) ────────────────────────
 * Two headline claims with the innovation list behind a click — substance for
 * the buyer who wants it, no wall of text for the one who doesn't. Sits
 * between the why-us cards and the tools showcase so "thousands of built-in
 * tools and skills" leads straight into the section that proves it. */

const ValuePropSection = styled.section`
  width: 100%;
  padding: 4.5rem 2rem 3.5rem;
  display: flex;
  flex-direction: column;
  align-items: center;
  background: linear-gradient(135deg, rgba(200, 167, 94, 0.07) 0%, #ffffff 55%, rgba(200, 167, 94, 0.04) 100%);
  content-visibility: auto;
  contain-intrinsic-size: auto 480px;

  @media (max-width: 768px) {
    padding: 3rem 1.25rem 2.5rem;
  }
`;

const ValuePropInner = styled.div`
  max-width: 1000px;
  width: 100%;
  margin: 0 auto;
  text-align: center;
`;

const ValuePropTitle = styled.h2`
  font-family: 'Space Grotesk', sans-serif;
  font-size: clamp(1.8rem, 4.5vw, 2.4rem);
  font-weight: 600;
  letter-spacing: -0.02em;
  color: var(--brand-black);
  margin: 0 0 0.9rem;
  position: relative;
  padding-bottom: 1.1rem;

  &::after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 50%;
    transform: translateX(-50%);
    width: 100px;
    height: 4px;
    background: linear-gradient(to right, var(--brand-gold-alt), var(--brand-black));
    border-radius: 4px;
  }
`;

const InnovationToggle = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  background: none;
  border: none;
  margin-top: 1.1rem;
  padding: 0.25rem 0.5rem;
  min-height: 0;
  min-width: 0;
  font-family: 'Inter', sans-serif;
  font-size: 0.9rem;
  /* #A07D3A measured 4.3:1 on the canvas — a hand-darkened gold that still
     missed. --brand-gold-text is the one that clears every house ground. */
  color: var(--brand-gold-text);
  cursor: pointer;
  text-decoration: underline;
  text-underline-offset: 0.2em;
  text-decoration-color: rgba(200, 167, 94, 0.5);

  &:hover { color: #B8924C; text-decoration-color: #B8924C; }
  &:focus { outline: none; }
  &:focus-visible { outline: 2px solid var(--brand-gold-alt); outline-offset: 3px; border-radius: 0.2rem; }
`;

const InnovationGrid = styled.ul`
  list-style: none;
  margin: 1.25rem auto 0;
  padding: 0;
  max-width: 840px;
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 0.6rem;
  text-align: left;

  @media (max-width: 640px) {
    grid-template-columns: 1fr;
  }
`;

const landingShineSweep = keyframes`
  0%, 70% { transform: translateX(-140%) skewX(-18deg); }
  100% { transform: translateX(280%) skewX(-18deg); }
`;

/* The headline innovation (2026-08-21 owner directive): the patented memory
 * system gets the one shiny gold tile, full-width at the top of the grid. */
const FeaturedInnovation = styled.li`
  grid-column: 1 / -1;
  position: relative;
  overflow: hidden;
  font-family: 'Space Grotesk', sans-serif;
  font-size: 1rem;
  font-weight: 700;
  letter-spacing: -0.01em;
  line-height: 1.45;
  color: #2B2208;
  text-align: center;
  background: linear-gradient(135deg, #EAD394 0%, var(--brand-gold-alt) 48%, #B8924C 100%);
  border: 1px solid rgba(200, 167, 94, 0.6);
  border-radius: 16px;
  padding: 0.95rem 1.2rem;
  box-shadow: 0 10px 28px rgba(200, 167, 94, 0.35), inset 0 1px 0 rgba(255, 255, 255, 0.45);

  &::after {
    content: '';
    position: absolute;
    top: 0;
    bottom: 0;
    left: 0;
    width: 42%;
    background: linear-gradient(105deg, transparent 0%, rgba(255, 255, 255, 0.45) 50%, transparent 100%);
    animation: ${landingShineSweep} 4.6s ease-in-out infinite;
    pointer-events: none;
  }
  @media (prefers-reduced-motion: reduce) {
    &::after { animation: none; }
  }
`;

const InnovationChip = styled.li`
  font-family: 'Inter', sans-serif;
  font-size: 0.88rem;
  line-height: 1.5;
  color: #444;
  background: rgba(255, 255, 255, 0.95);
  border: 1px solid rgba(200, 167, 94, 0.35);
  border-radius: 14px;
  padding: 0.75rem 1rem;
  box-shadow: 0 6px 18px rgba(0, 0, 0, 0.04);
`;

/* The landing innovation list mirrors the pricing page's (each claim + a
 * plain-words gloss). Authored lowercase per this page's convention. */
const LANDING_INNOVATIONS = [
  'every task runs on the model chosen for that task, not one default',
  'retries, overruns and thinking time are always free — you pay for finished work',
  'correct it once and it holds — it consolidates what it learned every night',
  'it reads whole batches of files and comes back with one deliverable',
  'research comes back cited, as PDF and Word',
  'anything that leaves your firm waits for a person to authorize it',
  'permissions are checked for the person asking, on every single message',
  'improvements land in your plan automatically',
];

/* Hero conversion row: ONE primary action (signup, $200 loaded) beside the
 * existing demo-video play button. */
/* ── The 0 is a workday ─────────────────────────────────────────────────────
   The brand mark is a zero and the product is sold by the month, so the hero's
   zero becomes a 24-hour ring the visitor can operate. Every cost on it is the
   published rate, imported from the pricing SSOT (src/components/zero/).

   It sits BELOW the hero deliberately: the hero's logo is the LCP element with
   a matching preload and explicit dimensions for layout stability, and putting
   an interactive graphic above it would trade a measured first paint for a
   flourish. NOTE: no backticks anywhere in these comments -- this is inside a
   styled-components template literal, where a backtick ends the literal. */
const DaySection = styled.section`
  padding: 4.5rem 2rem 4rem;
  background: var(--brand-canvas);
  border-top: 1px solid var(--brand-line);

  @media (max-width: 768px) {
    padding: 3rem 1.25rem 2.75rem;
  }
`;

const DayInner = styled.div`
  max-width: 1040px;
  margin: 0 auto;
`;

const DayHead = styled.h2`
  font-family: 'Space Grotesk', sans-serif;
  font-size: clamp(1.35rem, 3vw, 1.9rem);
  line-height: 1.22;
  color: var(--brand-text);
  margin: 0 0 0.5rem;
  text-wrap: balance;
`;

const DayLead = styled.p`
  color: var(--brand-text-secondary);
  font-size: 0.97rem;
  line-height: 1.6;
  max-width: 46rem;
  margin: 0 0 1.6rem;
`;

const ChipRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin: 0 0 2rem;
`;

const Chip = styled.button<{ $on: boolean }>`
  appearance: none;
  border-radius: 999px;
  padding: 0.44rem 0.95rem;
  font-size: 0.86rem;
  cursor: pointer;
  transition: background 0.18s ease, border-color 0.18s ease, color 0.18s ease;
  border: 1px solid ${({ $on }) => ($on ? 'var(--brand-gold)' : 'var(--brand-line-strong)')};
  background: ${({ $on }) => ($on ? 'var(--brand-gold)' : 'transparent')};
  color: ${({ $on }) => ($on ? '#2B2208' : 'var(--brand-text-secondary)')};

  &:hover { border-color: var(--brand-gold); }
  &:focus-visible {
    outline: none;
    box-shadow: 0 0 0 3px rgba(198, 167, 94, 0.35);
  }
`;

const DayFoot = styled.p`
  margin: 1.9rem 0 0;
  font-size: 0.9rem;
  color: var(--brand-text-secondary);
  max-width: 46rem;
`;

const HeroCtaRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: center;
  gap: 1rem;
  margin-top: 0.4rem;
`;

/* Ghost secondary on the dark closing section. */
const DarkGhostButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.6rem;
  background: transparent;
  color: var(--brand-gold-alt);
  border: 1px solid rgba(200, 167, 94, 0.65);
  border-radius: 999px;
  padding: 1rem 2.2rem;
  font-family: 'Space Grotesk', 'Inter', sans-serif;
  font-size: 1.05rem;
  font-weight: 600;
  cursor: pointer;
  transition: transform 0.16s ease, background 0.16s ease, box-shadow 0.16s ease;

  &:hover {
    transform: translateY(-2px);
    background: rgba(200, 167, 94, 0.12);
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.25);
  }
  &:focus { outline: none; }
  &:focus-visible { outline: 2px solid var(--brand-gold-alt); outline-offset: 3px; }
`;

const LandingPage = (): JSX.Element => {
  // Add useAuth hook
  const { isAuthenticated, user, login, logout } = useAuth();
  const { canSeeWallet, canSeeActivity } = useUsageVisibility();
  const navigate = useNavigate();
  const loginSectionRef = useRef<HTMLDivElement>(null);
  const capabilitiesSectionRef = useRef<HTMLElement>(null);
  const featuresSectionRef = useRef<HTMLElement>(null);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [promptHtml, setPromptHtml] = useState('');
  const [prompt, setPrompt] = useState('');
  const [isSignUp, setIsSignUp] = useState(false);
  const [demoVideoOpen, setDemoVideoOpen] = useState(false);
  const [isBookingModalOpen, setIsBookingModalOpen] = useState(false);
  const [innovationsOpen, setInnovationsOpen] = useState(true);
  const [isMobileFullscreen, setIsMobileFullscreen] = useState(false);

  // Which day the 0 shows. An EXPLICIT pick only — never inferred from what
  // the visitor typed, never from a header, never classified. D8: nothing
  // client-side may decide product behaviour from keywords. This is a
  // presentation preference the visitor states and we remember.
  const [vertical, setVertical] = useState<ZeroVertical>(() => {
    try {
      const saved = localStorage.getItem(VERTICAL_KEY);
      if (saved === 'law' || saved === 'finance' || saved === 'other') return saved;
    } catch {}
    return 'law';
  });
  useEffect(() => {
    try { localStorage.setItem(VERTICAL_KEY, vertical); } catch {}
  }, [vertical]);
  const demoVideoRef = useRef<HTMLVideoElement>(null);
  const mobileVideoContainerRef = useRef<HTMLDivElement>(null);
  const [rememberMe, setRememberMe] = useState<boolean>(() => {
    try { return localStorage.getItem('remember_me') === '1'; } catch { return false; }
  });
  const [showGoogleRequired, setShowGoogleRequired] = useState(false);
  const [googleRequiredEmail, setGoogleRequiredEmail] = useState('');
  const landingChatOpenKey = buildPageStorageKey(GLOBAL_CHAT_OPEN_KEY, '/');
  const landingChatDockKey = buildPageStorageKey(GLOBAL_CHAT_DOCK_KEY, '/');
  const landingChatFullscreenKey = buildPageStorageKey(GLOBAL_CHAT_FULLSCREEN_KEY, '/');
const [isChatDockedRight, setIsChatDockedRight] = useState<boolean>(() => {
  try {
    return localStorage.getItem(landingChatDockKey) === '1';
  } catch {
    return false;
  }
});

const [isChatFullScreen, setIsChatFullScreen] = useState<boolean>(() => {
  try {
    return localStorage.getItem(landingChatFullscreenKey) === '1';
  } catch {
    return false;
  }
});

const [isChatOpen, setIsChatOpen] = useState<boolean>(() => {
  try {
    return localStorage.getItem(landingChatOpenKey) === '1';
  } catch {
    return false;
  }
});

const [showClearConfirmModal, setShowClearConfirmModal] = useState<boolean>(false);

  // P3 (chat-surface consistency, 2026-07-12): the three persist effects that
  // lived here wrote RAW '1'/'0' strings onto the live widget's storage keys
  // on every '/' mount — the widget writes JSON-encoded values, so these raw
  // writes silently clobbered/reset its open/dock/fullscreen state whenever
  // the user visited the landing page. The local mirror states below remain
  // (they feed landing-page layout), but ONLY the GlobalChatSystem writes chat
  // UI keys now (single-writer rule).

  const lastAppliedChatUiSyncRef = useRef<{ open: boolean; dock: boolean; fullscreen: boolean } | null>(null);
  const chatUiStateRef = useRef<{ open: boolean; dock: boolean; fullscreen: boolean }>({
    open: isChatOpen,
    dock: isChatDockedRight,
    fullscreen: isChatFullScreen,
  });

  useEffect(() => {
    chatUiStateRef.current = {
      open: isChatOpen,
      dock: isChatDockedRight,
      fullscreen: isChatFullScreen,
    };
  }, [isChatDockedRight, isChatFullScreen, isChatOpen]);

  // Cross-tab/page sync for open/dock/fullscreen state
  useEffect(() => {
    return subscribeToChatUiStateSync(
      { instanceId: 'global_chat', pagePath: '/' },
      (payload) => {
        const current = chatUiStateRef.current;
        const nextOpen = typeof payload.isChatOpen === 'boolean' ? payload.isChatOpen : current.open;
        const nextDock = typeof payload.isChatDockedRight === 'boolean' ? payload.isChatDockedRight : current.dock;
        const nextFullscreen = typeof payload.isChatFullScreen === 'boolean' ? payload.isChatFullScreen : current.fullscreen;
        lastAppliedChatUiSyncRef.current = {
          open: nextOpen,
          dock: nextDock,
          fullscreen: nextFullscreen,
        };
        if (typeof payload.isChatOpen === 'boolean') setIsChatOpen(payload.isChatOpen);
        if (typeof payload.isChatDockedRight === 'boolean') setIsChatDockedRight(payload.isChatDockedRight);
        if (typeof payload.isChatFullScreen === 'boolean') setIsChatFullScreen(payload.isChatFullScreen);
      }
    );
  }, []);

  useEffect(() => {
    const lastApplied = lastAppliedChatUiSyncRef.current;
    if (
      lastApplied
      && lastApplied.open === isChatOpen
      && lastApplied.dock === isChatDockedRight
      && lastApplied.fullscreen === isChatFullScreen
    ) {
      lastAppliedChatUiSyncRef.current = null;
      return;
    }
    publishChatUiStateSync({
      instanceId: 'global_chat',
      pagePath: '/',
      payload: {
        isChatOpen,
        isChatDockedRight,
        isChatFullScreen,
      },
      source: 'LandingPage',
    });
  }, [isChatDockedRight, isChatFullScreen, isChatOpen]);
  const [isResettingPassword, setIsResettingPassword] = useState(false);
  const [isClearingData, setIsClearingData] = useState(false);
  const googleButtonRef = useRef<HTMLDivElement | null>(null);

  // Handle password reset
  const handleForgotPassword = async () => {
    const validEmail = email.trim().toLowerCase();
    if (!validEmail) {
      alert('Please enter your email address first');
      return;
    }
    setIsResettingPassword(true);
    try {
      const { getAuth, sendPasswordResetEmail } = await import('firebase/auth');
      const auth = getAuth();
      await sendPasswordResetEmail(auth, validEmail);
      alert(`Password reset email sent to ${validEmail}. Please check your inbox.`);
    } catch (error: any) {
      console.error('Password reset error:', error);
      if (error?.code === 'auth/user-not-found') {
        alert('No account found with this email address.');
      } else if (error?.code === 'auth/invalid-email') {
        alert('Please enter a valid email address.');
      } else {
        alert('Failed to send password reset email. Please try again.');
      }
    } finally {
      setIsResettingPassword(false);
    }
  };

  // Handle clearing all browser data for sign-in troubleshooting
  const handleClearBrowserData = async () => {
    const confirmed = window.confirm(
      'This will clear all cached data and sign you out. This can help fix sign-in issues.\n\nContinue?'
    );
    if (!confirmed) return;
    
    setIsClearingData(true);
    try {
      console.log('🧹 Clearing all browser data for troubleshooting...');
      
      // 1. Sign out from Firebase
      try {
        const { getAuth } = await import('firebase/auth');
        const auth = getAuth();
        await auth.signOut();
      } catch {}
      
      // 2. Wipe all client data including IndexedDB
      try {
        const { wipeAllClientData } = await import('../utils/siteDataWipe');
        await wipeAllClientData();
      } catch {}
      
      // 3. Clear any remaining localStorage
      try { localStorage.clear(); } catch {}
      try { sessionStorage.clear(); } catch {}
      
      // 4. Clear specific Firebase IndexedDB databases
      const dbNames = [
        'firebaseLocalStorageDb',
        'firebase-heartbeat-database',
        'firebase-installations-database'
      ];
      for (const name of dbNames) {
        try {
          await new Promise<void>((resolve) => {
            const req = indexedDB.deleteDatabase(name);
            req.onsuccess = () => resolve();
            req.onerror = () => resolve();
            req.onblocked = () => resolve();
          });
        } catch {}
      }
      
      alert('Browser data cleared! The page will now reload.');
      
      // Force full page reload to clear all in-memory state
      window.location.replace(window.location.origin);
    } catch (error) {
      console.error('Error clearing browser data:', error);
      alert('Failed to clear data. Please try clearing your browser\'s site data manually.');
    } finally {
      setIsClearingData(false);
    }
  };

  // Prefill email from localStorage if the user opted into remember-me previously
  useEffect(() => {
    try {
      if (rememberMe && !email) {
        const savedEmail = localStorage.getItem('remember_email') || '';
        if (savedEmail) setEmail(savedEmail);
      }
    } catch {}
  }, [rememberMe]);
  const [activeTab, setActiveTab] = useState<'triggers' | 'models' | 'tools'>('triggers');
  const [isSectionExpanded, setIsSectionExpanded] = useState(true);
  const [autoRotateTabs, setAutoRotateTabs] = useState(true);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [workflowSessions, setWorkflowSessions] = useState<WorkflowSession[]>([]);
  const [activeWorkflowSessionId, setActiveWorkflowSessionId] = useState<string | null>(() => getStoredWorkflowSessionId());
  const [loadingWorkflowSessions, setLoadingWorkflowSessions] = useState(false);
  const [activeTokenInfoIndex, setActiveTokenInfoIndex] = useState<number | null>(null);
// PRE-70: Session-scoped chat storage — each thread gets its own localStorage key
const getChatStorageKeyForSession = (sessionId: string | null) =>
  sessionId ? `prest0_chat_session_${sessionId}` : GLOBAL_CHAT_HISTORY_KEY;
const CHAT_STORAGE_KEY = getChatStorageKeyForSession(activeWorkflowSessionId);
const CHAT_DRAFT_STORAGE_KEY = GLOBAL_CHAT_DRAFT_KEY;
const prevSessionIdRef = useRef<string | null>(activeWorkflowSessionId);
// PRE-70: Ref to active session for stable closure in event handlers
const activeSessionIdRef = useRef<string | null>(activeWorkflowSessionId);
// PRE-70: Ref to current messages so the switch effect always reads latest state (avoids stale closure)
const messagesRef = useRef<ChatMessage[]>([]);

// PRE-70: When session switches, save current messages to old key and restore from new key
useEffect(() => {
  if (prevSessionIdRef.current === activeWorkflowSessionId) return;
  // Save current messages to the PREVIOUS session's storage (use ref to avoid stale closure)
  const prevKey = getChatStorageKeyForSession(prevSessionIdRef.current);
  try {
    const payload = messagesRef.current.map(m => ({
      text: m.text, isUser: m.isUser,
      timestamp: m.timestamp instanceof Date ? m.timestamp.toISOString() : m.timestamp,
      html: m.html, attachmentsSerialized: m.attachmentsSerialized,
      sourceTag: m.sourceTag, tokenUsage: m.tokenUsage, tokenUsages: m.tokenUsages, tokenUsageSnapshot: m.tokenUsageSnapshot, tokenUsageSnapshots: m.tokenUsageSnapshots,
    }));
    localStorage.setItem(prevKey, JSON.stringify(payload));
  } catch { /* ignore */ }

  // Restore messages from the NEW session's storage
  const newKey = getChatStorageKeyForSession(activeWorkflowSessionId);
  try {
    const raw = localStorage.getItem(newKey);
    if (raw) {
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed)) {
        const restored = parsed.map((m: any) => ({
          text: String(m?.text ?? ''), isUser: !!m?.isUser,
          timestamp: m?.timestamp ? new Date(m.timestamp) : new Date(),
          html: m?.html ? String(m.html) : undefined,
          attachmentsSerialized: Array.isArray(m?.attachmentsSerialized) ? m.attachmentsSerialized : undefined,
          sourceTag: m?.sourceTag ? String(m.sourceTag) : undefined,
          tokenUsage: m?.tokenUsage ?? undefined,
          tokenUsages: m?.tokenUsages ?? undefined,
          tokenUsageSnapshot: m?.tokenUsageSnapshot ?? undefined,
          tokenUsageSnapshots: m?.tokenUsageSnapshots ?? undefined,
        } as ChatMessage)).filter((m: ChatMessage) => m.text || m.attachmentsSerialized?.length);
        setMessages(restored);
      } else {
        setMessages([]);
      }
    } else {
      setMessages([]);
    }
  } catch {
    setMessages([]);
  }
  prevSessionIdRef.current = activeWorkflowSessionId;
  activeSessionIdRef.current = activeWorkflowSessionId;
  // eslint-disable-next-line react-hooks/exhaustive-deps
}, [activeWorkflowSessionId]);

// PRE-70: Keep messagesRef current so switch effect always reads latest messages (avoids stale closure)
useEffect(() => { messagesRef.current = messages; }, [messages]);

const chatRestoredRef = useRef(false);
const promptRestoredRef = useRef(false);
const draftPersistTimeoutRef = useRef<number | null>(null);
// Payload of a scheduled-but-unfired debounced draft save — flushed
// synchronously on pagehide so a reload inside the 500 ms debounce window
// (most visibly the update banner's "refresh now" click) cannot lose the
// just-typed tail of the hero composer. Mirrors useGlobalChat.ts.
const draftPendingFlushRef = useRef<{ html: string; text: string } | null>(null);
const draftLastSavedRef = useRef<string | null>(null);
const promptFocusedRef = useRef(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false); // Add this line for mobile menu state
  const [expandedCards, setExpandedCards] = useState<ExpandedCardState>({});
  const [expandedFeatures, setExpandedFeatures] = useState<ExpandedCardState>({});
  const [expandedBenefits, setExpandedBenefits] = useState<ExpandedCardState>({});
  const [openPricingFaqs, setOpenPricingFaqs] = useState<Record<string, boolean>>({});
  const [openPricing, setOpenPricing] = useState<boolean>(false);
  const [openHighlight, setOpenHighlight] = useState<Record<string, boolean>>({});
  const [isPricingLockedOpen, setIsPricingLockedOpen] = useState<boolean>(false);
  const hasAccount = isAuthenticated && !!(user && user.accountId);
  const [isNotificationsOpen, setIsNotificationsOpen] = useState(false);
  const notificationWrapperRef = useRef<HTMLDivElement>(null);
  type HeaderNotification = { id: string; title: string; message: string; timestamp: string };
  const [notifications, setNotifications] = useState<HeaderNotification[]>([]);
  const [readIds, setReadIds] = useState<string[]>([]);
  const [unreadCount, setUnreadCount] = useState<number>(0);
  const pricingSectionRef = useRef<HTMLElement>(null);
  const pricingContainerRef = useRef<HTMLDivElement>(null);
  const location = useLocation();
  
  // Scroll to built-in tools (Marketplace) when arriving with #features
  useEffect(() => {
    if (location.hash === '#features') {
      // Retry a few times in case Marketplace is still mounting/expanding
      const attemptScroll = (attempts: number) => {
        if (featuresSectionRef.current) {
          featuresSectionRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
        } else if (attempts > 0) {
          setTimeout(() => attemptScroll(attempts - 1), 100);
        }
      };
      // Small delay to allow initial render
      setTimeout(() => attemptScroll(10), 50);
    }
  }, [location]);
  
  // Manual scrolling state for features section
  const [isManualScrolling, setIsManualScrolling] = useState(false);
  const [manualScrollPosition, setManualScrollPosition] = useState(0);
  const [pauseTimeout, setPauseTimeout] = useState<NodeJS.Timeout | null>(null);
  const [isTransitioningToAuto, setIsTransitioningToAuto] = useState(false);
  const featuresScrollWrapperRef = useRef<HTMLDivElement>(null);
  const tokenInfoButtonRefs = useRef<{ [key: number]: HTMLButtonElement | null }>({});
  const seenPlatformMessageIdsRef = useRef<Set<string>>(new Set());
  const platformInitRef = useRef<boolean>(false);
  const [attachments, setAttachments] = useState<File[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isAttachmentPanelVisible, setIsAttachmentPanelVisible] = useState(false);
  const [userCollapsedAttachments, setUserCollapsedAttachments] = useState(false);
  const [lastAttachmentVisibleOnHide, setLastAttachmentVisibleOnHide] = useState(false);
  const [isMobileViewport, setIsMobileViewport] = useState<boolean>(() => {
    if (typeof window === 'undefined') return false;
    return window.matchMedia('(max-width: 820px)').matches;
  });
  const [isDragging, setIsDragging] = useState(false);

  const promptBarRef = useRef<HTMLDivElement>(null);
  const floatingPromptBarRef = useRef<HTMLDivElement>(null);
  const chatContainerRef = useRef<HTMLDivElement>(null);
  const promptInputRef = useRef<HTMLDivElement>(null);
  const isFirstLoadRef = useRef<boolean>(true);
  const [richHtml, setRichHtml] = useState<string>('');

  // No dynamic height changes - textarea has fixed height via CSS and scrolls when content exceeds it
  // This eliminates the wiggle/jump completely

// Attachment serialization helpers for persistence
const serializeAttachments = async (files: File[]): Promise<SerializedAttachment[]> => {
  const tasks = files.map(
    file =>
      new Promise<SerializedAttachment>((resolve) => {
        const reader = new FileReader();
        reader.onload = () => {
          resolve({
            name: file.name,
            type: file.type,
            size: file.size,
            lastModified: file.lastModified,
            dataUrl: typeof reader.result === 'string' ? reader.result : undefined,
          });
        };
        reader.onerror = () => {
          resolve({
            name: file.name,
            type: file.type,
            size: file.size,
            lastModified: file.lastModified,
          });
        };
        reader.readAsDataURL(file);
      })
  );

  return Promise.all(tasks);
};

const deserializeAttachments = async (serialized: SerializedAttachment[]): Promise<File[]> => {
  const tasks = serialized.map(async (att) => {
    if (att.dataUrl) {
      try {
        const res = await fetch(att.dataUrl);
        const blob = await res.blob();
        return new File([blob], att.name || 'attachment', { type: att.type || blob.type, lastModified: att.lastModified || Date.now() });
      } catch {
        // Fall through to placeholder file
      }
    }
    return new File([''], att.name || 'attachment', { type: att.type || 'application/octet-stream', lastModified: att.lastModified || Date.now() });
  });

  return Promise.all(tasks);
};

  const constructionWaitlist = useConstructionWaitlist();

  const loadWorkflowSessions = useCallback(async () => {
    if (!isAuthenticated) return;
    try {
      setLoadingWorkflowSessions(true);
      const data = await listWorkflowSessions(['active', 'idle'], 20);
      const sessions = data.sessions || [];
      setWorkflowSessions(sessions);
      if (!activeWorkflowSessionId && sessions.length) {
        const firstId = sessions[0].sessionId;
        setActiveWorkflowSessionId(firstId);
        setStoredWorkflowSessionId(firstId);
      } else if (!activeWorkflowSessionId && sessions.length === 0) {
        // PRE-70 fix: bootstrap a default session if the user has none, so
        // messages always land in a session-scoped key and isolation works.
        try {
          const session = await createWorkflowSession({ title: 'Thread 1', workflowType: 'chat' });
          setWorkflowSessions([session]);
          setActiveWorkflowSessionId(session.sessionId);
          setStoredWorkflowSessionId(session.sessionId);
        } catch (createErr) {
          console.warn('[PRE-70] Failed to bootstrap default session:', createErr);
        }
      }
    } catch (err) {
      console.warn('[PRE-70] Failed to load workflow sessions:', err);
      // PRE-70 fix: even if list fails, ensure we have a local session ID so
      // isolation works — generate one locally without waiting for backend.
      if (!activeWorkflowSessionId) {
        const localId = typeof crypto !== 'undefined' && crypto.randomUUID
          ? crypto.randomUUID()
          : `local_${Date.now()}_${Math.random().toString(36).slice(2)}`;
        const localSession: WorkflowSession = {
          sessionId: localId,
          uid: '',
          title: 'Thread 1',
          status: 'active' as const,
          workflowType: 'chat',
          messageCount: 0,
        };
        setWorkflowSessions([localSession]);
        setActiveWorkflowSessionId(localId);
        setStoredWorkflowSessionId(localId);
      }
    } finally {
      setLoadingWorkflowSessions(false);
    }
  }, [isAuthenticated, activeWorkflowSessionId]);

  useEffect(() => {
    if (isAuthenticated) {
      loadWorkflowSessions().catch(() => void 0);
    }
  }, [isAuthenticated, loadWorkflowSessions]);

  // Refs and state to stabilize functionality tab height across switches
  const triggersPanelRef = useRef<HTMLDivElement>(null);
  const modelsPanelRef = useRef<HTMLDivElement>(null);
  const toolsPanelRef = useRef<HTMLDivElement>(null);
  const [capabilitiesPanelsHeight, setCapabilitiesPanelsHeight] = useState<number>(0);

  // Restore chat history locally (applies whether signed in or not)
  useEffect(() => {
    if (chatRestoredRef.current) return;
    let cancelled = false;

    (async () => {
      try {
        const raw = localStorage.getItem(CHAT_STORAGE_KEY);
        if (raw) {
          const parsed = JSON.parse(raw);
          if (Array.isArray(parsed)) {
            const restored = await Promise.all(
              parsed.map(async (m: any) => {
                const attachmentsSerialized: SerializedAttachment[] = Array.isArray(m?.attachmentsSerialized)
                  ? m.attachmentsSerialized.map((a: any) => ({
                      name: String(a?.name ?? ''),
                      type: String(a?.type ?? ''),
                      size: Number(a?.size ?? 0),
                      lastModified: a?.lastModified ? Number(a.lastModified) : undefined,
                      dataUrl: typeof a?.dataUrl === 'string' ? a.dataUrl : undefined,
                    }))
                  : [];

                const attachments = attachmentsSerialized.length ? await deserializeAttachments(attachmentsSerialized) : undefined;

                return {
                  text: String(m?.text ?? ''),
                  isUser: !!m?.isUser,
                  timestamp: m?.timestamp ? new Date(m.timestamp) : new Date(),
                  html: m?.html ? String(m.html) : undefined,
                  attachments,
                  attachmentsSerialized: attachmentsSerialized.length ? attachmentsSerialized : undefined,
                  sourceTag: m?.sourceTag ? String(m.sourceTag) : undefined,
                  tokenUsage: m?.tokenUsage ?? undefined,
                  tokenUsages: m?.tokenUsages ?? undefined,
                  tokenUsageSnapshot: m?.tokenUsageSnapshot ?? undefined,
                  tokenUsageSnapshots: m?.tokenUsageSnapshots ?? undefined,
                } as ChatMessage;
              })
            );

            const filtered = restored
              .filter((m: ChatMessage) => m.text || (m.attachmentsSerialized && m.attachmentsSerialized.length) || (m.attachments && m.attachments.length))
              .sort((a: ChatMessage, b: ChatMessage) => {
                const at = a.timestamp instanceof Date ? a.timestamp.getTime() : new Date(a.timestamp).getTime();
                const bt = b.timestamp instanceof Date ? b.timestamp.getTime() : new Date(b.timestamp).getTime();
                return at - bt;
              });

            if (!cancelled) setMessages(filtered);
          }
        }
      } catch {
        // ignore
      } finally {
        if (!cancelled) chatRestoredRef.current = true;
      }
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  // Persist chat history locally (applies whether signed in or not). Wait until restore runs to avoid overwriting.
  useEffect(() => {
    if (!chatRestoredRef.current) return;
    let cancelled = false;

    (async () => {
      try {
        const payload = await Promise.all(
          messages.map(async (m) => {
            let attachmentsSerialized = m.attachmentsSerialized;
            if (!attachmentsSerialized && m.attachments?.length) {
              try {
                attachmentsSerialized = await serializeAttachments(m.attachments);
              } catch {
                attachmentsSerialized = undefined;
              }
            }

            return {
              text: m.text,
              isUser: m.isUser,
              timestamp: m.timestamp instanceof Date ? m.timestamp.toISOString() : m.timestamp,
              html: m.html,
              attachmentsSerialized,
              sourceTag: m.sourceTag,
              tokenUsage: m.tokenUsage,
              tokenUsages: m.tokenUsages,
              tokenUsageSnapshot: m.tokenUsageSnapshot,
              tokenUsageSnapshots: m.tokenUsageSnapshots,
            };
          })
        );

        if (!cancelled) {
          localStorage.setItem(CHAT_STORAGE_KEY, JSON.stringify(payload));
        }
      } catch {
        // ignore
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [messages]);

  // Cross-tab/page sync for global chat history
  // This ensures the landing page stays in sync with other pages (e.g., law firm page)
  useEffect(() => {
    // Ref to track last known message count to avoid infinite update loops
    const lastMessageCountRef = { current: messages.length };
    
    // Parse stored history and update state if different
    const syncFromStorage = async () => {
      try {
        const raw = localStorage.getItem(CHAT_STORAGE_KEY);
        if (!raw) return;
        
        const parsed = JSON.parse(raw);
        if (!Array.isArray(parsed)) return;
        
        // Only update if the stored history is different from current
        if (parsed.length !== lastMessageCountRef.current) {
          const restored = await Promise.all(
            parsed.map(async (m: Record<string, unknown>) => {
              const attachmentsSerialized: SerializedAttachment[] = Array.isArray((m as any)?.attachmentsSerialized)
                ? (m as any).attachmentsSerialized.map((a: any) => ({
                    name: String(a?.name ?? ''),
                    type: String(a?.type ?? ''),
                    size: Number(a?.size ?? 0),
                    lastModified: a?.lastModified ? Number(a.lastModified) : undefined,
                    dataUrl: typeof a?.dataUrl === 'string' ? a.dataUrl : undefined,
                  }))
                : [];

              const attachments = attachmentsSerialized.length ? await deserializeAttachments(attachmentsSerialized) : undefined;

              return {
                text: String(m?.text ?? ''),
                isUser: !!m?.isUser,
                timestamp: m?.timestamp ? new Date(m.timestamp as string) : new Date(),
                html: m?.html ? String(m.html) : undefined,
                attachments,
                attachmentsSerialized: attachmentsSerialized.length ? attachmentsSerialized : undefined,
              } as ChatMessage;
            })
          );

          const filtered = restored
            .filter((m: ChatMessage) => m.text)
            .sort((a: ChatMessage, b: ChatMessage) => {
              const at = a.timestamp instanceof Date ? a.timestamp.getTime() : new Date(a.timestamp).getTime();
              const bt = b.timestamp instanceof Date ? b.timestamp.getTime() : new Date(b.timestamp).getTime();
              return at - bt;
            });
          
          lastMessageCountRef.current = filtered.length;
          setMessages(filtered);
        }
      } catch {
        // Silently ignore parse errors
      }
    };
    
    // Handler for storage events from other tabs/windows
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === CHAT_STORAGE_KEY) {
        syncFromStorage();
      }
    };
    
    // Poll for changes in the same tab (storage events don't fire for same-tab changes)
    // PRE-70: skip cross-tab polling when a session is active — each session has its own key
    // and polling here would cause Thread 1 messages to bleed into Thread 2 during rapid switching.
    const pollInterval = !activeWorkflowSessionId ? setInterval(() => {
      try {
        const raw = localStorage.getItem(CHAT_STORAGE_KEY);
        if (!raw) return;
        const parsed = JSON.parse(raw);
        if (Array.isArray(parsed) && parsed.length !== lastMessageCountRef.current) {
          syncFromStorage();
        }
      } catch {
        // Silently ignore
      }
    }, 500) : null; // Poll every 500ms for responsive sync (no-session only)
    
    window.addEventListener('storage', handleStorageChange);
    
    return () => {
      window.removeEventListener('storage', handleStorageChange);
      if (pollInterval) clearInterval(pollInterval);
    };
  }, [messages.length, activeWorkflowSessionId]);

  // Restore prompt draft on mount (works signed in or out, desktop or mobile)
  useEffect(() => {
    if (promptRestoredRef.current) return;
    // BUG-DR-COMPOSER-REPOP-LONG-TURN — if a send is in flight or a turn
    // is processing (or we are within the post-send/post-flight cooldown
    // window), skip restore. The localStorage CHAT_DRAFT_STORAGE_KEY may
    // contain stale just-sent text that an adjacent component re-wrote
    // before useGlobalChat could synchronously remove it. We intentionally
    // do NOT flip promptRestoredRef so a legitimate later mount (after
    // the suppression window closes) can still restore a real draft.
    if (shouldSuppressDraftRehydrate()) return;
    try {
      const raw = localStorage.getItem(CHAT_DRAFT_STORAGE_KEY);
      if (raw) {
        const parsed = JSON.parse(raw) as { html?: string; text?: string };
        const safeHtml = parsed?.html ? sanitizeHtmlInput(parsed.html) : '';
        const plain = parsed?.text ?? (safeHtml ? htmlToPlainText(safeHtml) : '');
        // FH-QA composer-refill race on LONG NON-attachment prompts
        // (2026-05-21 16:25 UTC): content-domain sentinel check on
        // hero composer hydrate-on-mount. Refuse to restore a draft
        // whose body matches the just-sent sentinel — and HEAL the key
        // (2026-08-12 sticky-composer round) so the resurrection can't
        // outlive the sentinel window in storage.
        if (draftIsSentTextMatch(plain, safeHtml)) {
          try { localStorage.removeItem(CHAT_DRAFT_STORAGE_KEY); } catch {}
          promptRestoredRef.current = true;
          return;
        }
        if (safeHtml || plain) {
          setPromptHtml(safeHtml);
          setPrompt(plain);
          // Defer DOM update to next frame so ref is ready
          requestAnimationFrame(() => {
            if (promptInputRef.current) {
              promptInputRef.current.innerHTML = safeHtml || plain || '';
              // Place caret at end
              try {
                const range = document.createRange();
                const sel = window.getSelection();
                range.selectNodeContents(promptInputRef.current);
                range.collapse(false);
                sel?.removeAllRanges();
                sel?.addRange(range);
                promptInputRef.current.focus();
              } catch {}
            }
          });
        }
      }
    } catch {}
    promptRestoredRef.current = true;
  }, []);

  // Cross-tab/page sync for prompt draft
  useEffect(() => {
    const syncDraftFromStorage = () => {
      try {
        // Don't sync if user is actively typing (input is focused)
        if (promptFocusedRef.current) return;

        // BUG-DR-COMPOSER-REPOP-LONG-TURN — suppression gate. Covers:
        //   (a) the legacy 1500 ms post-send window, AS WELL AS
        //   (b) the entire duration of any in-flight turn
        //       (set by useGlobalChat when state.isProcessing flips), AS WELL AS
        //   (c) a 1500 ms post-flight cooldown after the last turn ended.
        // Without this gate, a Deep Research turn (5-8 min) would expire
        // the post-send window in useGlobalChat, and the LandingPage
        // debounced persist effect would re-write the just-sent text to
        // localStorage; the next 500 ms poll here would repopulate the
        // composer with the user's just-sent prompt.
        if (shouldSuppressDraftRehydrate()) return;

        const raw = localStorage.getItem(CHAT_DRAFT_STORAGE_KEY);
        // Skip if no stored draft or if it matches what we last saved
        if (!raw || raw === draftLastSavedRef.current) return;
        
        const parsed = JSON.parse(raw) as { html?: string; text?: string };
        const safeHtml = parsed?.html ? sanitizeHtmlInput(parsed.html) : '';
        const plain = parsed?.text ?? (safeHtml ? htmlToPlainText(safeHtml) : '');

        // FH-QA composer-refill race on LONG NON-attachment prompts
        // (2026-05-21 16:25 UTC): content-domain sentinel check. If
        // the stored draft body matches the just-sent sentinel,
        // refuse to dispatch it into the hero composer's state — and
        // HEAL the key (2026-08-12 sticky-composer round).
        if (draftIsSentTextMatch(plain, safeHtml)) {
          try { localStorage.removeItem(CHAT_DRAFT_STORAGE_KEY); } catch {}
          draftLastSavedRef.current = null;
          return;
        }

        // Update our tracking ref
        draftLastSavedRef.current = raw;
        
        setPromptHtml(safeHtml);
        setPrompt(plain);
        requestAnimationFrame(() => {
          if (promptInputRef.current && !promptFocusedRef.current) {
            promptInputRef.current.innerHTML = safeHtml || plain || '';
            try {
              const range = document.createRange();
              const sel = window.getSelection();
              range.selectNodeContents(promptInputRef.current);
              range.collapse(false);
              sel?.removeAllRanges();
              sel?.addRange(range);
            } catch {}
          }
        });
      } catch {
        // ignore
      }
    };

    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === CHAT_DRAFT_STORAGE_KEY) {
        syncDraftFromStorage();
      }
    };

    const pollInterval = setInterval(() => {
      syncDraftFromStorage();
    }, 500);

    window.addEventListener('storage', handleStorageChange);
    return () => {
      window.removeEventListener('storage', handleStorageChange);
      clearInterval(pollInterval);
    };
  }, []);

  // Persist prompt draft locally with debounce to reduce write frequency
  useEffect(() => {
    // Wait until restore completes to avoid overwriting saved draft with empty string
    if (!promptRestoredRef.current) return;
    // Clear any pending timeout
    if (draftPersistTimeoutRef.current) {
      window.clearTimeout(draftPersistTimeoutRef.current);
    }
    // Stash for the pagehide flush: if the page unloads before the debounce
    // fires (update-banner refresh, F5, tab close), the flush effect below
    // persists exactly this payload through the same fire-time guards.
    draftPendingFlushRef.current = { html: promptHtml || '', text: prompt || '' };
    // Debounce: wait 500ms after last change before writing
    draftPersistTimeoutRef.current = window.setTimeout(() => {
      draftPendingFlushRef.current = null;
      try {
        // FH-QA composer-refill race (2026-05-21 13:50 UTC): bail if
        // suppression is active so a closure scheduled BEFORE send
        // (carrying pre-send promptHtml/prompt) can't rewrite the
        // CHAT_DRAFT key while another composer's handleSend is mid-
        // await on serializeAttachments. The shared singleton is
        // stamped synchronously by GlobalChatSystem's send wrapper
        // before handleSend runs, so this gate sees the window open.
        if (shouldSuppressDraftRehydrate()) return;
        const payload = { html: promptHtml || '', text: prompt || '' };
        // FH-QA composer-refill race on LONG NON-attachment prompts
        // (2026-05-21 16:25 UTC): content-domain sentinel check at
        // fire time so a closure that escaped the time-domain gate
        // (e.g. a backgrounded tab unparking the 500 ms timer hours
        // later) still cannot resurrect the just-sent body.
        if (draftIsSentTextMatch(payload.text, payload.html)) return;
        const serialized = JSON.stringify(payload);
        // Track what we saved so we don't re-sync our own saves
        draftLastSavedRef.current = serialized;
        localStorage.setItem(CHAT_DRAFT_STORAGE_KEY, serialized);
      } catch {}
    }, 500);
    return () => {
      if (draftPersistTimeoutRef.current) {
        window.clearTimeout(draftPersistTimeoutRef.current);
        draftPersistTimeoutRef.current = null;
      }
    };
  }, [promptHtml, prompt]);

  // Draft flush on page exit (deploy-freshness doctrine, 2026-08-20): fire
  // the pending debounced save synchronously on pagehide, through the SAME
  // guards as the debounced closure (suppression window + sent-text
  // sentinel), so a reload inside the 500 ms window keeps the half-typed
  // hero draft and can never resurrect just-sent text. Mirrors the
  // useGlobalChat.ts flush; see draftSuppressionState.ts for the guards.
  useEffect(() => {
    const flushPendingDraft = () => {
      const pending = draftPendingFlushRef.current;
      if (!pending) return;
      draftPendingFlushRef.current = null;
      try {
        if (shouldSuppressDraftRehydrate()) return;
        if (draftIsSentTextMatch(pending.text, pending.html)) return;
        const serialized = JSON.stringify(pending);
        draftLastSavedRef.current = serialized;
        localStorage.setItem(CHAT_DRAFT_STORAGE_KEY, serialized);
      } catch {}
    };
    window.addEventListener('pagehide', flushPendingDraft);
    return () => window.removeEventListener('pagehide', flushPendingDraft);
  }, []);

  // Sync chat state across tabs of the same browser (helps avoid accidental "loss" when one tab clears/updates history)
  useEffect(() => {
    const handler = (event: StorageEvent) => {
      if (event.key !== CHAT_STORAGE_KEY) return;
      if (!event.newValue) return;
      try {
        const parsed = JSON.parse(event.newValue);
        if (!Array.isArray(parsed)) return;
        const restored = parsed
          .map((m: any) => ({
            text: String(m?.text ?? ''),
            isUser: !!m?.isUser,
            timestamp: m?.timestamp ? new Date(m.timestamp) : new Date(),
            html: m?.html ? String(m.html) : undefined
          }))
          .filter((m: any) => m.text)
          .sort((a: ChatMessage, b: ChatMessage) => {
            const at = a.timestamp instanceof Date ? a.timestamp.getTime() : new Date(a.timestamp).getTime();
            const bt = b.timestamp instanceof Date ? b.timestamp.getTime() : new Date(b.timestamp).getTime();
            return at - bt;
          });
        setMessages(restored);
      } catch {}
    };
    window.addEventListener('storage', handler);
    return () => window.removeEventListener('storage', handler);
  }, []);

  // Keep the prompt input focused when the chat drawer is opened so users can type immediately
  useEffect(() => {
    if (attachments.length === 0 && isAttachmentPanelVisible) {
      setIsAttachmentPanelVisible(false);
    }
  }, [attachments.length, isAttachmentPanelVisible]);

  // Track user collapse of attachment tray and auto-show only if not manually collapsed
  const prevAttachmentCountRef = useRef<number>(0);
  useEffect(() => {
    const prev = prevAttachmentCountRef.current;
    // If new attachments were added, reset manual collapse so tray can show again
    if (attachments.length > prev) {
      setUserCollapsedAttachments(false);
    }
    prevAttachmentCountRef.current = attachments.length;
  }, [attachments.length]);

  // When docked + hidden and there are pending attachments, auto-show the panel
  // unless the user manually collapsed it before hiding.
  useEffect(() => {
    if (isChatDockedRight && !isChatOpen) {
      const shouldShow = attachments.length > 0 && !userCollapsedAttachments && lastAttachmentVisibleOnHide;
      if (shouldShow) {
        setIsAttachmentPanelVisible(true);
      } else if (isAttachmentPanelVisible) {
        setIsAttachmentPanelVisible(false);
      }
    }
  }, [attachments.length, isChatDockedRight, isChatOpen, userCollapsedAttachments, isAttachmentPanelVisible, lastAttachmentVisibleOnHide]);

  // Track viewport size
  useEffect(() => {
    const mq = window.matchMedia('(max-width: 820px)');
    const handler = (e: MediaQueryListEvent | MediaQueryList) => {
      const mobile = 'matches' in e ? e.matches : (e as MediaQueryList).matches;
      setIsMobileViewport(mobile);
    };
    handler(mq);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);

  // Auto-play + resume position + mobile fullscreen when demo modal opens
  useEffect(() => {
    if (!demoVideoOpen) {
      return;
    }

    const timer = setTimeout(() => {
      const video = demoVideoRef.current;
      if (!video) return;

      const restoreAndPlay = () => {
        document.querySelectorAll('video').forEach(v => {
          if (v !== video && !v.paused) v.pause();
        });

        try {
          const saved = sessionStorage.getItem('demo_video_current_time');
          if (saved) {
            const t = parseFloat(saved);
            if (t > 0 && isFinite(t) && t < (video.duration || Infinity)) {
              video.currentTime = t;
            }
          }
        } catch {}

        video.play().catch(() => {});
      };

      if (video.readyState < 1) {
        video.load();
      }

      if (video.readyState >= 3) {
        restoreAndPlay();
      } else {
        video.addEventListener('canplay', restoreAndPlay, { once: true });
        setTimeout(() => {
          if (video.paused) restoreAndPlay();
        }, 1000);
      }

      if (isMobileViewport) {
        (async () => {
          try {
            const fsTarget = mobileVideoContainerRef.current || video;
            const reqFS = (fsTarget as any).requestFullscreen || (fsTarget as any).webkitRequestFullscreen || (video as any).webkitEnterFullScreen || (fsTarget as any).mozRequestFullScreen;
            if (reqFS) {
              await reqFS.call(fsTarget);
              setIsMobileFullscreen(true);
              try {
                const orientation = (window.screen as any).orientation;
                if (orientation?.lock) await orientation.lock('landscape').catch(() => {});
              } catch {}
            }
          } catch {
            setIsMobileFullscreen(true);
          }
        })();
      }
    }, 100);

    return () => clearTimeout(timer);
  }, [demoVideoOpen, isMobileViewport]);

  // Listen for fullscreen exit / close demo modal on Escape key
  useEffect(() => {
    if (!demoVideoOpen) return;

    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        if (demoVideoRef.current && demoVideoRef.current.currentTime > 0) {
          try { sessionStorage.setItem('demo_video_current_time', String(demoVideoRef.current.currentTime)); } catch {}
        }
        setDemoVideoOpen(false);
        if (demoVideoRef.current) demoVideoRef.current.pause();
      }
    };

    const onFullscreenChange = () => {
      const doc: any = document;
      if (!document.fullscreenElement && !doc.webkitFullscreenElement) {
        if (isMobileFullscreen) {
          setIsMobileFullscreen(false);
          try {
            const orientation = (window.screen as any).orientation;
            if (orientation?.unlock) orientation.unlock();
          } catch {}
        }
        if (demoVideoOpen && isMobileViewport) {
          if (demoVideoRef.current && demoVideoRef.current.currentTime > 0) {
            try { sessionStorage.setItem('demo_video_current_time', String(demoVideoRef.current.currentTime)); } catch {}
          }
          setDemoVideoOpen(false);
          if (demoVideoRef.current) demoVideoRef.current.pause();
        }
      }
    };

    window.addEventListener('keydown', onKey);
    document.addEventListener('fullscreenchange', onFullscreenChange);
    document.addEventListener('webkitfullscreenchange', onFullscreenChange as EventListener);
    return () => {
      window.removeEventListener('keydown', onKey);
      document.removeEventListener('fullscreenchange', onFullscreenChange);
      document.removeEventListener('webkitfullscreenchange', onFullscreenChange as EventListener);
    };
  }, [isMobileFullscreen, demoVideoOpen, isMobileViewport]);

  useEffect(() => {
    if (isChatOpen && promptInputRef.current) {
      try {
        // Focus immediately and again on next tick to ensure caret lands
        promptInputRef.current.focus();
        requestAnimationFrame(() => {
          promptInputRef.current?.focus();
        });
      } catch {
        /* ignore focus errors */
      }
    }
  }, [isChatOpen]);

  // Measure panels and stabilize wrapper height
  useLayoutEffect(() => {
    if (!isSectionExpanded) return;
    const triggersEl = triggersPanelRef.current;
    const modelsEl = modelsPanelRef.current;
    const toolsEl = toolsPanelRef.current;
    if (!triggersEl || !modelsEl || !toolsEl) return;

    const computeMaxHeight = () => {
      const heights = [triggersEl.scrollHeight, modelsEl.scrollHeight, toolsEl.scrollHeight];
      const max = Math.max(...heights);
      setCapabilitiesPanelsHeight(max);
    };

    // Initial measure
    computeMaxHeight();

    // Observe each panel for size changes
    const resizeObserver = new (window as any).ResizeObserver(() => {
      computeMaxHeight();
    });
    resizeObserver.observe(triggersEl);
    resizeObserver.observe(modelsEl);
    resizeObserver.observe(toolsEl);

    // Update on window resize as well
    const handleWindowResize = () => computeMaxHeight();
    window.addEventListener('resize', handleWindowResize);

    return () => {
      try {
        resizeObserver.disconnect();
      } catch {}
      window.removeEventListener('resize', handleWindowResize);
    };
  }, [isSectionExpanded, activeTab]);

  // If URL changes to #login or #price (legacy #pricing) after first load, open/scroll into view
  useEffect(() => {
    try {
      if (!isFirstLoadRef.current && location && (location.hash === '#price' || location.hash === '#pricing')) {
        // Ensure pricing details are fully opened when accessed via hash
        setOpenPricing(true);
        setIsPricingLockedOpen(true);
        setTimeout(() => {
          scrollPricingIntoCenteredView();
        }, window.innerWidth <= 1024 ? 120 : 50);
      }
      if (!isFirstLoadRef.current && location && (location.hash === '#login')) {
        // Scroll to login section with adaptive placement
        if (loginSectionRef.current) {
          scrollElementIntoViewAdaptive(loginSectionRef.current, {
            headerSelector: 'header',
            placement: 'smart',
            recenterOnResizeMs: 1500
          });
        }
      }
      if (isFirstLoadRef.current) {
        isFirstLoadRef.current = false; // ensure sections stay closed on initial load/refresh
      }
    } catch (e) {
      // no-op
    }
  }, [location]);

  // Mirror in‑app platform messages into the local chat thread so planning‑stage replies appear as chat bubbles
  useEffect(() => {
    try {
      const uid = getAuth().currentUser?.uid || null;
      if (!uid) return;
      const unsub = onPlatformMessagesSnapshot(uid, { limitSize: 10 }, (items) => {
        // On first snapshot, mark existing items as seen to avoid spamming historical messages
        if (!platformInitRef.current) {
          for (const it of items.filter((ii: any) => String((ii as any)?.source_tool || '') === 'send_platform_message_to_user')) {
            const id = String((it as any)?.message_id || (it as any)?.id || '');
            if (id) seenPlatformMessageIdsRef.current.add(id);
          }
          platformInitRef.current = true;
          return;
        }
        // Append only newly created platform messages
        const ordered = items
          .filter((it: any) => String((it as any)?.source_tool || '') === 'send_platform_message_to_user')
          .slice()
          .reverse();
        const nextBots: ChatMessage[] = [];
        for (const it of ordered) {
          const id = String((it as any)?.message_id || (it as any)?.id || '');
          if (!id || seenPlatformMessageIdsRef.current.has(id)) continue;
          seenPlatformMessageIdsRef.current.add(id);
          const html = String(((it as any)?.content?.html || '') as any);
          const md = String(((it as any)?.content?.markdown || '') as any);
          const plain = html ? html.replace(/<[^>]*>/g, '') : md;
          nextBots.push({ text: plain, isUser: false, timestamp: new Date() });
        }
        if (nextBots.length) {
          setMessages((prev) => [...prev, ...nextBots]);
          if (!isChatOpen) {
            setIsChatOpen(true);
            try {
              localStorage.setItem(landingChatOpenKey, '1');
            } catch {}
          }
        }
      }, () => {});
      return () => { try { unsub(); } catch {} };
    } catch {}
  }, [isChatOpen]);

  const typingTexts = [
    "research in minutes, with the citations",
    "never miss a deadline again",
    "gets more accurate the more you use it",
    "handle more clients without hiring",
    "reads every file, not a sample of them",
    "works 24/7 so you don't have to",
    "only ever does what the role allows"
  ];
  
  // Enhanced pricing section scrolling – force top alignment on mobile/tablet and retry during expansion
  const scrollPricingIntoCenteredView = () => {
    try {
      const target = pricingContainerRef.current || pricingSectionRef.current;
      if (!target) return;

      // Dynamically calculate viewport category
      const width = window.innerWidth;
      const height = window.innerHeight;
      
      // Categorize viewport for optimal scrolling behavior
      const isSmallMobile = width <= 480;
      const isMobile = width <= 768;
      const isTablet = width > 768 && width <= 1024;
      const isDesktop = width > 1024;

      const performScroll = () => {
        const rect = target.getBoundingClientRect();
        const currentY = window.pageYOffset || document.documentElement.scrollTop || document.body.scrollTop || 0;
        
        // Calculate optimal offset based on viewport and ensure pricing is easily readable
        let optimalOffset: number;
        
        if (isSmallMobile) {
          // Small mobile: position near top with minimal header clearance
          optimalOffset = Math.max(80, height * 0.08);
        } else if (isMobile) {
          // Standard mobile: comfortable top positioning
          optimalOffset = Math.max(100, height * 0.12);
        } else if (isTablet) {
          // Tablet: more breathing room at top
          optimalOffset = Math.max(140, height * 0.16);
        } else {
          // Desktop: center the pricing section vertically for best readability
          const targetHeight = rect.height;
          const availableHeight = height;
          
          // If content is taller than viewport, position at top with offset
          if (targetHeight > availableHeight * 0.85) {
            optimalOffset = Math.max(120, height * 0.12);
          } else {
            // Otherwise, center it vertically
            optimalOffset = (availableHeight - targetHeight) / 2;
            // Ensure minimum offset for header clearance
            optimalOffset = Math.max(optimalOffset, 100);
          }
        }
        
        // Calculate scroll position
        const desiredTop = Math.max(0, currentY + rect.top - optimalOffset);
        const maxScrollTop = Math.max(0, document.documentElement.scrollHeight - height);
        const finalScrollTop = Math.min(desiredTop, maxScrollTop);
        
        // Perform smooth scroll
        window.scrollTo({ 
          top: finalScrollTop, 
          behavior: 'smooth' 
        });
        
        // Verification and adjustment after initial scroll
        setTimeout(() => {
          const newRect = target.getBoundingClientRect();
          const currentOffset = newRect.top;
          
          // Check if pricing section is optimally positioned
          const isOptimallyPositioned = isDesktop 
            ? (currentOffset > height * 0.1 && currentOffset < height * 0.4)
            : (currentOffset > 60 && currentOffset < height * 0.25);
          
          // Re-adjust if not optimally positioned
          if (!isOptimallyPositioned) {
            const adjustedY = window.pageYOffset + newRect.top - optimalOffset;
            const clampedY = Math.min(Math.max(0, adjustedY), maxScrollTop);
            
            window.scrollTo({ 
              top: clampedY, 
              behavior: 'smooth' 
            });
          }
        }, 450);
      };

      // Initial scroll
      performScroll();

      // Progressive retry schedule for content expansion
      const retrySchedule = isDesktop 
        ? [150, 400, 750] 
        : [100, 250, 500, 850, 1200];
      
      retrySchedule.forEach(delay => setTimeout(performScroll, delay));

      // Monitor content size changes on mobile/tablet
      if (!isDesktop && (window as any).ResizeObserver) {
        const observer = new (window as any).ResizeObserver(() => {
          // Re-scroll when pricing content expands
          setTimeout(performScroll, 50);
        });
        
        try { 
          observer.observe(target); 
        } catch {}
        
        // Auto-disconnect after content stabilizes
        setTimeout(() => { 
          try { observer.disconnect(); } catch {} 
        }, 2500);
      }
    } catch (error) {
      // Graceful fallback
      try {
        const target = pricingContainerRef.current || pricingSectionRef.current;
        if (target) {
          const offset = window.innerWidth <= 768 ? 100 : 120;
          const rect = target.getBoundingClientRect();
          const top = window.pageYOffset + rect.top - offset;
          window.scrollTo({ top: Math.max(0, top), behavior: 'smooth' });
        }
      } catch {}
    }
  };

  // Add function to toggle section expansion
  const toggleSection = () => {
    setIsSectionExpanded(!isSectionExpanded);
  };

  // AI Agent marketplace data
  // (marketplaceAgents removed 2026-09-09 — 155 lines of dead data that
  // nothing rendered. It still shipped in the bundle AND was scraped into
  // the guest knowledge index, so the signed-out Prest0n could quote a
  // "YC managing partner" endorsement and healthcare / vacation-rental
  // revenue figures that appear nowhere on the site and have nothing to do
  // with law or finance. Git history is the archive.)

    const handleGoogleSuccess = async (credentialResponse: any) => {
    try {
      const { signInWithGoogleIdTokenAndResolveUser } = await import('../lib/identity/signInOrLink');
      const idToken = (credentialResponse as any)?.credential || '';
      console.log('🔐 Google sign-in starting with ID token...');
      const outcome = await signInWithGoogleIdTokenAndResolveUser(idToken);
      console.log('🔐 Google sign-in outcome:', outcome.status);
      if (outcome.status === 'ok') {
        const { getAuth } = await import('firebase/auth');
        const auth = getAuth();
        const cu = auth.currentUser;
        if (!cu) {
          console.error('No Firebase user after Google sign-in');
          alert('Authentication failed. Please try again.');
          return;
        }
        console.log(`Google sign-in successful: Firebase UID: ${cu.uid}, Canonical UID: ${outcome.canonicalUid}`);
        // CRITICAL: Use the canonical UID from mapping, not the Firebase UID, to prevent duplicates
        const user = {
          id: outcome.canonicalUid,
          name: cu.displayName || 'Google User',
          firstName: (cu.displayName || 'Google').split(' ')[0] || 'Google',
          lastName: (cu.displayName || '').split(' ').slice(1).join(' ') || 'User',
          email: cu.email || '',
          profileImage: cu.photoURL || '',
          googleAuth: true,
        };
        console.log('🔐 Calling login with user:', user.email);
        await login(user);
        console.log('🔐 Login completed, isAuthenticated should be true now');
        setShowGoogleRequired(false);
        setGoogleRequiredEmail('');
        // New signups go to onboarding; returning users go to wallet
        const targetPath = (outcome as any)?.newUser ? '/onboarding' : '/wallet';
        console.log('🔐 Navigating to:', targetPath);
        // Small delay to ensure state is fully propagated before navigation
        await new Promise(resolve => setTimeout(resolve, 100));
        navigate(targetPath);
      } else if (outcome.status === 'link_required') {
        // Email is registered with another method - show helpful message
        console.log('Account exists with different method:', outcome.methods);
        // If the user is already signed in, treat as success
        try {
          const { getAuth } = await import('firebase/auth');
          const auth = getAuth();
          const cu = auth.currentUser;
          if (cu && !cu.isAnonymous) {
            const fallbackUser = {
              id: cu.uid,
              name: cu.displayName || '',
              firstName: (cu.displayName || '').split(' ')[0] || '',
              lastName: (cu.displayName || '').split(' ').slice(1).join(' ') || '',
              email: cu.email || '',
              profileImage: cu.photoURL || '',
              googleAuth: true,
            };
            await login(fallbackUser as any);
            setShowGoogleRequired(false);
            setGoogleRequiredEmail('');
            // Small delay to ensure state is fully propagated before navigation
            await new Promise(resolve => setTimeout(resolve, 100));
            navigate('/desktop');
            setTimeout(() => { window.scrollTo(0, 0); }, 100);
            return;
          }
        } catch {}
        // Show error message about linking requirement
        const methodsStr = outcome.methods?.join(', ') || 'another method';
        alert(`This email is already registered with ${methodsStr}. Please sign in with that method first.`);
      } else {
        alert(outcome.message || 'Google sign-in failed. Please try again.');
      }
    } catch (err) {
      console.error('Google sign-in error:', err);
      alert('Google sign-in failed. Please try again.');
    }
  };

  const handleGoogleError = () => {
    console.error('Google login failed');
  };

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    console.log('Login with:', email, password);
    
    // Get existing user accounts to check for existing IDs
    const existingEmailSubsStr = localStorage.getItem('email_subscriptions');
    const existingUserAccountsStr = localStorage.getItem('user_accounts');
    const existingProfilesStr = localStorage.getItem('all_profiles_data');
    
    const existingEmailSubs = existingEmailSubsStr ? JSON.parse(existingEmailSubsStr) : [];
    const existingUserAccounts = existingUserAccountsStr ? JSON.parse(existingUserAccountsStr) : [];
    const existingProfiles = existingProfilesStr ? JSON.parse(existingProfilesStr) : [];
    
    // Check if email already exists in accounts
    const emailExists = [...existingEmailSubs, ...existingUserAccounts].some(
      item => item.email.toLowerCase() === email.toLowerCase()
    );
    
    // Save the email to localStorage with unique IDs
    const success = saveEmailSubscription(email, 'signup', { 
      hasPassword: true,
      signupDate: new Date().toISOString()
    });
    
    if (success) {
      console.log(`Successfully registered new user with email: ${email}`);
      
      // Get the fresh user accounts to confirm IDs
      const updatedUserAccountsStr = localStorage.getItem('user_accounts');
      if (updatedUserAccountsStr) {
        try {
                     const updatedAccounts = JSON.parse(updatedUserAccountsStr) as EmailSubscription[];
           const userAccount = updatedAccounts.find(acc => acc.email.toLowerCase() === email.toLowerCase());
          
          if (userAccount && userAccount.metadata) {
            console.log(`Confirmed IDs for ${email}: Account ID=${userAccount.metadata.accountId}, Member ID=${userAccount.metadata.profileId}`);
          }
        } catch (err) {
          console.error('Error checking updated accounts:', err);
        }
      }
    } else if (!emailExists) {
      console.error(`Failed to save new user with email: ${email}`);
    }
    
    // Do not navigate without a real Firebase session
    try {
      const { getAuth } = await import('firebase/auth');
      const cu = getAuth().currentUser;
      if (cu) {
        navigate('/desktop');
      } else {
        // Show sign-in UI instead of token-only navigation
        scrollToLogin();
      }
    } catch {
      scrollToLogin();
    }
  };

  const currentPlainText = useCallback((): string => {
    const plain = promptHtml ? htmlToPlainText(promptHtml) : '';
    return (plain || prompt || '').trim();
  }, [promptHtml, prompt]);

  const handleToggleDock = useCallback(() => {
    setIsChatDockedRight(prev => !prev);
    // Ensure chat stays open when docking/undocking
    setIsChatOpen(true);
    try {
      localStorage.setItem(landingChatOpenKey, '1');
    } catch {}
  }, []);

  // Cycle through layout modes: default → docked → fullscreen → default
  const handleCycleMode = useCallback(() => {
    if (isChatFullScreen) {
      // Fullscreen → Default (centered, not docked)
      setIsChatFullScreen(false);
      setIsChatDockedRight(false);
    } else if (isChatDockedRight) {
      // Docked → Fullscreen
      setIsChatFullScreen(true);
      setIsChatDockedRight(false);
    } else {
      // Default → Docked right
      setIsChatDockedRight(true);
      setIsChatOpen(true);
      try {
        localStorage.setItem(landingChatOpenKey, '1');
      } catch {}
    }
  }, [isChatFullScreen, isChatDockedRight]);

  // Clear chat history handlers
  const handleClearChat = useCallback(() => {
    setShowClearConfirmModal(true);
  }, []);

  const confirmClearChat = useCallback(async () => {
    // Hide modal
    setShowClearConfirmModal(false);

    // Clear React state
    setMessages([]);

    // Clear localStorage
    try {
      localStorage.removeItem(CHAT_STORAGE_KEY);
      localStorage.removeItem(CHAT_DRAFT_STORAGE_KEY);
    } catch (e) {
      console.warn('Failed to clear localStorage:', e);
    }

    // Clear Firestore (for authenticated users)
    if (user?.id) {
      try {
        await clearConversationHistory(user.id);
      } catch (e) {
        console.warn('Failed to clear Firestore history:', e);
      }
    }

    // Clear draft input in UI
    if (promptInputRef.current) {
      promptInputRef.current.innerHTML = '';
      promptInputRef.current.textContent = '';
    }
    setPrompt('');
    setPromptHtml('');
  }, [user]);

  const hideClearConfirmation = useCallback(() => {
    setShowClearConfirmModal(false);
  }, []);

  // Collapsed bar click handler: reopen chat while staying docked
  const handleCollapsedClick = useCallback(() => {
    setIsChatOpen(true);
    try {
      localStorage.setItem(landingChatOpenKey, '1');
    } catch {}
  }, []);

  // BUG-DR-COMPOSER-MID-RUN-LANDINGPAGE — 15-minute safety-net timer for
  // the draft-suppression in-flight gate opened in handleSend. Tracked in
  // a ref so back-to-back sends cancel the previous timer (avoid stacking)
  // and unmount can drain a pending bump (avoid permanent suppression
  // after SPA navigation mid-stream).
  const landingDraftSuppressionEndTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    return () => {
      if (landingDraftSuppressionEndTimerRef.current) {
        clearTimeout(landingDraftSuppressionEndTimerRef.current);
        landingDraftSuppressionEndTimerRef.current = null;
        // Drain the refcount bump so the cooldown timer starts ticking;
        // otherwise a remount would observe stale unconditional suppression.
        draftMarkProcessingEnded();
      }
    };
  }, []);

  // Copy message to clipboard
  const copyMessageToClipboard = useCallback((message: ChatMessage) => {
    let textToCopy = '';
    if (message.html) {
      // Extract text from HTML
      const temp = document.createElement('div');
      temp.innerHTML = message.html;
      textToCopy = temp.textContent || temp.innerText || '';
    } else {
      textToCopy = message.text || '';
    }
    // Add attachment info if present
    if (message.attachmentsSerialized?.length) {
      const attachNames = message.attachmentsSerialized.map(a => a.name).join(', ');
      textToCopy += `\n\nattachments: ${attachNames}`;
    }
    navigator.clipboard.writeText(textToCopy).catch(() => {
      // Fallback for older browsers
      const textarea = document.createElement('textarea');
      textarea.value = textToCopy;
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand('copy');
      document.body.removeChild(textarea);
    });
  }, []);

  const handleSend = async () => {
    const safeHtml = promptHtml ? normalizeChatHtml(promptHtml) : '';
    const text = currentPlainText();
    if (!text) return;
    let attachmentsSerialized: SerializedAttachment[] | undefined;
    if (attachments.length) {
      try {
        attachmentsSerialized = await serializeAttachments(attachments);
      } catch {
        attachmentsSerialized = undefined;
      }
    }

    let sessionId = activeWorkflowSessionId;
    if (isAuthenticated && !sessionId) {
      try {
        const session = await createWorkflowSession({ title: 'New thread', workflowType: 'chat' });
        sessionId = session.sessionId;
        setActiveWorkflowSessionId(sessionId);
        setStoredWorkflowSessionId(sessionId);
        setWorkflowSessions(prev => [session, ...prev]);
      } catch (err) {
        console.warn('[PRE-70] Failed to create workflow session:', err);
      }
    }

    const userMessage: ChatMessage = {
      text,
      html: safeHtml || undefined,
      isUser: true,
      timestamp: new Date(),
      attachments: attachments.length ? [...attachments] : undefined,
      attachmentsSerialized,
    };
    setMessages(prev => [...prev, userMessage]);
    try { window.dispatchEvent(new CustomEvent('chat:user-message', { detail: userMessage })); } catch {}
    // BUG-DR-COMPOSER-REPOP-LONG-TURN — stamp the shared post-send window
    // BEFORE clearing prompt state so the cross-tab poll / storage-event
    // sync (both this file's and useGlobalChat's) sees the window and
    // skips. Without this, the LandingPage's OWN send path (which never
    // calls useGlobalChat.handleSend) would not stamp the singleton and
    // useGlobalChat's poll would still rehydrate.
    draftMarkSentNow();
    // FH-QA composer-refill race on LONG NON-attachment prompts
    // (2026-05-21 16:25 UTC): stamp the content-domain sentinel
    // alongside the time-domain stamp. This composer's own debounced
    // persist effect and its sibling cross-tab poll both consult
    // `isSentTextMatch` at fire time so a stale closure carrying the
    // just-sent body cannot resurrect it after RESET clears state.
    draftMarkSentText(text || '', safeHtml || '');
    // BUG-DR-COMPOSER-MID-RUN-LANDINGPAGE — open the in-flight gate
    // synchronously from THIS entry point so suppression covers the full
    // turn duration (5-8 min for Deep Research) even when useGlobalChat's
    // own isProcessing-bridging useEffect hasn't fired yet, isn't mounted
    // yet, or lives in a different React tree. Paired with a 15-minute
    // safety-net markProcessingEnded() (see ref/effect below) so a missed
    // observation of turn-end can never leave a permanent suppression.
    draftMarkProcessingStarted();
    if (landingDraftSuppressionEndTimerRef.current) {
      clearTimeout(landingDraftSuppressionEndTimerRef.current);
      landingDraftSuppressionEndTimerRef.current = null;
    }
    landingDraftSuppressionEndTimerRef.current = setTimeout(() => {
      draftMarkProcessingEnded();
      landingDraftSuppressionEndTimerRef.current = null;
    }, LANDING_HANDLE_SEND_SAFETY_END_MS);
    setPrompt('');
    setPromptHtml('');
    // Clear saved draft since message was sent
    try { localStorage.removeItem(CHAT_DRAFT_STORAGE_KEY); } catch {}
    if (promptInputRef.current) {
      promptInputRef.current.innerHTML = '';
    }
    setAttachments([]);
    setIsAttachmentPanelVisible(false);
    if (!isChatOpen) setIsChatOpen(true);
    const uid = (() => {
      try {
        return getAuth().currentUser?.uid || '';
      } catch {
        return '';
      }
    })();

    if (!uid) {
      // Not authenticated — show construction/waitlist notice
      openConstructionNotice({ text, source: 'landing_prompt_bar' });
      return;
    }

    // Authenticated user — route through platform message service
    try {
      const res = await PlatformMessageService.getInstance().sendIncomingPlatformMessage(text, { session_id: sessionId || undefined });
      // Extract message from planning_stage.planned_tools (response shape: { operation_status, planning_stage, tools_executed })
      if (res?.planning_stage?.planned_tools?.length) {
        const tool = res.planning_stage.planned_tools[0];
        const elements = Array.isArray(tool?.elements) ? tool.elements : [];
        const messageElement = elements.find((el: any) => el?.key === 'message_content');
        const aiText = String(messageElement?.value || '').trim();
        if (aiText) {
          const reply: ChatMessage = { text: aiText, isUser: false, timestamp: new Date() };
          setMessages(prev => [...prev, reply]);
          window.dispatchEvent(new CustomEvent('chat:add-platform-messages', { detail: [{ text: aiText, isUser: false, timestamp: new Date() }] }));
        }
      }
    } catch (error: any) {
      console.warn('[PlatformMessage] Request failed:', error?.code || 'UNKNOWN', error?.message || error);
      // Authenticated user hit an error — show a friendly fallback instead of silence
      // No on-call notice here (follow-up to #1233/#1275): this is an
      // unclassified client-side catch — it covers network failures where the
      // request never reached a server that could file anything, so claiming
      // a report would send the user chasing one that does not exist.
      const fallback: ChatMessage = { text: 'Something went wrong processing your request. Please try again.', isUser: false, timestamp: new Date() };
      setMessages(prev => [...prev, fallback]);
    }
  };
  
  // The landing page's forked chat-scroll machinery (near-bottom refs,
  // scroll persistence to the shared CHAT_SCROLL key, reveal-older scroll
  // animation) was deleted in the 2026-08 scroll redesign: the custom
  // transcript UI it drove was already removed (GlobalChatSystem renders at
  // App level and owns ALL transcript scrolling via the shared
  // useTranscriptScroll controller), its container ref never attached, and
  // its localStorage writes clobbered the production chat's saved scroll
  // position.



  // Close token info popup when messages change
  useEffect(() => {
    if (activeTokenInfoIndex !== null) {
      setActiveTokenInfoIndex(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [messages.length]);

  // Close token info popup on outside click
  useEffect(() => {
    if (activeTokenInfoIndex === null) return;

    const handleGlobalClick = (e: MouseEvent) => {
      const target = e.target as Node | null;
      if (!target) return;

      // Check if click is inside the chat container
      const chatContainer = document.querySelector('[data-chat-container]');
      if (chatContainer && !chatContainer.contains(target)) {
        setActiveTokenInfoIndex(null);
      }
    };

    // Small delay to prevent immediate close on the same click that opened it
    const timeoutId = setTimeout(() => {
      document.addEventListener('mousedown', handleGlobalClick);
    }, 10);

    return () => {
      clearTimeout(timeoutId);
      document.removeEventListener('mousedown', handleGlobalClick);
    };
  }, [activeTokenInfoIndex]);


  // Listen for platform messages and append them as AI chat bubbles
  useEffect(() => {
    function onPlatformChat(e: any) {
      try {
        const arr = Array.isArray(e?.detail) ? e.detail : [];
        if (!arr.length) return;
        // Use ref for stable closure — avoids stale activeWorkflowSessionId during session switches
        const currentSessionId = activeSessionIdRef.current;
        const relevant = arr.filter((it: any) => {
          const eventSessionId = typeof it?.session_id === 'string' ? it.session_id : undefined;
          // No active session → show only untagged messages
          if (!currentSessionId) return !eventSessionId;
          // Active session → show ONLY messages tagged with this session
          return eventSessionId === currentSessionId;
        });
        if (!relevant.length) return;
        setMessages((prev) => [
          ...prev,
          ...relevant.map((it: any) => ({
            text: String(it?.text || it?.html || ''),
            html: typeof it?.html === 'string' ? it.html : undefined,
            isUser: false,
            timestamp: new Date()
          }))
        ]);
      } catch {}
    }
    window.addEventListener('chat:add-platform-messages', onPlatformChat);
    function onSystemMessage(e: any) {
      try {
        const d = e?.detail || {};
        const text = String(d.text || '').trim();
        if (!text) return;
        const ts = d.timestamp ? new Date(d.timestamp) : new Date();
        const sourceTag = String(d.source || 'system');
        setMessages((prev) => [
          ...prev,
          {
            text,
            isUser: false,
            timestamp: ts,
            html: typeof d.html === 'string' ? d.html : undefined,
            sourceTag,
          }
        ].sort((a, b) => {
          const ta = (a.timestamp instanceof Date) ? a.timestamp.getTime() : new Date(a.timestamp).getTime();
          const tb = (b.timestamp instanceof Date) ? b.timestamp.getTime() : new Date(b.timestamp).getTime();
          return ta - tb;
        }));
      } catch {}
    }
    window.addEventListener('chat:add-system-message', onSystemMessage);
    // Poll fallback API for in-app messages when Firestore is not available (localhost only)
    let polling = true;
    async function poll() {
      try {
        // Only poll on localhost - production uses Firestore real-time listeners
        const host = (window.location.hostname || '').toLowerCase();
        const isLocal = host === 'localhost' || host === '127.0.0.1' || host.endsWith('.local');
        if (!isLocal) return;

        const uid = (await import('firebase/auth')).getAuth().currentUser?.uid || '';
        if (uid) {
          const res = await fetch(`http://127.0.0.1:3001/api/pull-inapp-messages?uid=${encodeURIComponent(uid)}`);
          const data = await res.json().catch(() => ({ ok: false }));
          if (data && data.ok && Array.isArray(data.items) && data.items.length) {
            const chatItems = data.items.map((it: any) => ({ text: String(it?.text || ''), html: String(it?.html || ''), isUser: false, timestamp: new Date() }));
            window.dispatchEvent(new CustomEvent('chat:add-platform-messages', { detail: chatItems }));
          }
        }
      } catch {}
      if (polling) setTimeout(poll, 1500);
    }
    poll();
    return () => { 
      try { window.removeEventListener('chat:add-platform-messages', onPlatformChat); } catch {} 
      try { window.removeEventListener('chat:add-system-message', onSystemMessage); } catch {}
    };
  }, [activeWorkflowSessionId]);

  // PRE-70: Direct Firestore listener for session-scoped AI responses.
  // This bypasses InAppMessageOverlay's broadcast and ensures each thread only sees its own messages.
  // Solves the message bleed issue where switching threads missed messages dispatched while another thread was active.
  useEffect(() => {
    const uid = getAuth().currentUser?.uid;
    const sessionId = activeSessionIdRef.current;
    if (!uid || !sessionId) return;
    // Track message IDs we've already added to chat to avoid duplicates
    const addedIds = new Set<string>();
    // Seed with any messages already rendered (from localStorage restore)
    // This prevents re-adding historical messages on listener attach
    let seeded = false;
    const unsub = onSessionPlatformMessagesSnapshot(uid, sessionId, (items) => {
      if (!seeded) {
        // First snapshot: mark all existing messages as already added
        items.forEach((m: any) => addedIds.add(m.id));
        seeded = true;
        return;
      }
      const newItems = items.filter((m: any) => !addedIds.has(m.id));
      if (!newItems.length) return;
      newItems.forEach((m: any) => addedIds.add(m.id));
      setMessages((prev) => [
        ...prev,
        ...newItems.map((m: any) => ({
          text: String(m?.content?.markdown || m?.content?.html || ''),
          html: typeof m?.content?.html === 'string' ? m.content.html : undefined,
          isUser: false,
          timestamp: new Date(m?.created_at_iso || Date.now()),
        })),
      ]);
    }, (err) => {
      // eslint-disable-next-line no-console
      console.warn('PRE-70 session platform messages listener error:', err?.message || err);
    });
    return () => { try { unsub(); } catch {} };
  }, [activeWorkflowSessionId]);

  // Handle key press in prompt input (works for both input and textarea)
  const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const scrollToLogin = () => {
    const el = document.getElementById('login-section');
    if (el) {
      scrollElementIntoViewAdaptive(el, {
        headerSelector: 'header',
        placement: 'smart',
        recenterOnResizeMs: 1500
      });
    }
  };
  
  // Programmatically trigger the Google sign-in button when needed (e.g., link-required flows)
  const triggerGoogleSignIn = () => {
    try {
      const btn = googleButtonRef.current?.querySelector('div[role="button"]') as HTMLElement | null;
      btn?.click();
    } catch (err) {
      console.warn('Unable to trigger Google sign-in button', err);
    }
  };

  // Close chat when clicking outside the chat history + prompt area
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent | TouchEvent) => {
      if (!isChatOpen) return;
      const target = event.target as Node | null;
      if (!target) return;
      const insideChat = chatContainerRef.current?.contains(target);
      const insidePrompt = promptBarRef.current?.contains(target);
      const insideFloating = floatingPromptBarRef.current?.contains(target);
      if (!insideChat && !insidePrompt && !insideFloating) {
        setLastAttachmentVisibleOnHide(isAttachmentPanelVisible && !userCollapsedAttachments && attachments.length > 0);
        setIsChatOpen(false);
        try {
          localStorage.setItem(landingChatOpenKey, '0');
        } catch {}
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('touchstart', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, [isChatOpen]);
  
  // Auto-rotate tabs every 8 seconds
  useEffect(() => {
    if (!isSectionExpanded || !autoRotateTabs) return;
    
    const tabRotationTimer = setInterval(() => {
      setActiveTab(prev => {
        if (prev === 'triggers') return 'models';
        if (prev === 'models') return 'tools';
        return 'triggers';
      });
    }, 8000);
    
    return () => clearInterval(tabRotationTimer);
  }, [isSectionExpanded, autoRotateTabs]);
  
  // Pause auto-rotation when user manually switches tabs
  const handleTabChange = (tab: 'triggers' | 'models' | 'tools') => {
    setActiveTab(tab);
    setAutoRotateTabs(false);
    
    // Resume auto-rotation after 30 seconds of inactivity
    const resumeTimer = setTimeout(() => {
      setAutoRotateTabs(true);
    }, 30000);
    
    return () => clearTimeout(resumeTimer);
  };

  // Add handleFileSelect function to handle file selection
  const handleFileSelect = () => {
    // Always surface the chat so users see history + attachments context
    setIsChatOpen(true);
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };
  // Update the handleFileChange function to handle multiple files
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    // Check each file size (the cap is MAX_UPLOAD_BYTES_PER_FILE, currently 50MB)
    const maxSize = MAX_UPLOAD_BYTES_PER_FILE; // 50MB — see src/constants/uploads.ts
    const validFiles: File[] = [];
    const oversizedFiles: string[] = [];

    Array.from(files).forEach(file => {
      if (file.size <= maxSize) {
        validFiles.push(file);
      } else {
        oversizedFiles.push(file.name);
      }
    });

    // Add valid files to attachments
    if (validFiles.length > 0) {
      setAttachments(prev => [...prev, ...validFiles]);
      
      // Show attachment panel
      setIsAttachmentPanelVisible(true);
      setIsChatOpen(true);
    }

    // Alert for oversized files
    if (oversizedFiles.length > 0) {
      alert(`The following file(s) exceed the ${MAX_UPLOAD_LABEL_PER_FILE} size limit and were not added:\n${oversizedFiles.join('\n')}`);
    }

    // Reset the file input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // Sanitize HTML: allow a small set of tags and filtered styles; strip scripts
  const sanitizeHtmlInput = (html: string): string => {
    try {
      const allowedTags = new Set(['b', 'strong', 'i', 'em', 'u', 'span', 'p', 'br', 'ul', 'ol', 'li', 'blockquote', 'pre', 'code', 'div']);
      const allowedStyles = new Set([
        'font-weight',
        'font-style',
        'text-decoration',
        'font-size',
        'font-family',
        'color',
        'background-color',
        'margin-left',
        'padding-left',
        'text-align'
      ]);

      const filterStyle = (styleValue: string) => {
        const parts = styleValue.split(';').map(s => s.trim()).filter(Boolean);
        const kept: string[] = [];
        for (const part of parts) {
          const [propRaw, ...valParts] = part.split(':');
          if (!propRaw || valParts.length === 0) continue;
          const prop = propRaw.trim().toLowerCase();
          const val = valParts.join(':').trim();
          if (allowedStyles.has(prop)) {
            kept.push(`${prop}: ${val}`);
          }
        }
        return kept.join('; ');
      };

      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');

      Array.from(doc.body.querySelectorAll('*')).forEach((node) => {
        const tag = node.tagName.toLowerCase();
        if (!allowedTags.has(tag)) {
          node.replaceWith(...Array.from(node.childNodes));
          return;
        }
        Array.from(node.attributes).forEach(attr => {
          const name = attr.name.toLowerCase();
          if (name === 'style') {
            const filtered = filterStyle(attr.value);
            if (filtered) node.setAttribute('style', filtered);
            else node.removeAttribute('style');
          } else if (name === 'href') {
            const href = attr.value || '';
            if (!/^https?:/i.test(href) && !/^mailto:/i.test(href)) {
              node.removeAttribute('href');
            }
          } else if (name.startsWith('on') || name === 'src' || name === 'id' || name === 'class' || name === 'data' || name === 'aria-label') {
            node.removeAttribute(attr.name);
          }
        });
      });

      return doc.body.innerHTML;
    } catch {
      return '';
    }
  };

  // Basic HTML-to-plain-text formatter that preserves line breaks and bullets
  const htmlToPlainText = (html: string): string => {
    try {
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');
      const lines: string[] = [];

      const walk = (node: Node) => {
        if (node.nodeType === Node.TEXT_NODE) {
          const text = (node.textContent || '').replace(/\s+/g, ' ');
          if (text.trim()) lines.push(text);
          return;
        }
        if (node.nodeType !== Node.ELEMENT_NODE) return;
        const el = node as HTMLElement;
        const blockTags = ['P', 'DIV', 'SECTION', 'ARTICLE', 'PRE', 'BLOCKQUOTE'];
        const listItem = el.tagName === 'LI';
        const lineBreak = el.tagName === 'BR';

        if (lineBreak) {
          lines.push('\n');
          return;
        }
        if (listItem) {
          lines.push('\n- ');
        }

        Array.from(el.childNodes).forEach(walk);

        if (blockTags.includes(el.tagName) || listItem) {
          lines.push('\n');
        }
      };

      Array.from(doc.body.childNodes).forEach(walk);
      const combined = lines.join('').replace(/\n{3,}/g, '\n\n').trim();
      return combined;
    } catch {
      return html.replace(/<\/?[^>]+(>|$)/g, '');
    }
  };

  const handlePaste = (e: React.ClipboardEvent<HTMLDivElement>) => {
    const files = e.clipboardData?.files;
    const hasFiles = files && files.length > 0;

    // If files exist in clipboard, treat as attachment
    if (hasFiles) {
      const maxSize = MAX_UPLOAD_BYTES_PER_FILE; // 50MB — see src/constants/uploads.ts
      const validFiles: File[] = [];
      const oversizedFiles: string[] = [];

      Array.from(files!).forEach(file => {
        if (file.size <= maxSize) {
          validFiles.push(file);
        } else {
          oversizedFiles.push(file.name);
        }
      });

      if (validFiles.length > 0) {
        e.preventDefault(); // avoid inserting file names into the text box
        setAttachments(prev => [...prev, ...validFiles]);
        setIsAttachmentPanelVisible(true);
        setIsChatOpen(true);
      }

      if (oversizedFiles.length > 0) {
        alert(`The following file(s) exceed the ${MAX_UPLOAD_LABEL_PER_FILE} size limit and were not added:\n${oversizedFiles.join('\n')}`);
      }
      return;
    }

    // Otherwise, format pasted rich text into sanitized HTML and update prompt
    // text. Same pipeline as the global composer (pasteSanitizer, 2026-07-13):
    // keeps the user's formatting, contrast-fixes only genuinely too-light
    // colors, strips pasted backgrounds, trims edge blank blocks; plain-text
    // pastes insert literally (escape + <br>) instead of being parsed as HTML.
    const html = e.clipboardData?.getData('text/html') || '';
    const plain = e.clipboardData?.getData('text/plain') || '';
    if (html || plain) {
      e.preventDefault();
      const sanitized = html
        ? sanitizePastedHtml(html)
        : plainTextToComposerHtml(sanitizePastedText(plain));
      // Whole-composer trim (owner follow-up 2026-07-14, mirrors
      // useGlobalChat.handlePaste): the fragment is edge-trimmed by the
      // sanitizers, but the COMPOSED prompt (previous content + <br> +
      // fragment) can still carry edge empties from the pre-existing content
      // (e.g. the stray <br> browsers leave in an "emptied" contentEditable).
      const newHtml = trimEdgeEmptyBlocks(
        promptHtml
          ? `${promptHtml}${promptHtml.endsWith('<br>') ? '' : '<br>'}${sanitized}`
          : sanitized
      );
      setPromptHtml(newHtml);
      setPrompt(htmlToPlainText(newHtml));
      if (promptInputRef.current) {
        if (promptInputRef.current.innerHTML !== newHtml) {
          promptInputRef.current.innerHTML = newHtml;
        }
        // Move caret to end after paste
        try {
          const range = document.createRange();
          const sel = window.getSelection();
          range.selectNodeContents(promptInputRef.current);
          range.collapse(false);
          sel?.removeAllRanges();
          sel?.addRange(range);
        } catch {}
      }
    }
  };

  const handleInput = (e: React.FormEvent<HTMLDivElement>) => {
    // Mark as actively typing - prevents sync from overwriting user input
    promptFocusedRef.current = true;
    const html = e.currentTarget.innerHTML;
    const sanitized = sanitizeHtmlInput(html);
    // Only mutate DOM if sanitization changed the content (e.g., stripping disallowed tags)
    // In that case, restore caret to end after mutation
    if (sanitized !== html) {
      // Save selection before mutation
      const sel = window.getSelection();
      e.currentTarget.innerHTML = sanitized;
      // Move caret to end after DOM mutation
      try {
        const range = document.createRange();
        range.selectNodeContents(e.currentTarget);
        range.collapse(false);
        sel?.removeAllRanges();
        sel?.addRange(range);
      } catch {}
    }
    // Update state (no DOM mutation here - browser handles caret naturally)
    setPromptHtml(sanitized);
    setPrompt(htmlToPlainText(sanitized));
  };

  // Handler for prompt input focus
  const handlePromptFocus = () => {
    promptFocusedRef.current = true;
  };

  // Handler for prompt input blur - allows sync to resume
  const handlePromptBlur = () => {
    promptFocusedRef.current = false;
  };

  // Add handler to remove a specific attachment
  const handleRemoveAttachment = (index: number) => {
    setAttachments(prev => prev.filter((_, i) => i !== index));
    
    // Hide panel if no attachments left
    if (attachments.length === 1) {
      setTimeout(() => setIsAttachmentPanelVisible(false), 300);
    }
  };
  
  // Add handler to clear all attachments
  const handleClearAttachments = () => {
    setAttachments([]);
    setTimeout(() => setIsAttachmentPanelVisible(false), 300);
  };
  
  // Toggle attachment panel visibility
  const toggleAttachmentPanel = () => {
    if (attachments.length > 0) {
      setIsAttachmentPanelVisible(prev => {
        const next = !prev;
        if (!next) setUserCollapsedAttachments(true);
        else setUserCollapsedAttachments(false);
        return next;
      });
    }
  };

  // Unified attachment button behavior (chat-open only; collapsed view no button)
  const handleAttachmentButtonClick = (e?: React.MouseEvent) => {
    try { e?.stopPropagation(); } catch {}
    if (!isChatOpen) return;

    if (attachments.length > 0) {
      setIsAttachmentPanelVisible(prev => !prev);
      return;
    }

    handleFileSelect();
  };

  // Handle drag events
  const handleDragEnter = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    
    // Check if we're still within the prompt bar
    const rect = promptBarRef.current?.getBoundingClientRect();
    if (rect) {
      const { clientX, clientY } = e;
      if (
        clientX < rect.left ||
        clientX > rect.right ||
        clientY < rect.top ||
        clientY > rect.bottom
      ) {
        setIsDragging(false);
      }
    }
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
  };
  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    
    const files = e.dataTransfer.files;
    if (!files || files.length === 0) return;

    // Check each file size (the cap is MAX_UPLOAD_BYTES_PER_FILE, currently 50MB)
    const maxSize = MAX_UPLOAD_BYTES_PER_FILE; // 50MB — see src/constants/uploads.ts
    const validFiles: File[] = [];
    const oversizedFiles: string[] = [];

    Array.from(files).forEach(file => {
      if (file.size <= maxSize) {
        validFiles.push(file);
      } else {
        oversizedFiles.push(file.name);
      }
    });

    // Add valid files to attachments
    if (validFiles.length > 0) {
      setAttachments(prev => [...prev, ...validFiles]);
      
      // Show attachment panel
      setIsAttachmentPanelVisible(true);
      setIsChatOpen(true);
    }

    // Alert for oversized files
    if (oversizedFiles.length > 0) {
      alert(`The following file(s) exceed the ${MAX_UPLOAD_LABEL_PER_FILE} size limit and were not added:\n${oversizedFiles.join('\n')}`);
    }
  };

  // Add an event listener to close the mobile menu when clicking outside of it - inside the component
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      // Check if the mobile menu is open and if the click is outside the menu
      if (isMobileMenuOpen && event.target instanceof Element) {
        const mobileMenuElements = document.querySelectorAll('[data-mobile-menu="true"]');
        let isMenuClick = false;
        
        mobileMenuElements.forEach(element => {
          if (element.contains(event.target as Node)) {
            isMenuClick = true;
          }
        });
        
        if (!isMenuClick) {
          setIsMobileMenuOpen(false);
        }
      }
    }
    
    // Add event listener
    document.addEventListener('mousedown', handleClickOutside);
    
    // Return cleanup function
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isMobileMenuOpen]);

  // Ensure marketplace stays user-controlled; do not auto-expand on load or resize

  // Close notifications dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (!isNotificationsOpen) return;
      if (notificationWrapperRef.current && event.target instanceof Element) {
        if (!notificationWrapperRef.current.contains(event.target)) {
          setIsNotificationsOpen(false);
        }
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isNotificationsOpen]);

  // Load notifications for the authenticated account from localStorage
  useEffect(() => {
    if (!hasAccount || !user?.accountId) {
      setNotifications([]);
      setReadIds([]);
      setUnreadCount(0);
      return;
    }
    try {
      const raw = localStorage.getItem(`notifications_${user.accountId}`);
      const all: HeaderNotification[] = raw ? JSON.parse(raw) : [];
      all.sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());
      setNotifications(all);
    } catch (err) {
      console.warn('Failed to load notifications:', err);
      setNotifications([]);
    }
  }, [hasAccount, user?.accountId]);
  // Load read ids
  useEffect(() => {
    if (!hasAccount || !user?.accountId) return;
    try {
      const raw = localStorage.getItem(`notifications_read_${user.accountId}`);
      const ids: string[] = raw ? JSON.parse(raw) : [];
      setReadIds(Array.isArray(ids) ? ids : []);
    } catch {
      setReadIds([]);
    }
  }, [hasAccount, user?.accountId]);

  // Compute unread
  useEffect(() => {
    if (!hasAccount) return;
    const unread = notifications.filter(n => !readIds.includes(n.id)).length;
    setUnreadCount(unread);
  }, [hasAccount, notifications, readIds]);
  // Mark as read when opening the dropdown
  useEffect(() => {
    if (!isNotificationsOpen || !hasAccount || !user?.accountId) return;
    const allIds = notifications.map(n => n.id);
    if (allIds.length === 0) return;
    const merged = Array.from(new Set([...(readIds || []), ...allIds]));
    setReadIds(merged);
    try {
      localStorage.setItem(`notifications_read_${user.accountId}`, JSON.stringify(merged));
      window.dispatchEvent(new CustomEvent('notifications-updated', { detail: { accountId: user.accountId } }));
    } catch {}
  }, [isNotificationsOpen]);

  const formatDateTime = (iso: string) => {
    try {
      const d = new Date(iso);
      return `${d.toLocaleDateString()} ${d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`;
    } catch {
      return iso;
    }
  };

  // Feature data array for easier management and duplication
  const featuresData = [
    {
      id: 'case-law-research',
      icon: RealtimeOnlineSearchIcon,
      title: 'case law research',
      description: 'find the right cases and statutes in seconds. stop searching, start working.'
    },
    {
      id: 'document-drafting',
      icon: SmartBreakdownIcon,
      title: 'document drafting',
      description: 'contracts, motions, briefs, memos — drafted in seconds, in your voice.'
    },
    {
      id: 'client-intake',
      icon: IntuitiveTeachingIcon,
      title: 'client intake automation',
      description: 'new clients get handled fast. intake, conflict checks, and routing — done automatically.'
    },
    {
      id: 'deadline-tracking',
      icon: AmbientFunctionalityIcon,
      title: 'deadline & docket tracking',
      description: 'every deadline tracked. nothing missed. you get an alert before it\'s urgent.'
    },
    {
      id: 'billing-time',
      icon: InfiniteCapabilityIcon,
      title: 'billing & time capture',
      description: 'stop losing billable hours to forgetfulness. every minute of work gets captured.'
    },
    {
      id: 'matter-management',
      icon: SystemCompatibilityIcon,
      title: 'matter management',
      description: 'all your case files, emails, and docs in one place. find anything instantly.'
    },
    {
      id: 'regulatory-compliance',
      icon: AdaptivePlanningIcon,
      title: 'regulatory compliance',
      description: 'always up to date on bar rules and ethics requirements. no more guessing.'
    },
    {
      id: 'multilingual-support',
      icon: SmartIntentRecognitionIcon,
      title: 'multilingual support',
      description: 'work with clients in english or spanish. documents and messages translated instantly.'
    },
    {
      id: 'deposition-prep',
      icon: IntelligentToolSelectionIcon,
      title: 'deposition preparation',
      description: 'walks you into every deposition prepared. key facts, weak spots, ready questions.'
    },
    {
      id: 'contract-review',
      icon: SelfOptimizingIcon,
      title: 'contract review',
      description: 'spots the risky language before your client signs. every contract, every time.'
    },
    {
      id: 'client-communication',
      icon: CustomizationIcon,
      title: 'client communication',
      description: 'keeps clients informed without eating up your time. updates drafted in your voice.'
    },
    {
      id: 'knowledge-management',
      icon: EasyKnowledgeUseIcon,
      title: 'knowledge management',
      description: 'every case teaches the system. new matters get handled with the knowledge of all your past ones.'
    },
    {
      id: 'privacy-by-design',
      icon: TrustShieldIcon,
      title: 'privacy by design',
      description: 'client data stays yours. never shared. deleted on demand. privilege protected.'
    }
  ];



  // Handle card expand/collapse
  const handleCardToggle = (cardId: string) => {
    setExpandedCards(prev => ({
      ...prev,
      [cardId]: !prev[cardId]
    }));
  };

  // Handler for feature card interactions
  const handleFeatureToggle = (featureId: string) => {
    setExpandedFeatures(prev => {
      // On mobile, only allow one expanded at a time
      if (window.innerWidth <= 768) {
        const newState: ExpandedCardState = {};
        // If the clicked feature is already expanded, close it. Otherwise, open only this one.
        if (!prev[featureId]) {
          newState[featureId] = true;
        }
        return newState;
      } else {
        // On desktop, allow multiple expanded
        return {
          ...prev,
          [featureId]: !prev[featureId]
        };
      }
    });
  };

  const handleFeatureHover = (featureId: string, isHovering: boolean) => {
    // On desktop, show/hide on hover
    if (window.innerWidth > 768) {
      setExpandedFeatures(prev => ({
        ...prev,
        [featureId]: isHovering
      }));
    }
  };

  // Handler for benefit card interactions
  const handleBenefitToggle = (benefitId: string) => {        
    setExpandedBenefits(prev => {
      // On mobile, only allow one expanded at a time
      if (window.innerWidth <= 768) {
        const newState: ExpandedCardState = {};
        // If the clicked benefit is already expanded, close it. Otherwise, open only this one.
        if (!prev[benefitId]) {
          newState[benefitId] = true;
        }
        return newState;
      }
      
      // On desktop, toggle individual benefit
      return {
        ...prev,
        [benefitId]: !prev[benefitId]
      };
    });
  };

  const handleBenefitHover = (benefitId: string, isHovering: boolean) => {
    // On desktop, show/hide on hover
    if (window.innerWidth > 768) {
      setExpandedBenefits(prev => ({
        ...prev,
        [benefitId]: isHovering
      }));
    }
  };

  // Define structured data for the homepage
  const homeStructuredData = {
    "@context": "https://schema.org",
    "@type": "WebSite",
    "name": "prest0",
    "url": "https://prest0.ai/",
    "potentialAction": {
      "@type": "SearchAction",
      "target": "https://prest0.ai/search?q={search_term_string}",
      "query-input": "required name=search_term_string"
    },
    "description": "AI employees built for law and finance — research, drafting, deadlines, client intake, and more. Invite-only access.",
    "publisher": {
      "@type": "Organization",
      "name": "prest0",
      "logo": {
        "@type": "ImageObject",
        "url": "https://www.prest0.ai/images/prest0-logo.webp"
      }
    }
  };

  // Add handler functions for mobile menu
  const handleMobileLogout = () => {
    logout();
    setIsMobileMenuOpen(false);
  };

  const handleMobileLogin = () => {
    // Close the mobile menu
    setIsMobileMenuOpen(false);

    // Navigate to the auth route (matches desktop LoginButton behavior).
    // Previously this scrolled to loginSectionRef, but that ref is never
    // attached to any element, so the mobile login button did nothing.
    navigate('/auth', { state: { returnUrl: '/' } });
  };

  // Manual navigation handlers for features section
  const handleFeaturesNavigation = (direction: 'left' | 'right') => {
    // Clear any existing timeout
    if (pauseTimeout) {
      clearTimeout(pauseTimeout);
    }
    
    // Calculate tile width (including gap) - exact measurements from FeatureCard and gap
    const isMobile = window.innerWidth <= 768;
    const tileWidth = isMobile ? 332 : 400; // mobile: 300px + 32px gap, desktop: 360px + 40px gap
    
    // Get current position - either from manual scrolling or capture current animation position
    let currentPosition = manualScrollPosition;
    
    // If not already in manual scrolling mode, we need to capture the current animation position
    if (!isManualScrolling) {
      // Use the ref to get the current transform position from the CSS animation
      const scrollWrapper = featuresScrollWrapperRef.current;
      if (scrollWrapper) {
        const computedStyle = window.getComputedStyle(scrollWrapper);
        const transform = computedStyle.transform;
        
        // Extract translateX value from transform matrix with high precision
        if (transform && transform !== 'none') {
          const matrix = transform.match(/matrix.*\((.+)\)/);
          if (matrix) {
            const values = matrix[1].split(', ');
            // The translateX value is the 5th value (index 4) in the matrix
            // Use parseFloat for precise decimal values
            currentPosition = parseFloat(values[4]) || 0;
          }
        } else {
          // If no transform is applied yet, start from 0
          currentPosition = 0;
        }
      }
    }
    
    // CRITICAL: First set the current position without transition to ensure seamless handoff
    if (!isManualScrolling) {
      // Temporarily set manual scrolling with current position (no transition)
      setIsManualScrolling(true);
      setManualScrollPosition(currentPosition);
      
      // Use requestAnimationFrame to ensure the DOM updates before applying the new position
      requestAnimationFrame(() => {
        // Calculate new position from the CURRENT position (where the animation was when clicked)
        let newPosition = currentPosition;
        if (direction === 'right') {
          newPosition -= tileWidth;
        } else {
          newPosition += tileWidth;
        }
        
        // Set bounds to prevent scrolling too far
        // Calculate bounds based on the number of features and duplicates
        // We have 3 sets of duplicated features, so we can scroll through 2 full sets
        const featuresCount = 13; // Number of original features
        const maxNegativeScroll = -(featuresCount * 2 * tileWidth); // Allow scrolling through 2 full sets
        const minScroll = 0;
        
        // Apply bounds
        newPosition = Math.max(maxNegativeScroll, Math.min(minScroll, newPosition));
        
        // Now apply the new position with smooth transition
        setManualScrollPosition(newPosition);
      });
    } else {
      // Already in manual mode, just calculate and apply new position
      let newPosition = currentPosition;
      if (direction === 'right') {
        newPosition -= tileWidth;
      } else {
        newPosition += tileWidth;
      }
      
      // Set bounds to prevent scrolling too far
      const featuresCount = 13; // Number of original features
      const maxNegativeScroll = -(featuresCount * 2 * tileWidth); // Allow scrolling through 2 full sets
      const minScroll = 0;
      
      // Apply bounds
      newPosition = Math.max(maxNegativeScroll, Math.min(minScroll, newPosition));
      
      // Apply the new position with smooth transition
      setManualScrollPosition(newPosition);
    }
    
    // Set timeout to resume automatic scrolling after 4 seconds
    const timeout = setTimeout(() => {
      const currentPos = manualScrollPosition;
      const isMobile = window.innerWidth <= 768;
      
      if (isMobile) {
        // For mobile, keep the existing behavior (simpler transition)
        setIsManualScrolling(false);
        setManualScrollPosition(0);
        setIsTransitioningToAuto(false);
      } else {
        // For desktop only: ensure seamless continuation of the exact same scrolling effect
        setIsTransitioningToAuto(true);
        
        // Keep the current position exactly where it is
        // This ensures the tiles don't jump when transitioning back to auto-scroll
        setIsManualScrolling(false);
        
        // After a brief moment, gradually reset to allow CSS animation to take over
        // This creates a seamless handoff from manual to automatic scrolling
        setTimeout(() => {
          setManualScrollPosition(0);
          
          // After the position resets, clear the transition flag
          setTimeout(() => {
            setIsTransitioningToAuto(false);
          }, 100);
        }, 50);
      }
    }, 4000);
    
    setPauseTimeout(timeout);
  };

  // Cleanup timeout on unmount
  React.useEffect(() => {
    return () => {
      if (pauseTimeout) {
        clearTimeout(pauseTimeout);
      }
    };
  }, [pauseTimeout]);
  // Create multiple duplicated features for seamless looping
  const duplicatedFeatures = [...featuresData, ...featuresData, ...featuresData];
  // Determine if scrolling should be paused (when any feature is expanded or manual scrolling)
  const shouldPauseScrolling = Object.values(expandedFeatures).some(Boolean) || isManualScrolling;
  return (
    <>
      <LandingContainer>
        <SeoHead
          title="prest0 — AI Employees for Law & Finance"
          tabTitle="prest0"
          description="Prest0 builds 24/7 AI employees for law and finance teams — research, drafting, ops, and client work on secure, dedicated infrastructure."
          keywords="prest0, AI employees, law, finance, AI research, AI drafting, client intake AI, dedicated AI infrastructure"
          structuredData={homeStructuredData}
        />
      <HomeHeader>
        <NavButtonGroup>
          <HeaderIconLink to="/team">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
              <circle cx="9" cy="7" r="4"/>
              <path d="M23 21v-2a4 4 0 0 0-3-3.87"/>
              <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
            </svg>
            team
          </HeaderIconLink>
          <HeaderIconLink to="/mission" onClick={() => {
              // Ensure we scroll to the top of the page when navigating to mission page
              setTimeout(() => window.scrollTo(0, 0), 100);
          }}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M4 15s1-1 4-1 5 2 8 2 4-1 4-1V3s-1 1-4 1-5-2-8-2-4 1-4 1z"></path>
              <line x1="4" y1="22" x2="4" y2="15"></line>
            </svg>
            mission
          </HeaderIconLink>
          <HeaderIconLink to="/insights">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="12" y1="2" x2="12" y2="6" />
              <path d="M5 10a7 7 0 0 1 14 0c0 3.07-1.63 4.68-3 5.58a2 2 0 0 0-1 1.7V19a2 2 0 0 1-2 2h-2a2 2 0 0 1-2-2v-1.72a2 2 0 0 0-1-1.7C6.63 14.68 5 13.07 5 10z" />
              <line x1="9" y1="22" x2="15" y2="22" />
            </svg>
            insights
          </HeaderIconLink>
          <HeaderIconLink to="/pricing">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="12" y1="1" x2="12" y2="23" />
              <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" />
            </svg>
            pricing
          </HeaderIconLink>
        </NavButtonGroup>

        {isAuthenticated ? (
          <>
            <ProfileImage size={36} />
            {hasAccount && (
              <NotificationWrapper ref={notificationWrapperRef}>
                <NotificationIconButton onClick={() => setIsNotificationsOpen(v => !v)} aria-label="notifications" title="notifications">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M18 8a6 6 0 10-12 0c0 7-3 9-3 9h18s-3-2-3-9"/>
                    <path d="M13.73 21a2 2 0 01-3.46 0"/>
                  </svg>
                  {unreadCount > 0 && (
                    <span style={{ position: 'absolute', top: -6, right: -6, minWidth: 20, height: 20, padding: '0 6px', borderRadius: 10, background: '#ff3b30', color: '#fff', fontSize: 12, fontWeight: 800, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 0 0 2px #fff, 0 6px 12px rgba(255,59,48,0.35)' }}>{unreadCount > 99 ? '99+' : unreadCount}</span>
                  )}
                  {unreadCount === 0 && notifications.length === 0 && (
                    <span style={{ position: 'absolute', top: -6, right: -6, minWidth: 20, height: 20, padding: '0 6px', borderRadius: 10, background: '#e9ecef', color: '#6c757d', fontSize: 12, fontWeight: 700, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 0 0 2px #fff' }}>0</span>
                  )}
                </NotificationIconButton>
                <NotificationDropdown $isOpen={isNotificationsOpen} onClick={e => e.stopPropagation()}>
                  <NotificationHeader>
                    <h4>notifications</h4>
                    <button
                      onClick={() => {
                        setIsNotificationsOpen(false);
                        navigate('/notifications');
                      }}
                      aria-label="view all notifications"
                      title="view all"
                      style={{ background: 'transparent', border: 'none', cursor: 'pointer', padding: 4, borderRadius: 6 }}
                    >
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M9 18l6-6-6-6" />
                      </svg>
                    </button>
                  </NotificationHeader>
                  <NotificationList>
                    {notifications.slice(0, 10).length === 0 ? (
                      <NotificationEmpty>no notifications yet</NotificationEmpty>
                    ) : (
                      notifications.slice(0, 10).map(n => (
                        <NotificationItem key={n.id}>
                          <span className="icon">
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <path d="M18 8a6 6 0 10-12 0c0 7-3 9-3 9h18s-3-2-3-9"/>
                              <path d="M13.73 21a2 2 0 01-3.46 0"/>
                            </svg>
                          </span>
                          <div className="content">
                            <div className="title">{n.title || 'notification'}</div>
                            <div className="message">{n.message}</div>
                            <div className="meta">{formatDateTime(n.timestamp)}</div>
                          </div>
                        </NotificationItem>
                      ))
                    )}
                  </NotificationList>
                  <ViewAllButton onClick={() => { setIsNotificationsOpen(false); navigate('/notifications'); }}>view all</ViewAllButton>
                </NotificationDropdown>
              </NotificationWrapper>
            )}
          </>
        ) : (
          <HeaderIconButton as={LoginButton} onLoginSuccess={() => {
            // Stay on the landing page, just update header UI
          }} />
        )}
        
        {/* Mobile Menu Button */}
        <MobileMenuButton 
          onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          aria-label="Toggle mobile menu"
          data-mobile-menu="true"
        >
          <HamburgerIcon />
        </MobileMenuButton>
        
        {/* Mobile Menu Dropdown */}
        <MobileMenuDropdown $isOpen={isMobileMenuOpen} data-mobile-menu="true">
          <MobileMenuItem to="/team">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
              <circle cx="9" cy="7" r="4"/>
              <path d="M23 21v-2a4 4 0 0 0-3-3.87"/>
              <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
            </svg>
            team
          </MobileMenuItem>
          <MobileMenuItem to="/mission" onClick={() => setIsMobileMenuOpen(false)}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M4 15s1-1 4-1 5 2 8 2 4-1 4-1V3s-1 1-4 1-5-2-8-2-4 1-4 1z" />
              <line x1="4" y1="22" x2="4" y2="15" />
            </svg>
            mission
          </MobileMenuItem>
          <MobileMenuItem to="/insights">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="12" y1="2" x2="12" y2="6" />
              <path d="M5 10a7 7 0 0 1 14 0c0 3.07-1.63 4.68-3 5.58a2 2 0 0 0-1 1.7V19a2 2 0 0 1-2 2h-2a2 2 0 0 1-2-2v-1.72a2 2 0 0 0-1-1.7C6.63 14.68 5 13.07 5 10z" />
              <line x1="9" y1="22" x2="15" y2="22" />
            </svg>
            insights
          </MobileMenuItem>
          
          {isAuthenticated ? (
            <>
              <MobileMenuItem to="/desktop">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="2" y="4" width="20" height="16" rx="2"/>
                  <path d="M16 12h.01"/>
                  <path d="M2 10h20"/>
                </svg>
                desktop
              </MobileMenuItem>
              {/* ORG-USAGE-2026: same billing.view gate as the header nav link —
                  fail-open (guests / legacy accounts unaffected), so a member the
                  admin restricted doesn't get a mobile-menu path to the access wall. */}
              {canSeeWallet && (
                <MobileMenuItem to="/wallet">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="2" y="4" width="20" height="16" rx="2" />
                    <path d="M16 12h.01" />
                    <path d="M2 10h20" />
                  </svg>
                  wallet
                </MobileMenuItem>
              )}
              {/* ORG-USAGE-2026: same activity.view gate as the header nav link —
                  fail-open. Menu order convention: activity sits between wallet
                  and profile. */}
              {canSeeActivity && (
                <MobileMenuItem to="/activity">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M3 3v18h18" />
                    <path d="M7 15l4-4 3 3 5-6" />
                  </svg>
                  activity
                </MobileMenuItem>
              )}
              <MobileMenuItem to="/profile">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                  <circle cx="12" cy="7" r="4" />
                </svg>
                profile
              </MobileMenuItem>
              <MobileMenuItem to="/settings">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M12.22 2h-.44a2 2 0 0 0-2 2v.18a2 2 0 0 1-1 1.73l-.43.25a2 2 0 0 1-2 0l-.15-.08a2 2 0 0 0-2.73.73l-.22.38a2 2 0 0 0 .73 2.73l.15.1a2 2 0 0 1 1 1.72v.51a2 2 0 0 1-1 1.74l-.15.09a2 2 0 0 0-.73 2.73l.22.38a2 2 0 0 0 2.73.73l.15-.08a2 2 0 0 1 2 0l.43.25a2 2 0 0 1 1 1.73V20a2 2 0 0 0 2 2h.44a2 2 0 0 0 2-2v-.18a2 2 0 0 1 1-1.73l.43-.25a2 2 0 0 1 2 0l.15.08a2 2 0 0 0 2.73-.73l.22-.39a2 2 0 0 0-.73-2.73l-.15-.08a2 2 0 0 1-1-1.74v-.5a2 2 0 0 1 1-1.74l.15-.09a2 2 0 0 0 .73-2.73l-.22-.38a2 2 0 0 0-2.73-.73l-.15.08a2 2 0 0 1-2 0l-.43-.25a2 2 0 0 1-1-1.73V4a2 2 0 0 0-2-2z"/>
                  <circle cx="12" cy="12" r="3"/>
                </svg>
                settings
              </MobileMenuItem>
              <MobileMenuLoginButton onClick={handleMobileLogout}>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
                  <polyline points="16 17 21 12 16 7" />
                  <line x1="21" y1="12" x2="9" y2="12" />
                </svg>
                logout
              </MobileMenuLoginButton>
              {hasAccount && (
                <MobileMenuAction onClick={() => { setIsMobileMenuOpen(false); navigate('/notifications'); }}>
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M18 8a6 6 0 10-12 0c0 7-3 9-3 9h18s-3-2-3-9"/>
                    <path d="M13.73 21a2 2 0 01-3.46 0"/>
                  </svg>
                  notifications
                </MobileMenuAction>
              )}
            </>
          ) : (
                          <MobileMenuLoginButton onClick={handleMobileLogin}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M15 3h4a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-4"/>
              <polyline points="10 17 15 12 10 7"/>
              <line x1="15" y1="12" x2="3" y2="12"/>
            </svg>
            login
          </MobileMenuLoginButton>
          )}
        </MobileMenuDropdown>
      </HomeHeader>
      
      <HeroSection>
        <HeroCanvasBackground />
        <HeroContent>
          {/* LCP hero logo — URL (incl. ?v=) must stay identical to the
              <link rel="preload"> in public/index.html so the preloaded
              bytes are reused. width/height = intrinsic ratio (CLS). */}
          <Logo>prest<HeroInlineLogo src="/images/project-olivia-logo.webp?v=20260715" alt="0" width={545} height={512} loading="eager" decoding="async" /></Logo>
          <Title>
            <div className="top-line first-heading">24/7 <span className="ai-uppercase">AI</span> employees</div>
            <div className="bottom-line main-heading">for <span>law and finance</span></div>
          </Title>
          <TypewriterContainer>
            <TypewriterLabel>prest<SmallInlineLogo src="/images/project-olivia-logo.webp?v=20260715" alt="0" width={545} height={512} loading="eager" decoding="async" style={{ marginRight: '0.25em', marginBottom: '0.18em' }} /> is</TypewriterLabel>
            <TypewriterText>
              <Typewriter texts={typingTexts} />
            </TypewriterText>
          </TypewriterContainer>

          {/* Conversion pair, hero edition (2026-08-19 owner directive):
              ONE primary action — create the account, $200 of work units and
              a 14-day evaluation load automatically, straight into the same
              unified onboarding. The play button stays as the learning path;
              the high-touch demo path lives in the closing section. Signed-in
              users get their workspace instead of a signup pitch. */}
          <HeroCtaRow>
            <ShinyGoldButton
              type="button"
              $size="lg"
              onClick={() => navigate(isAuthenticated ? '/desktop' : '/auth', isAuthenticated ? undefined : { state: { authMode: 'signup', returnUrl: '/' } })}
            >
              {isAuthenticated ? 'open your workspace' : 'start free — over 100 jobs on us'}{' '}
              <span aria-hidden="true">→</span>
            </ShinyGoldButton>
            <DemoPlayButton onClick={() => setDemoVideoOpen(true)} style={{ margin: 0 }}>
              <svg viewBox="0 0 24 24"><path d="M8 5v14l11-7z"/></svg>
              demo
            </DemoPlayButton>
          </HeroCtaRow>
        </HeroContent>
      </HeroSection>

      {/* Prest0, as a day. An EXPLICIT pick re-scores it — nothing here reads
          what the visitor typed or infers their trade, so the page stays a
          renderer and every real decision stays with the primary. */}
      <DaySection id="a-day">
        <DayInner>
          {/* NAMING (Miles, 2026-09-10): the mark is called Prest0 — never
              "the 0" — and the 0 in it is ALWAYS the logo image, the same
              treatment the header and the sidebar wordmark use. */}
          <DayHead>
            a day inside prest
            <SmallInlineLogo
              src="/images/project-olivia-logo.webp?v=20260715"
              alt="0"
              width={545}
              height={512}
              loading="lazy"
              decoding="async"
            />
          </DayHead>
          <DayLead>
            drag it. every hour is something a <KeepCase>Prest0n</KeepCase> actually
            does, at the rate we publish.
          </DayLead>

          <ChipRow role="group" aria-label="show the day for">
            {([
              ['law', 'i run a law firm'],
              ['finance', 'i run a finance firm'],
              ['other', 'something else'],
            ] as Array<[ZeroVertical, string]>).map(([key, label]) => (
              <Chip
                key={key}
                type="button"
                $on={vertical === key}
                aria-pressed={vertical === key}
                onClick={() => setVertical(key)}
              >
                {label}
              </Chip>
            ))}
          </ChipRow>

          <Zero vertical={vertical} />

          <DayFoot>
            one <KeepCase>Prest0n</KeepCase> includes {MONTHLY_WU_PER_PRESTON.toLocaleString('en-US')}{' '}
            {WU_FULL_NAME} a month. retries, overruns and thinking time are always free —
            you pay for finished work, never for the tries it took.
          </DayFoot>
        </DayInner>
      </DaySection>

      <Room />

      <VideoModalOverlay
        $isOpen={demoVideoOpen}
        onClick={() => {
          if (demoVideoRef.current && demoVideoRef.current.currentTime > 0) {
            try { sessionStorage.setItem('demo_video_current_time', String(demoVideoRef.current.currentTime)); } catch {}
          }
          if (demoVideoRef.current) demoVideoRef.current.pause();
          setDemoVideoOpen(false);
        }}
      >
        <VideoModalContent onClick={(e) => e.stopPropagation()}>
          <VideoModalClose onClick={() => {
            if (demoVideoRef.current && demoVideoRef.current.currentTime > 0) {
              try { sessionStorage.setItem('demo_video_current_time', String(demoVideoRef.current.currentTime)); } catch {}
            }
            if (demoVideoRef.current) demoVideoRef.current.pause();
            setDemoVideoOpen(false);
          }}>✕</VideoModalClose>
          <VideoModalContainer
            ref={mobileVideoContainerRef}
            $mobileFullscreen={isMobileFullscreen && isMobileViewport}
          >
            <video
              ref={demoVideoRef}
              src="https://storage.googleapis.com/project-olivia-frontend-415/demo-video/prest0-demo-latest.mp4"
              controls
              playsInline
              /* preload="none": the modal's open-effect already calls
                 video.load() when readyState < 1, so nothing regresses —
                 but the closed modal no longer fetches metadata of a
                 44 MB mp4 during first paint of the home page. */
              preload="none"
              onEnded={() => {
                setDemoVideoOpen(false);
                setIsBookingModalOpen(true);
              }}
            />
          </VideoModalContainer>
        </VideoModalContent>
      </VideoModalOverlay>
      
      <BenefitsSection>
        <BenefitsGrid>
          <BenefitCard
            isExpanded={expandedBenefits['easy-create']}
            onClick={() => handleBenefitToggle('easy-create')}
            onMouseEnter={() => handleBenefitHover('easy-create', true)}
            onMouseLeave={() => handleBenefitHover('easy-create', false)}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                handleBenefitToggle('easy-create');
              }
            }}
            aria-expanded={expandedBenefits['easy-create']}
            aria-label="Learn more about easy creation - tap to expand"
          >
            <BenefitIcon>
              <EasyCreateIcon />
            </BenefitIcon>
            <BenefitTitle isExpanded={expandedBenefits['easy-create']}>hours of work, done in minutes</BenefitTitle>
            <BenefitExpandedContent isExpanded={expandedBenefits['easy-create']}>
              <BenefitExpandedDescription isAnimated={expandedBenefits['easy-create']}>
                research, drafting, and intake that used to take hours — done in minutes. your team moves faster from day one.
              </BenefitExpandedDescription>
            </BenefitExpandedContent>
          </BenefitCard>
          
          <BenefitCard
            isExpanded={expandedBenefits['exactly-what-you-need']}
            onClick={() => handleBenefitToggle('exactly-what-you-need')}
            onMouseEnter={() => handleBenefitHover('exactly-what-you-need', true)}
            onMouseLeave={() => handleBenefitHover('exactly-what-you-need', false)}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                handleBenefitToggle('exactly-what-you-need');
              }
            }}
            aria-expanded={expandedBenefits['exactly-what-you-need']}
            aria-label="Learn more about personalized solutions - tap to expand"
          >
            <BenefitIcon>
              <ExactlyWhatYouNeedIcon />
            </BenefitIcon>
            <BenefitTitle isExpanded={expandedBenefits['exactly-what-you-need']}>built for your vertical</BenefitTitle>
            <BenefitExpandedContent isExpanded={expandedBenefits['exactly-what-you-need']}>
              <BenefitExpandedDescription isAnimated={expandedBenefits['exactly-what-you-need']}>
                handles research, drafts, deadlines, and client questions — the work that piles up every day.
              </BenefitExpandedDescription>
            </BenefitExpandedContent>
          </BenefitCard>
          
          <BenefitCard
            isExpanded={expandedBenefits['trust-first']}
            onClick={() => handleBenefitToggle('trust-first')}
            onMouseEnter={() => handleBenefitHover('trust-first', true)}
            onMouseLeave={() => handleBenefitHover('trust-first', false)}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                handleBenefitToggle('trust-first');
              }
            }}
            aria-expanded={expandedBenefits['trust-first']}
            aria-label="Learn more about trust-first operations - tap to expand"
          >
            <BenefitIcon>
              <TrustShieldIcon />
            </BenefitIcon>
            <BenefitTitle isExpanded={expandedBenefits['trust-first']}>your clients' data stays private</BenefitTitle>
            <BenefitExpandedContent isExpanded={expandedBenefits['trust-first']}>
              <BenefitExpandedDescription isAnimated={expandedBenefits['trust-first']}>
                your client information is never shared or used to train AI. every action is logged. you stay in control.
              </BenefitExpandedDescription>
            </BenefitExpandedContent>
          </BenefitCard>
          
        </BenefitsGrid>
      </BenefitsSection>

      <ValuePropSection>
        <ValuePropInner>
          <ValuePropTitle>thousands of built-in tools and skills from day one</ValuePropTitle>
          {/* 2026-08-21 owner revision: open by default — grid leads, the
              collapse control trails quietly. */}
          {innovationsOpen && (
            <InnovationGrid>
              <FeaturedInnovation>
                a patent-pending memory architecture — it remembers your firm, and gets more accurate the more you use it
              </FeaturedInnovation>
              {LANDING_INNOVATIONS.map((item) => (
                <InnovationChip key={item}>{item}</InnovationChip>
              ))}
            </InnovationGrid>
          )}
          <InnovationToggle
            type="button"
            aria-expanded={innovationsOpen}
            onClick={() => setInnovationsOpen((v) => !v)}
          >
            {innovationsOpen ? 'hide the innovations' : 'see the innovations'}{' '}
            <span aria-hidden="true">{innovationsOpen ? '↑' : '↓'}</span>
          </InnovationToggle>
        </ValuePropInner>
      </ValuePropSection>

      {/* The 15-tile marketplace grid and the 30-logo marquee stood here. Both
          were breadth claims, and breadth is not what a law firm buys on — and
          the marquee named practice-management systems that have no direct
          connector, which is only honest if the page explains the second lane.
          Mechanisms does. `#features` stays as the id so the header link and
          featuresSectionRef keep working. */}
      <section ref={featuresSectionRef} id="features" style={{ width: '100%', scrollMarginTop: '80px' }}>
        <Mechanisms />
      </section>

      <TrustBlock />

      {/* ── Book a Demo Section ── */}
      <section style={{
        width: '100%',
        padding: '7rem 2rem',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        background: 'linear-gradient(135deg, var(--brand-black) 0%, #2a2a2a 100%)',
      }}>
        <div style={{ maxWidth: '1000px', width: '100%', textAlign: 'center' }}>
          <ActionTitle>
            see agent <span style={{
              background: 'linear-gradient(135deg, var(--brand-gold-alt), #B8924C)',
              backgroundSize: '200% 100%',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
            }}>Prest</span><SmallInlineLogo
              src="/images/project-olivia-logo.webp?v=20260715"
              alt="0"
              width={545}
              height={512}
              loading="lazy"
              decoding="async"
              style={{ 
                height: '1.02em', /* Taller to match capital O/P with built-in image padding */
                marginLeft: '-0.16em', /* Tighter pull to compress image padding next to 't' */
                marginRight: '-0.14em', /* Tighter pull next to 'n' */
                marginBottom: '-0.08em', /* Deeper baseline alignment for taller element */
                filter: 'drop-shadow(0 0 8px rgba(255, 255, 255, 0.15)) drop-shadow(0 0 4px rgba(255, 255, 255, 0.18))', /* Very subtle white shade for dark background contrast */
              }} 
            /><span style={{
              background: 'linear-gradient(135deg, var(--brand-gold-alt), #B8924C)',
              backgroundSize: '200% 100%',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
            }}>n</span> in action
          </ActionTitle>

          {/* Closing conversion pair (2026-08-19 owner directive): the one
              place both paths sit side by side, with clear hierarchy — the
              self-serve start is primary (risk reversal: $200 loaded, 14 days,
              no card), the demonstration is the high-touch secondary with its
              own incentive (demo attendees start with a larger grant). A
              signed-in user just gets their workspace. */}
          {isAuthenticated ? (
            <ShinyGoldButton type="button" $size="lg" onClick={() => navigate('/desktop')}>
              open your workspace <span aria-hidden="true">→</span>
            </ShinyGoldButton>
          ) : (
            <>
              <div style={{
                display: 'flex',
                flexWrap: 'wrap',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '1rem',
              }}>
                <ShinyGoldButton type="button" $size="lg" onClick={() => navigate('/auth', { state: { authMode: 'signup', returnUrl: '/' } })}>
                  start free — over 100 jobs on us <span aria-hidden="true">→</span>
                </ShinyGoldButton>
                <DarkGhostButton type="button" onClick={() => setIsBookingModalOpen(true)}>
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ width: 20, height: 20 }}>
                    <rect x="3" y="4" width="18" height="18" rx="2" ry="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/>
                  </svg>
                  book a demo — start with more
                </DarkGhostButton>
              </div>
              <p style={{
                fontFamily: "'Inter', sans-serif",
                fontSize: '0.82rem',
                color: '#8a8a8a',
                margin: '0.9rem 0 0',
              }}>
                demo attendees start with a larger grant.
              </p>
            </>
          )}
        </div>
      </section>
      
      <FooterWrapper>
        <GlobalFooter />
      </FooterWrapper>
      {/* Custom chat system removed — GlobalChatSystem now rendered at App level */}
      <Modal
        isOpen={isBookingModalOpen}
        onClose={() => setIsBookingModalOpen(false)}
        title="book a live demo"
        size="xlarge"
        maxWidth="1100px"
        footer={null}
        noScroll={true}
      >
        <div style={{ height: 'calc(90vh - 80px)', minHeight: '600px', width: '100%', borderRadius: '0 0 12px 12px', overflow: 'hidden' }}>
          <iframe
            src="https://calendar.google.com/calendar/appointments/schedules/AcZssZ2bVpuAMuVJh3Xikmu1Bi9AuBaaHxB5a-HeOlE8xm3l2ehjgrdvG5QQEqmrLDKsF1Vgbncg2BIK?gv=true"
            style={{ border: 'none', width: '100%', height: '100%' }}
          ></iframe>
        </div>
      </Modal>
  </LandingContainer>
  </>
);
};

export default LandingPage;
